import { articlesStep05 } from './articles-step05';
import { articlesStep05Ext } from './articles-step05-ext';
import { articlesStep01Ext } from './articles-step01-ext';

export interface Article {
  id: number;
  step: string;
  title: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  excerpt: string;
  tags: string[];
  content: string;
}

export const articles: Article[] = [
  {
    id: 7,
    step: '01',
    title: "鳥取で家づくりは何から始める？初心者が最初にやるべき5ステップ",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約12分",
    image: "https://public.readdy.ai/ai/img_res/75a8e81b78b37f75e6aef456abd84d2b.jpg",
    excerpt: "「鳥取で家づくりを始めたいけど、何から手をつければいいかわからない…」そんな方へ。初心者が最初にやるべき5つのステップをわかりやすく解説します。",
    tags: ["鳥取", "はじめて", "5ステップ"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家づくりを始めたいけど、何から手をつければいいかわからない…」<br />そんな不安を抱えていませんか？</p>
      <p>私自身も最初は同じように迷いました。住宅会社に行くべきなのか、土地探しから始めるべきなのか、住宅ローンのことを考えるべきなのか…。調べれば調べるほど情報が多すぎて、逆に動けなくなる人も多いです。</p>
      <p>この記事では、<strong>鳥取県で家づくりを始める初心者が「最初にやるべき5つのステップ」</strong>を、わかりやすく解説します。この記事を読み終える頃には、次に何をすればいいのかが明確になり、迷いなく動き出せるようになります。</p>

      <h2>結論：鳥取で家づくりを始める最短ルート</h2>
      <p>鳥取で家づくりを始めるなら、まずは次の5ステップを順番に進めるのが最短ルートです。</p>
      <ol>
        <li>知識をつける（全体像を理解する）</li>
        <li>予算を決める（無理のない資金計画）</li>
        <li>パートナーを決める（住宅会社選び）</li>
        <li>物件・プランを決める（土地＋間取り）</li>
        <li>契約・引き渡し（最終ステップ）</li>
      </ol>
      <p>この流れで進めることで、家づくりの失敗リスクを大幅に減らせます。</p>

      <h2>① 家づくりの知識をつける（最初にやるべき理由）</h2>
      <p>家づくりは「知らないまま進める」と失敗しやすい分野です。特に鳥取市・米子市・倉吉市では、土地価格や住宅会社の特徴が地域ごとに大きく異なるため、最低限の知識がないと判断が難しくなります。</p>

      <h3>知識不足が招く典型的な失敗例</h3>
      <ul>
        <li>営業トークを鵜呑みにして高いプランを契約</li>
        <li>土地の条件を理解せず、追加費用が発生</li>
        <li>断熱性能の違いを知らず、冬が寒い家になる</li>
        <li>諸費用を見落として予算オーバー</li>
      </ul>
      <p>私自身も最初は「何がわからないのかすらわからない」状態でした。しかし、基本的な流れや費用の仕組みを知るだけで、住宅会社との打ち合わせがスムーズになり、無駄な出費を防げます。</p>

      <h3>初心者が最初に学ぶべきポイント</h3>
      <ul>
        <li>家づくりの全体の流れ</li>
        <li>鳥取の土地相場（鳥取市・米子市・倉吉市）</li>
        <li>建物価格の仕組み（坪単価・標準仕様）</li>
        <li>断熱性能・耐震性能の基礎</li>
        <li>住宅ローンの種類</li>
      </ul>
      <p>まずは「ざっくり全体像をつかむ」ことが大切です。</p>

      <h2>② 予算を決める（家づくりの最重要ステップ）</h2>
      <p>家づくりの失敗で最も多いのが、予算オーバーです。特に鳥取では土地＋建物＋諸費用のバランスが重要で、予算を決めずに動くと後悔しやすくなります。</p>

      <h3>総予算の決め方（初心者向け）</h3>
      <ul>
        <li>年収の25〜30％以内の返済額に収める</li>
        <li>月々の支払いは「無理なく払える金額」に設定</li>
        <li>ボーナス払いは基本なしで考える</li>
      </ul>
      <p><strong>【例：年収400万円の場合】</strong></p>
      <ul>
        <li>月々の返済目安：7〜9万円</li>
        <li>借入可能額：2,500〜3,000万円</li>
        <li>総予算：3,000〜3,500万円（建物＋土地＋諸費用）</li>
      </ul>

      <h3>鳥取で予算が変わるポイント</h3>
      <ul>
        <li><strong>鳥取市</strong>は土地価格が高め</li>
        <li><strong>米子市</strong>はエリアによって差が大きい</li>
        <li><strong>倉吉市</strong>は比較的手頃</li>
      </ul>
      <p>同じ予算でも、地域によって買える土地の広さや条件が変わります。</p>

      <h2>③ パートナー（住宅会社）を決める</h2>
      <p>家づくりの満足度は、どの住宅会社を選ぶかで8割決まると言われています。鳥取には工務店・ハウスメーカー・ローコスト住宅など多くの選択肢があり、比較せずに決めると後悔しやすいです。</p>

      <h3>住宅会社選びで失敗しやすいポイント</h3>
      <ul>
        <li>営業担当との相性が悪い</li>
        <li>価格が安いだけで選んでしまう</li>
        <li>性能（断熱・気密）を確認していない</li>
        <li>標準仕様の内容を理解していない</li>
      </ul>
      <p>特に鳥取の冬は寒いため、断熱性能は必ずチェックすべきです。</p>

      <h3>比較するポイント</h3>
      <ul>
        <li>坪単価</li>
        <li>標準仕様</li>
        <li>断熱性能（UA値）</li>
        <li>気密性能（C値）</li>
        <li>施工事例</li>
        <li>アフター保証</li>
      </ul>
      <p>複数社の資料を取り寄せると、違いが一目でわかります。</p>

      <h2>④ 物件・プランを決める（土地探し＋間取り）</h2>
      <p>住宅会社が決まったら、次は土地と間取りの検討です。鳥取は地域によって土地の条件が大きく異なるため、プロと一緒に探すのが効率的です。</p>

      <h3>土地探しで見るべきポイント</h3>
      <ul>
        <li>日当たり</li>
        <li>地盤の強さ</li>
        <li>周辺環境（学校・スーパー）</li>
        <li>道路の広さ</li>
        <li>冬の雪対策</li>
      </ul>
      <p>鳥取市は地価が高め、米子市はエリア差が大きく、倉吉市は比較的手頃です。</p>

      <h3>間取りで後悔しやすいポイント</h3>
      <ul>
        <li>収納が足りない</li>
        <li>動線が悪い</li>
        <li>コンセントの位置</li>
        <li>将来の子ども部屋の使い方</li>
      </ul>
      <p>実際に住んだ後の生活をイメージしながら決めることが大切です。</p>

      <h2>⑤ 契約・着工・引き渡し（最後のステップ）</h2>
      <p>プランと見積もりが固まったら契約へ進みます。契約後は、着工→上棟→完成→引き渡しという流れです。</p>

      <h3>契約前に必ず確認すべきこと</h3>
      <ul>
        <li>見積もりに抜け漏れがないか</li>
        <li>標準仕様とオプションの違い</li>
        <li>工期のスケジュール</li>
        <li>保証内容</li>
      </ul>
      <p>契約後の変更は追加費用が発生しやすいため、事前確認が重要です。</p>

      <h3>引き渡し後にやること</h3>
      <ul>
        <li>住所変更</li>
        <li>火災保険の加入</li>
        <li>固定資産税の確認</li>
        <li>メンテナンススケジュールの把握</li>
      </ul>
      <p>家は建てて終わりではなく、住んでからが本番です。</p>

      <h2>まとめ</h2>
      <p>鳥取で家づくりを始めるなら、まずは次の5ステップを押さえればOKです。</p>
      <ol>
        <li>知識をつける</li>
        <li>予算を決める</li>
        <li>パートナーを決める</li>
        <li>物件・プランを決める</li>
        <li>契約・引き渡し</li>
      </ol>
      <p>この順番で進めることで、後悔のない家づくりができます。</p>

      <h2>次に読む記事</h2>
      <p>家づくりの全体像を理解したら、次は<br />👉 <a href="/articles/8">「鳥取で注文住宅を建てる流れを完全解説｜最短ルートで失敗しない方法」</a>を知っておくと、より具体的に動けるようになります。</p>
    `
  },
  {
    id: 8,
    step: '01',
    title: "鳥取で注文住宅を建てる流れを完全解説｜最短ルートで失敗しない方法",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約13分",
    image: "https://readdy.ai/api/search-image?query=A%20stunning%20modern%20custom-built%20Japanese%20house%20in%20Tottori%20region%2C%20clean%20architectural%20design%20with%20natural%20wood%20and%20white%20exterior%20walls%2C%20spacious%20open%20layout%2C%20fresh%20green%20garden%20with%20stone%20walkway%2C%20bright%20clear%20sky%20with%20soft%20clouds%2C%20warm%20midday%20sunlight%2C%20serene%20residential%20setting%2C%20professional%20architectural%20photography%2C%20welcoming%20and%20high%20quality%20craftsmanship&width=600&height=400&seq=art008&orientation=landscape",
    excerpt: "「注文住宅ってどんな流れで建てるの？」鳥取県での注文住宅の進め方を5ステップで完全解説。建売との違いや失敗しないための最短ルートをわかりやすくお伝えします。",
    tags: ["鳥取", "注文住宅", "流れ"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で注文住宅を建てたいけれど、どんな流れで進むの？」<br />「建売と違って、注文住宅は決めることが多いって聞くけど不安…」<br />そんな悩みを感じていませんか？</p>
      <p>私自身も最初は、<br />・どのタイミングで間取りを決めるのか<br />・土地は先に買うべきなのか<br />・契約までに何を決める必要があるのか<br />まったくわからず、ネットで調べても情報がバラバラで混乱しました。</p>
      <p>しかし、注文住宅の流れを最初に理解しておくと、打ち合わせがスムーズになり、後悔のない家づくりができます。この記事では、鳥取県で注文住宅を建てるときの流れを、初心者でもわかるように5ステップで解説します。</p>

      <h2>結論：鳥取で注文住宅を建てる流れは、次の5ステップです</h2>
      <ol>
        <li>知識をつける（注文住宅の特徴を理解）</li>
        <li>予算を決める（建物＋土地＋諸費用の総額）</li>
        <li>パートナーを決める（住宅会社選び）</li>
        <li>物件・プランを決める（間取り・仕様・土地）</li>
        <li>契約・着工・引き渡し（完成までの工程）</li>
      </ol>
      <p>建売とは違い、注文住宅は「決めることが多い」ため、この流れを理解しておくことが成功のカギになります。</p>

      <h2>① 注文住宅の知識をつける（建売との違いを理解）</h2>
      <p>注文住宅は、建売住宅と比べて自由度が高い反面、決めることが多く、時間もかかるという特徴があります。</p>
      <h3>注文住宅の特徴</h3>
      <ul>
        <li>間取りを自由に決められる</li>
        <li>断熱性能・耐震性能を選べる</li>
        <li>外観・内装を細かくカスタマイズできる</li>
        <li>打ち合わせが多く、完成まで時間がかかる</li>
      </ul>
      <p>鳥取は冬の寒さが厳しいため、<strong>断熱性能（UA値）や気密性能（C値）を選べる注文住宅は相性が良い</strong>というメリットがあります。</p>
      <h3>知識不足が招く注文住宅の失敗例</h3>
      <ul>
        <li>打ち合わせで疲れて適当に決めてしまう</li>
        <li>オプションが増えて予算オーバー</li>
        <li>性能を軽視して冬が寒い家になる</li>
        <li>生活動線を考えず住みにくい間取りになる</li>
      </ul>
      <p>注文住宅は「自由＝難しい」ため、最初に知識をつけることが重要です。</p>

      <h2>② 予算を決める（注文住宅は建売より費用が読みにくい）</h2>
      <p>注文住宅は、建売と違って仕様・間取り・設備によって価格が大きく変わるため、予算決めが非常に重要です。</p>
      <h3>注文住宅の総額の内訳</h3>
      <ul>
        <li>建物本体価格</li>
        <li>付帯工事（地盤改良・外構など）</li>
        <li>設備（キッチン・浴室）</li>
        <li>土地代</li>
        <li>諸費用（登記・火災保険など）</li>
      </ul>
      <p>特に鳥取では、地盤改良費がかかるエリアもあるため、建物価格だけで判断すると必ず失敗します。</p>
      <h3>予算決めのポイント</h3>
      <ul>
        <li>月々の返済額から逆算する</li>
        <li>建物価格は「坪単価×坪数」で概算</li>
        <li>土地代は鳥取市・米子市・倉吉市で大きく変わる</li>
        <li>諸費用は総額の10％前後</li>
      </ul>
      <p>注文住宅は「選ぶほど高くなる」ため、最初に予算の上限を決めておくことが大切です。</p>

      <h2>③ パートナー（住宅会社）を決める</h2>
      <p>注文住宅は、住宅会社によって性能・デザイン・価格・自由度が大きく違うため、比較が必須です。</p>
      <h3>注文住宅の会社選びで失敗しやすいポイント</h3>
      <ul>
        <li>営業担当との相性が悪い</li>
        <li>標準仕様が低く、オプションだらけになる</li>
        <li>断熱性能が弱く、冬が寒い家になる</li>
        <li>打ち合わせが雑で、細かい要望が伝わらない</li>
      </ul>
      <p>特に鳥取では、冬の寒さ対策として<strong>断熱性能（UA値）・気密性能（C値）</strong>は必ず確認すべきポイントです。</p>
      <h3>比較するポイント</h3>
      <ul>
        <li>坪単価</li>
        <li>標準仕様の内容</li>
        <li>断熱性能・気密性能</li>
        <li>間取りの自由度</li>
        <li>施工事例</li>
        <li>アフター保証</li>
      </ul>
      <p>資料請求で複数社を比較すると、違いが一目でわかります。</p>

      <h2>④ 物件・プランを決める（注文住宅の核心部分）</h2>
      <p>注文住宅の最大の特徴は、間取り・仕様・デザインを自由に決められること。ここが最も楽しく、最も後悔しやすい部分です。</p>
      <h3>間取りで後悔しやすいポイント</h3>
      <ul>
        <li>収納が足りない</li>
        <li>動線が悪い</li>
        <li>コンセントの位置が不便</li>
        <li>将来の子ども部屋を考えていない</li>
        <li>日当たりを考慮していない</li>
      </ul>
      <p>鳥取は冬の日射取得が重要なため、<strong>南向きリビング・窓の位置</strong>は特に慎重に決める必要があります。</p>
      <h3>仕様決めで失敗しやすいポイント</h3>
      <ul>
        <li>キッチン・浴室のグレードアップで予算オーバー</li>
        <li>外構費用を後回しにして追加出費</li>
        <li>断熱材の種類を理解せず寒い家になる</li>
      </ul>
      <p>注文住宅は「選ぶほど高くなる」ため、優先順位を決めておくと失敗しません。</p>

      <h2>⑤ 契約・着工・引き渡し（注文住宅ならではの工程）</h2>
      <p>注文住宅は、建売と違って契約後に詳細を決める工程が多いという特徴があります。</p>
      <h3>契約前に確認すべきこと</h3>
      <ul>
        <li>見積もりの抜け漏れ</li>
        <li>標準仕様とオプションの違い</li>
        <li>工期のスケジュール</li>
        <li>保証内容</li>
        <li>追加費用が発生する条件</li>
      </ul>
      <p>契約後の変更は追加費用が発生しやすいため、契約前の確認が非常に重要です。</p>
      <h3>着工〜引き渡しの流れ</h3>
      <ol>
        <li>地鎮祭</li>
        <li>基礎工事</li>
        <li>上棟</li>
        <li>内装工事</li>
        <li>完成</li>
        <li>引き渡し</li>
      </ol>
      <p>鳥取は冬季の工事が遅れやすいため、<strong>着工時期も住宅会社と相談するのがおすすめ</strong>です。</p>

      <h2>まとめ</h2>
      <p>鳥取で注文住宅を建てる流れは、次の5ステップです。</p>
      <ol>
        <li>知識をつける（注文住宅の特徴を理解）</li>
        <li>予算を決める（総額の把握）</li>
        <li>パートナーを決める（住宅会社選び）</li>
        <li>物件・プランを決める（間取り・仕様・土地）</li>
        <li>契約・引き渡し（完成までの工程）</li>
      </ol>
      <p>注文住宅は自由度が高い分、決めることが多く、失敗しやすい部分もあります。しかし、この流れを理解しておけば、後悔のない家づくりができます。</p>

      <h2>次に読む記事</h2>
      <p>次は、注文住宅で特に多い悩み<br />👉 <a href="/articles/9">「鳥取でマイホーム購入で失敗した人の共通点｜後悔しないための対策」</a>を知っておくと、さらに安心して進められます。</p>
    `
  },
  {
    id: 9,
    step: '01',
    title: "鳥取でマイホーム購入で失敗した人の共通点｜リアルな後悔と対策まとめ",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約11分",
    image: "https://readdy.ai/api/search-image?query=A%20thoughtful%20scene%20of%20a%20quiet%20Japanese%20residential%20street%20in%20Tottori%20on%20a%20calm%20winter%20morning%2C%20empty%20road%20lined%20with%20traditional%20houses%2C%20bare%20trees%20along%20the%20path%2C%20soft%20gray%20sky%20with%20gentle%20light%2C%20honest%20and%20reflective%20atmosphere%2C%20realistic%20architectural%20photography%2C%20subdued%20cool%20tones%2C%20peaceful%20yet%20introspective%20mood&width=600&height=400&seq=art009&orientation=landscape",
    excerpt: "「鳥取でマイホームを買いたいけれど、失敗したくない…」実際に後悔した人のリアルな体験から、失敗の共通点と具体的な対策をわかりやすく解説します。",
    tags: ["鳥取", "失敗", "後悔しない"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取でマイホームを買いたいけれど、失敗したくない…」<br />そんな不安を抱えていませんか？</p>
      <p>実際、私の周りでも<br />・焦って決めて後悔した<br />・土地選びで失敗した<br />・住んでから寒さに驚いた<br />など、マイホーム購入で後悔した人は少なくありません。</p>
      <p>特に鳥取は、<strong>気候・土地条件・住宅会社の特徴が地域ごとに大きく違う</strong>ため、失敗しやすいポイントが明確に存在します。</p>
      <p>この記事では、鳥取でマイホーム購入に失敗した人のリアルな後悔をもとに、「なぜ失敗したのか？」「どうすれば避けられるのか？」をわかりやすく解説します。</p>

      <h2>結論：鳥取でマイホーム購入に失敗した人の共通点は次の5つです</h2>
      <ol>
        <li>焦って決めてしまい、比較しなかった</li>
        <li>土地の条件（地盤・日当たり・雪）を甘く見た</li>
        <li>冬の寒さ対策を軽視した</li>
        <li>生活動線を考えず間取りで後悔した</li>
        <li>将来の変化を想定していなかった</li>
      </ol>
      <p>これらを避けるだけで、後悔のない家づくりができます。</p>

      <h2>① 焦って決めてしまい、比較しなかった（最も多い失敗）</h2>
      <p>鳥取で最も多い失敗が、「早く家が欲しい」という気持ちから、比較せずに決めてしまうこと。</p>
      <h3>よくある失敗例</h3>
      <ul>
        <li>営業担当の勢いに押されて契約</li>
        <li>他社の標準仕様を知らずに損をした</li>
        <li>もっと安く建てられる会社があった</li>
        <li>断熱性能が低い会社を選んでしまった</li>
      </ul>
      <p>私自身も最初は「どこも同じだろう」と思っていましたが、実際は会社ごとに性能・価格・保証が大きく違います。</p>
      <h3>対策</h3>
      <ul>
        <li>最低3社は比較する</li>
        <li>資料請求で標準仕様をチェック</li>
        <li>営業担当の相性を見る</li>
        <li>見学会で実物を体感する</li>
      </ul>
      <p>比較するだけで、失敗リスクは大幅に減ります。</p>

      <h2>② 土地の条件（地盤・日当たり・雪）を甘く見た</h2>
      <p>鳥取は地域によって土地の条件が大きく異なります。そのため、土地選びの失敗は後悔につながりやすいです。</p>
      <h3>よくある失敗例</h3>
      <ul>
        <li>冬に日当たりが悪くて寒い</li>
        <li>地盤改良費が高額になった</li>
        <li>雪が多いエリアで車が出せない</li>
        <li>道路が狭くて駐車が大変</li>
        <li>周辺環境が合わなかった</li>
      </ul>
      <p>特に鳥取市は地価が高く、米子市はエリア差が大きく、倉吉市は比較的手頃です。</p>
      <h3>対策</h3>
      <ul>
        <li>地盤調査の必要性を理解する</li>
        <li>冬の日当たりを重視する</li>
        <li>道路幅・交通量をチェック</li>
        <li>住宅会社と一緒に土地を探す</li>
      </ul>
      <p>土地は「立地×地盤×日当たり」で決めるのが鉄則です。</p>

      <h2>③ 冬の寒さ対策を軽視した（鳥取特有の後悔）</h2>
      <p>鳥取の冬は本当に寒く、断熱性能・気密性能が低い家は後悔の原因になります。</p>
      <h3>よくある失敗例</h3>
      <ul>
        <li>エアコンをつけても寒い</li>
        <li>光熱費が高い</li>
        <li>結露が発生してカビが生える</li>
        <li>玄関・脱衣所が極端に寒い</li>
      </ul>
      <p>「建物価格が安いから」という理由で選ぶと、住んでからの光熱費が高くつくケースが多いです。</p>
      <h3>対策</h3>
      <ul>
        <li>UA値（断熱性能）を確認</li>
        <li>C値（気密性能）を確認</li>
        <li>標準仕様の断熱材をチェック</li>
        <li>冬の見学会で暖かさを体感する</li>
      </ul>
      <p>性能は「見えない部分」ですが、住み心地に直結します。</p>

      <h2>④ 生活動線を考えず間取りで後悔した</h2>
      <p>間取りの後悔は、住んでから気づくケースが多いです。</p>
      <h3>よくある失敗例</h3>
      <ul>
        <li>洗濯動線が長くて不便</li>
        <li>キッチンが狭い</li>
        <li>収納が足りない</li>
        <li>コンセントの位置が悪い</li>
        <li>子ども部屋の使い方を考えていなかった</li>
      </ul>
      <p>特に鳥取は冬の家事が大変なため、洗濯動線・家事動線の良し悪しが生活の快適さを左右します。</p>
      <h3>対策</h3>
      <ul>
        <li>洗濯→干す→しまうを最短に</li>
        <li>収納は多めに確保</li>
        <li>コンセントは多めに</li>
        <li>将来の生活をイメージして間取りを決める</li>
      </ul>

      <h2>⑤ 将来の変化を想定していなかった</h2>
      <p>今の生活だけで家を決めると、将来後悔します。</p>
      <h3>よくある失敗例</h3>
      <ul>
        <li>子ども部屋が足りない</li>
        <li>1階に寝室が必要になった</li>
        <li>収納が足りなくなった</li>
        <li>車が増えて駐車場が狭い</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>将来の家族構成を想定する</li>
        <li>可変性のある間取りにする</li>
        <li>駐車場は広めに確保</li>
        <li>収納は多めに設計</li>
      </ul>
      <p>家は「今」だけでなく「未来」も考えて設計する必要があります。</p>

      <h2>まとめ</h2>
      <p>鳥取でマイホーム購入に失敗した人の共通点は次の5つです。</p>
      <ol>
        <li>焦って決めて比較しなかった</li>
        <li>土地の条件を甘く見た</li>
        <li>冬の寒さ対策を軽視した</li>
        <li>間取りで後悔した</li>
        <li>将来の変化を想定していなかった</li>
      </ol>
      <p>これらを避けることで、後悔のない家づくりができます。</p>

      <h2>次に読む記事</h2>
      <p>次は、後悔しやすいポイントをさらに深掘りした<br />👉 <a href="/articles/10">「鳥取の家づくりで後悔しやすいポイント10選｜実体験から学ぶ注意点」</a>をチェックしておくと、より安心して進められます。</p>
    `
  },
  {
    id: 10,
    step: '01',
    title: "鳥取の家づくりで後悔しやすいポイント10選｜実体験から学ぶ注意点",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約14分",
    image: "https://readdy.ai/api/search-image?query=A%20serene%20Japanese%20residential%20neighborhood%20in%20Tottori%20during%20winter%2C%20frost%20on%20rooftops%20of%20modern%20houses%2C%20bare%20deciduous%20trees%20lining%20a%20quiet%20street%2C%20cold%20soft%20morning%20light%20casting%20long%20shadows%2C%20clean%20minimalist%20suburban%20scenery%2C%20muted%20cool%20tones%20with%20warm%20window%20glows%2C%20realistic%20and%20contemplative%20architectural%20photography%2C%20no%20people%2C%20genuine%20and%20lived-in%20atmosphere&width=600&height=400&seq=art010&orientation=landscape",
    excerpt: "「鳥取で家づくりを始めたいけど、後悔したくない…」実際に後悔した人の体験を10個のポイントにまとめ、どうすれば避けられるかをわかりやすく解説します。",
    tags: ["鳥取", "後悔", "10選"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家づくりを始めたいけれど、後悔したくない…」<br />そんな不安を感じていませんか？</p>
      <p>実際、私の周りでも<br />・間取りの後悔<br />・土地選びの失敗<br />・性能不足で寒い家になった<br />など、家づくりで後悔した人は少なくありません。</p>
      <p>特に鳥取は、<strong>気候・土地条件・住宅会社の特徴が地域ごとに大きく違う</strong>ため、後悔しやすいポイントが明確に存在します。</p>
      <p>この記事では、鳥取で家づくりをした人が実際に後悔したポイントを10個にまとめ、「どうすれば避けられるのか？」をわかりやすく解説します。</p>

      <h2>結論：後悔しやすいポイント10選</h2>
      <ol>
        <li>冬の寒さ対策を甘く見た</li>
        <li>収納が足りない</li>
        <li>動線が悪くて暮らしにくい</li>
        <li>土地選びを急いだ</li>
        <li>日当たりを軽視した</li>
        <li>外構費用を後回しにした</li>
        <li>コンセントの位置が不便</li>
        <li>予算オーバーで生活が苦しい</li>
        <li>住宅会社を比較しなかった</li>
        <li>将来の家族構成を考えていなかった</li>
      </ol>
      <p>これらを知っておくだけで、後悔のない家づくりができます。</p>

      <h2>① 冬の寒さ対策を甘く見た（鳥取で最も多い後悔）</h2>
      <p>鳥取は冬の寒さが厳しく、断熱性能・気密性能が低い家は本当に寒いです。</p>
      <h3>よくある後悔</h3>
      <ul>
        <li>エアコンをつけても寒い</li>
        <li>光熱費が高い</li>
        <li>結露が発生してカビが生える</li>
      </ul>
      <p>私自身もモデルハウスで初めて高断熱住宅を体感したとき、「こんなに違うのか…」と衝撃を受けました。</p>
      <h3>対策</h3>
      <ul>
        <li>UA値（断熱性能）を確認</li>
        <li>C値（気密性能）を確認</li>
        <li>標準仕様の断熱材をチェック</li>
        <li>冬の見学会で暖かさを体感する</li>
      </ul>

      <h2>② 収納が足りない（特に子育て世帯に多い）</h2>
      <p>家が完成してから「収納が全然足りない…」という後悔は非常に多いです。</p>
      <h3>よくある後悔</h3>
      <ul>
        <li>玄関が靴であふれる</li>
        <li>パントリーが小さくて使いにくい</li>
        <li>子どもの荷物が増えて収納不足</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>玄関収納は広めに</li>
        <li>パントリーは可動棚にする</li>
        <li>将来の荷物量を想定して収納を確保</li>
      </ul>
      <p>収納は「多すぎるくらいでちょうどいい」です。</p>

      <h2>③ 動線が悪くて暮らしにくい</h2>
      <p>間取りの後悔で最も多いのが「動線」です。</p>
      <h3>よくある後悔</h3>
      <ul>
        <li>洗濯動線が長い</li>
        <li>キッチンからダイニングが遠い</li>
        <li>2階のトイレが使いにくい</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>洗濯→干す→しまうを最短に</li>
        <li>キッチン横にパントリー</li>
        <li>トイレの位置は生活動線を考える</li>
      </ul>
      <p>実際の生活をイメージしながら決めることが大切です。</p>

      <h2>④ 土地選びを急いだ（鳥取で特に多い後悔）</h2>
      <p>鳥取は地域によって土地の条件が大きく異なります。</p>
      <h3>よくある後悔</h3>
      <ul>
        <li>日当たりが悪い</li>
        <li>冬に雪が多いエリアだった</li>
        <li>地盤改良費が高額になった</li>
        <li>道路が狭くて車が停めにくい</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>住宅会社と一緒に土地を探す</li>
        <li>地盤の強さを確認する</li>
        <li>冬の日当たりを重視する</li>
        <li>道路幅・交通量をチェック</li>
      </ul>
      <p>土地は「立地×地盤×日当たり」で決めるのが鉄則です。</p>

      <h2>⑤ 日当たりを軽視した（冬の後悔につながる）</h2>
      <p>鳥取の冬は日照時間が短いため、日当たりの悪い家は本当に寒いです。</p>
      <h3>よくある後悔</h3>
      <ul>
        <li>リビングが暗い</li>
        <li>冬に暖房が効きにくい</li>
        <li>洗濯物が乾かない</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>南向きリビングを優先</li>
        <li>窓の位置と大きさを調整</li>
        <li>隣家との距離を確認</li>
      </ul>

      <h2>⑥ 外構費用を後回しにした（追加費用の原因）</h2>
      <p>外構（駐車場・フェンス・庭）は後回しにされがちですが、実は100〜200万円かかることも多いです。</p>
      <h3>よくある後悔</h3>
      <ul>
        <li>駐車場が狭い</li>
        <li>砂利のままで使いにくい</li>
        <li>フェンスがなくて視線が気になる</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>外構費用を最初から予算に入れる</li>
        <li>駐車場は広めに確保</li>
        <li>生活動線に合わせて外構を設計</li>
      </ul>

      <h2>⑦ コンセントの位置が不便</h2>
      <p>住んでから気づく後悔の代表例です。</p>
      <h3>よくある後悔</h3>
      <ul>
        <li>キッチン家電が多くて足りない</li>
        <li>ベッド周りにコンセントがない</li>
        <li>掃除機のコードが届かない</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>家電の配置を想定して位置を決める</li>
        <li>ベッド周りは必ず2口以上</li>
        <li>階段下・廊下にも設置</li>
      </ul>

      <h2>⑧ 予算オーバーで生活が苦しい</h2>
      <p>家づくりで最も深刻な後悔です。</p>
      <h3>よくある後悔</h3>
      <ul>
        <li>オプションが増えて建物価格が膨らんだ</li>
        <li>土地代が高くて予算が足りなくなった</li>
        <li>諸費用を見落としていた</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>月々の返済額から逆算する</li>
        <li>ボーナス払いは基本なし</li>
        <li>諸費用は総額の10％前後で計算</li>
      </ul>

      <h2>⑨ 住宅会社を比較しなかった</h2>
      <p>比較しないと、「もっと良い会社があったのに…」という後悔につながります。</p>
      <h3>対策</h3>
      <ul>
        <li>複数社の資料を取り寄せる</li>
        <li>標準仕様を比較する</li>
        <li>断熱性能・気密性能を確認</li>
        <li>営業担当の相性を見る</li>
      </ul>

      <h2>⑩ 将来の家族構成を考えていなかった</h2>
      <p>今の生活だけで間取りを決めると後悔します。</p>
      <h3>よくある後悔</h3>
      <ul>
        <li>子ども部屋が足りない</li>
        <li>1階に寝室が必要になった</li>
        <li>収納が足りない</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>将来の変化を想定して間取りを設計</li>
        <li>可変性のある間取りにする</li>
        <li>収納は多めに確保</li>
      </ul>

      <h2>まとめ</h2>
      <p>鳥取の家づくりで後悔しやすいポイントは次の10個です。</p>
      <ol>
        <li>冬の寒さ対策不足</li>
        <li>収納不足</li>
        <li>動線の悪さ</li>
        <li>土地選びの失敗</li>
        <li>日当たりの軽視</li>
        <li>外構費用の後回し</li>
        <li>コンセント位置の後悔</li>
        <li>予算オーバー</li>
        <li>住宅会社の比較不足</li>
        <li>将来の見通し不足</li>
      </ol>
      <p>これらを避けることで、後悔のない家づくりができます。</p>

      <h2>次に読む記事</h2>
      <p>次は、後悔を避けるために知っておきたい<br />👉 <a href="/articles/11">「鳥取で住宅購入はいつが買い時？年収・金利から最適タイミングを解説」</a>をチェックしておくと、さらに安心して進められます。</p>
    `
  },
  {
    id: 11,
    step: '01',
    title: "鳥取で住宅購入はいつが買い時？年収・金利から最適タイミングを解説",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約12分",
    image: "https://readdy.ai/api/search-image?query=A%20calm%20and%20optimistic%20scene%20of%20a%20young%20Japanese%20couple%20standing%20in%20front%20of%20a%20newly%20built%20modern%20house%20in%20Tottori%2C%20lush%20green%20trees%20and%20a%20clear%20blue%20sky%20in%20the%20background%2C%20soft%20golden%20afternoon%20light%20illuminating%20the%20house%20exterior%2C%20warm%20residential%20atmosphere%2C%20professional%20lifestyle%20photography%2C%20natural%20tones%20of%20white%20wood%20and%20stone%2C%20no%20faces%20visible%2C%20hopeful%20and%20forward-looking%20mood&width=600&height=400&seq=art011&orientation=landscape",
    excerpt: "「鳥取で家を買うなら、いつがベスト？」年収・金利・ライフイベントの3つの視点から、住宅購入の最適タイミングをわかりやすく解説します。",
    tags: ["鳥取", "買い時", "タイミング"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を買うなら、いつがベストなんだろう…」<br />そんな悩みを抱えていませんか？</p>
      <p>実際、私の周りでも、早く買いすぎて後悔した、逆に迷っているうちに土地が売れてしまった、金利が上がって支払いが増えた、など、タイミングの判断で失敗した人は少なくありません。</p>
      <p>住宅購入は人生で最も大きな買い物。<strong>「いつ買うか」</strong>は、家の満足度だけでなく、家計にも大きく影響します。</p>

      <h2>結論：ベストタイミングの3条件</h2>
      <ol>
        <li>年収と支出が安定し、無理のない返済計画が立てられるとき</li>
        <li>金利が低く、住宅ローンの負担が軽い時期</li>
        <li>子育て・通勤・生活動線など、ライフイベントが明確になったとき</li>
      </ol>
      <p>特に鳥取は土地価格が安定しているため、<strong>「金利」と「ライフイベント」がタイミング判断のカギ</strong>になります。</p>

      <h2>① 年収と支出が安定しているタイミングがベスト</h2>
      <h3>年収別の購入タイミングの目安</h3>
      <table>
        <thead>
          <tr><th>年収</th><th>月々の返済目安</th><th>備考</th></tr>
        </thead>
        <tbody>
          <tr><td>300〜350万円</td><td>6〜7万円</td><td>無理なく払えるライン</td></tr>
          <tr><td>350〜450万円</td><td>7〜9万円</td><td>現実的な返済額</td></tr>
          <tr><td>450〜550万円</td><td>9〜11万円</td><td>余裕のある返済が可能</td></tr>
        </tbody>
      </table>
      <p>鳥取県は全国と比べて物価が低いため、<strong>年収350〜450万円でも十分に家を建てられる</strong>のが特徴です。</p>

      <h3>支出が安定している時期が買い時</h3>
      <ul>
        <li>車のローンが終わった</li>
        <li>子どもの教育費がまだ本格化していない</li>
        <li>転職の予定がない</li>
      </ul>

      <h2>② 金利が低い時期は"買い時"になる</h2>
      <h3>金利が1％変わるとどうなる？</h3>
      <table>
        <thead>
          <tr><th>金利</th><th>総返済額（3,000万円・35年）</th><th>差額</th></tr>
        </thead>
        <tbody>
          <tr><td>1.0％</td><td>約3,500万円</td><td>―</td></tr>
          <tr><td>2.0％</td><td>約4,000万円</td><td>約500万円の差</td></tr>
        </tbody>
      </table>
      <p><strong>たった1％の違いで500万円以上の差</strong>が出ます。</p>

      <h2>③ ライフイベントが明確になったときが買い時</h2>
      <h3>よくある購入タイミング</h3>
      <ul>
        <li>結婚した</li>
        <li>子どもが生まれた</li>
        <li>子どもが小学校に入る前</li>
        <li>実家の近くに住みたい</li>
        <li>通勤時間を短くしたい</li>
      </ul>

      <h2>④ 鳥取の土地価格は"急激に上がらない"ため、焦る必要はない</h2>
      <h3>鳥取の土地価格の特徴</h3>
      <table>
        <thead>
          <tr><th>エリア</th><th>土地価格の特徴</th></tr>
        </thead>
        <tbody>
          <tr><td>鳥取市</td><td>やや高めだが急騰はしにくい</td></tr>
          <tr><td>米子市</td><td>エリア差が大きいが安定</td></tr>
          <tr><td>倉吉市</td><td>比較的手頃で変動が少ない</td></tr>
        </tbody>
      </table>
      <p>焦って買うより、<strong>金利・ライフイベント・予算のバランス</strong>で判断する方が賢い選択になります。</p>

      <h2>⑤ 鳥取で"買って後悔した人"のタイミングの特徴</h2>
      <h3>後悔したタイミング例</h3>
      <ul>
        <li>子どもが生まれる直前でバタバタして決めた</li>
        <li>金利を気にせず勢いで契約した</li>
        <li>住宅会社を比較せずに急いで決めた</li>
        <li>土地が売れそうで焦って購入した</li>
      </ul>

      <h2>まとめ</h2>
      <p>「金利」×「ライフイベント」×「無理のない返済」、この3つが揃ったときが、あなたにとっての最適な買い時です。</p>

      <h2>次に読む記事</h2>
      <p>次は、住宅購入のタイミングとセットで知っておきたい<br />👉 <a href="/articles/12">「鳥取で家づくりする前に知るべき注意点｜見落としがちな落とし穴とは」</a>をチェックしておくと、さらに後悔のない選択ができます。</p>
    `
  },
  {
    id: 12,
    step: '01',
    title: "鳥取で家づくりする前に知るべき注意点｜見落としがちな落とし穴とは",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約12分",
    image: "https://readdy.ai/api/search-image?query=A%20quiet%20Japanese%20residential%20street%20in%20Tottori%20prefecture%20on%20a%20cloudy%20winter%20morning%2C%20traditional%20and%20modern%20houses%20side%20by%20side%2C%20bare%20trees%20casting%20shadows%20on%20the%20road%2C%20muted%20cool%20tones%20with%20earthy%20grays%20and%20greens%2C%20caution%20atmosphere%20near%20a%20residential%20construction%20area%2C%20contemplative%20and%20informative%20mood%2C%20realistic%20architectural%20photography%2C%20no%20people%2C%20clean%20suburban%20environment%20with%20soft%20diffused%20light&width=600&height=400&seq=art012&orientation=landscape",
    excerpt: "「鳥取で家を買うなら、何に気をつければいいのかわからない…」家づくりの初期段階で役立つ5つの注意点と落とし穴をわかりやすく解説します。",
    tags: ["鳥取", "注意点", "落とし穴"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家づくりを始めたいけれど、何に気をつければいいのかわからない…」<br />そんな不安を感じていませんか？</p>
      <p>実際、私の周りでも<br />・土地選びで失敗した<br />・予算が膨らんで苦しくなった<br />・住宅会社選びで後悔した<br />など、家づくりの"初期段階"でつまずいた人が多くいます。</p>
      <p>特に鳥取は、<strong>気候・土地条件・住宅会社の特徴が地域ごとに大きく違う</strong>ため、注意すべきポイントが明確に存在します。この記事では、鳥取で家づくりを始める前に必ず知っておきたい注意点を、初心者でもわかりやすく解説します。</p>

      <h2>結論：鳥取で家づくりを始める前に注意すべきポイントは次の5つです</h2>
      <ol>
        <li>土地の条件（地盤・日当たり・雪）を必ず確認する</li>
        <li>予算の上限を決めずに動き出さない</li>
        <li>住宅会社の"標準仕様"を必ずチェックする</li>
        <li>冬の寒さ対策（断熱・気密）を軽視しない</li>
        <li>焦って契約しない（比較は必須）</li>
      </ol>
      <p>これらを押さえるだけで、家づくりの失敗リスクは大幅に減らせます。</p>

      <h2>① 土地の条件（地盤・日当たり・雪）を必ず確認する</h2>
      <p>鳥取は地域によって土地の特徴が大きく異なります。そのため、土地選びの注意点を知らずに進めると後悔しやすいです。</p>
      <h3>注意点① 地盤の強さ</h3>
      <p>鳥取市・米子市・倉吉市でも、地盤の強さはエリアによって大きく違います。地盤が弱いと…</p>
      <ul>
        <li>地盤改良費が高額になる</li>
        <li>家が傾くリスクがある</li>
        <li>耐震性に影響する</li>
      </ul>
      <p>地盤改良費は30〜100万円以上かかることもあるため、必ず確認が必要です。</p>
      <h3>注意点② 冬の日当たり</h3>
      <p>鳥取の冬は日照時間が短いため、日当たりの悪い土地は本当に寒いです。</p>
      <ul>
        <li>リビングが暗い</li>
        <li>暖房効率が悪い</li>
        <li>洗濯物が乾かない</li>
      </ul>
      <p>冬の見学が特におすすめです。</p>
      <h3>注意点③ 雪の影響</h3>
      <p>鳥取市は比較的雪が少ないですが、米子市・倉吉市はエリアによって積雪量が変わります。</p>
      <ul>
        <li>車が出しにくい</li>
        <li>道路が凍結する</li>
        <li>駐車場の除雪が大変</li>
      </ul>
      <p>土地選びは「立地×地盤×日当たり×雪」で判断するのが鉄則です。</p>

      <h2>② 予算の上限を決めずに動き出さない</h2>
      <p>家づくりの初期段階で最も多い失敗が、予算を決めずに動き出してしまうこと。</p>
      <h3>注意点</h3>
      <ul>
        <li>気に入った土地を買ったら建物費が足りなくなる</li>
        <li>オプションが増えて予算オーバー</li>
        <li>諸費用を見落としていた</li>
        <li>住宅ローンの返済が苦しくなる</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>月々の返済額から逆算する</li>
        <li>ボーナス払いは基本なし</li>
        <li>諸費用は総額の10％前後で計算</li>
        <li>鳥取市・米子市・倉吉市で土地価格が違うことを理解する</li>
      </ul>
      <p>予算を決めるだけで、失敗リスクは大幅に減ります。</p>

      <h2>③ 住宅会社の"標準仕様"を必ずチェックする</h2>
      <p>家づくり初心者が見落としがちなのが、標準仕様の内容を確認すること。</p>
      <h3>注意点</h3>
      <ul>
        <li>坪単価が安く見えても、標準仕様が低い</li>
        <li>結局オプションだらけで高額になる</li>
        <li>断熱材・窓・設備が最低ランク</li>
        <li>外構が含まれていない</li>
      </ul>
      <p>特に鳥取は冬が寒いため、断熱性能・窓の性能は標準仕様でどこまで含まれるかが重要です。</p>
      <h3>対策</h3>
      <ul>
        <li>標準仕様の一覧を必ずもらう</li>
        <li>断熱材・窓・設備のグレードを確認</li>
        <li>外構費用が含まれているかチェック</li>
        <li>他社と比較して違いを把握する</li>
      </ul>

      <h2>④ 冬の寒さ対策（断熱・気密）を軽視しない</h2>
      <p>鳥取の家づくりで最も後悔しやすいのが、冬の寒さ対策を軽視すること。</p>
      <h3>注意点</h3>
      <ul>
        <li>断熱性能（UA値）が低い</li>
        <li>気密性能（C値）が悪い</li>
        <li>玄関・脱衣所が極端に寒い</li>
        <li>光熱費が高くなる</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>UA値（断熱性能）を確認</li>
        <li>C値（気密性能）を確認</li>
        <li>モデルハウスで暖かさを体感</li>
        <li>標準仕様の断熱材をチェック</li>
      </ul>
      <p>性能は「見えない部分」ですが、住み心地に直結します。</p>

      <h2>⑤ 焦って契約しない（比較は必須）</h2>
      <p>家づくりで最も危険なのが、焦って契約してしまうこと。</p>
      <h3>注意点</h3>
      <ul>
        <li>営業担当の勢いに押されて契約</li>
        <li>他社の標準仕様を知らずに損をする</li>
        <li>もっと安く建てられる会社があった</li>
        <li>性能が低い会社を選んでしまう</li>
      </ul>
      <h3>対策</h3>
      <ul>
        <li>最低3社は比較する</li>
        <li>資料請求で違いを把握</li>
        <li>営業担当の相性を見る</li>
        <li>見学会で実物を体感する</li>
      </ul>
      <p>比較するだけで、後悔はほぼ防げます。</p>

      <h2>まとめ</h2>
      <p>鳥取で家づくりを始める前に注意すべきポイントは次の5つです。</p>
      <ol>
        <li>土地の条件（地盤・日当たり・雪）</li>
        <li>予算の上限を決める</li>
        <li>標準仕様の確認</li>
        <li>冬の寒さ対策</li>
        <li>焦って契約しない</li>
      </ol>
      <p>これらを押さえることで、後悔のない家づくりができます。</p>

      <h2>次に読む記事</h2>
      <p>次は、家づくりの判断材料として重要な<br />👉 <a href="/articles/13">「鳥取で新築を建てるメリット・デメリット｜建売と注文住宅どちらが得？」</a>をチェックしておくと、より賢い選択ができます。</p>
    `
  },
  {
    id: 13,
    step: '01',
    title: "鳥取で新築を建てるメリット・デメリット｜建売と注文住宅どちらが得？",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約13分",
    image: "https://readdy.ai/api/search-image?query=A%20brand%20new%20modern%20Japanese%20house%20under%20a%20clear%20blue%20sky%20in%20Tottori%2C%20pristine%20white%20exterior%20walls%20with%20natural%20wood%20trim%2C%20freshly%20landscaped%20front%20garden%20with%20young%20green%20shrubs%20and%20stone%20path%2C%20bright%20cheerful%20daytime%20light%2C%20crisp%20and%20clean%20architectural%20photography%2C%20no%20people%2C%20sense%20of%20new%20beginning%20and%20quality%20craftsmanship%2C%20warm%20inviting%20tones%20with%20minimal%20design&width=600&height=400&seq=art013&orientation=landscape",
    excerpt: "「鳥取で家を建てるなら、新築がいいのか、中古や建売の方がいいのか…」新築のメリット・デメリットをわかりやすく解説し、どんな人に向いているかが明確になります。",
    tags: ["鳥取", "新築", "メリット"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を建てるなら、新築がいいのか、中古や建売の方がいいのか…」<br />そんな悩みを抱えていませんか？</p>
      <p>私自身も家づくりを考え始めたとき、</p>
      <ul>
        <li>新築は高い？</li>
        <li>中古は寒いって本当？</li>
        <li>建売は性能が心配…</li>
      </ul>
      <p>など、何が正解かわからず迷いました。</p>
      <p>特に鳥取は、<strong>冬の寒さ・土地価格・住宅会社の特徴</strong>が他県と大きく違うため、新築のメリット・デメリットを正しく理解しておくことが重要です。</p>

      <h2>結論：鳥取で新築を建てるメリット・デメリット</h2>
      <p><strong>メリット</strong></p>
      <ol>
        <li>冬でも暖かい高性能な家が建てられる</li>
        <li>間取りを自由に決められる</li>
        <li>メンテナンス費用が少ない</li>
        <li>住宅ローン控除などの制度が使える</li>
        <li>資産価値が落ちにくい</li>
      </ol>
      <p><strong>デメリット</strong></p>
      <ol>
        <li>費用が高くなりやすい</li>
        <li>決めることが多く時間がかかる</li>
        <li>土地探しが難しい</li>
        <li>完成までに半年〜1年かかる</li>
        <li>住宅会社選びを間違えると後悔する</li>
      </ol>
      <p>鳥取は気候の影響が大きいため、<strong>「性能の高さ」が新築最大のメリット</strong>になります。</p>

      <h2>① 鳥取で新築を建てるメリット</h2>

      <h3>① 冬でも暖かい高性能な家が建てられる（鳥取最大のメリット）</h3>
      <p>鳥取の冬は寒く、湿気も多いため、<strong>断熱性能・気密性能が高い新築は圧倒的に快適</strong>です。</p>
      <p>新築なら</p>
      <ul>
        <li>高断熱の窓</li>
        <li>高性能断熱材</li>
        <li>気密施工</li>
      </ul>
      <p>が標準で選べるため、中古住宅と比べて暖かさが段違いです。</p>
      <p>実際、私の知人も「新築にして光熱費が半分になった」と言っていました。</p>

      <h3>② 間取りを自由に決められる</h3>
      <p>新築（特に注文住宅）は、<strong>生活動線・収納・家事ラク動線を自由に設計できる</strong>のが魅力です。</p>
      <ul>
        <li>広めの玄関</li>
        <li>大きめのシューズクローク</li>
        <li>ベビーカー・アウトドア用品の収納</li>
        <li>車から荷物を運びやすい動線</li>
        <li>洗濯動線の短縮</li>
      </ul>

      <h3>③ メンテナンス費用が少ない</h3>
      <p>新築は設備がすべて新品のため、10年程度は大きな修繕費がかかりません。</p>

      <h3>④ 住宅ローン控除などの制度が使える</h3>
      <ul>
        <li>住宅ローン控除</li>
        <li>こどもエコすまい支援事業</li>
        <li>地域の補助金</li>
      </ul>

      <h3>⑤ 資産価値が落ちにくい</h3>
      <p>鳥取は土地価格が安定しているため、<strong>新築の方が資産価値が維持されやすい</strong>傾向があります。</p>

      <h2>② 鳥取で新築を建てるデメリット</h2>

      <h3>① 費用が高くなりやすい（最大のデメリット）</h3>
      <p>特に鳥取市は土地価格が高めなので、<strong>予算オーバーのリスク</strong>があります。</p>

      <h3>② 決めることが多く、時間がかかる</h3>
      <p>完成まで<strong>半年〜1年かかる</strong>のが一般的です。忙しい共働き世帯は負担に感じることもあります。</p>

      <h3>③ 土地探しが難しい</h3>
      <ul>
        <li>鳥取市 → 人気エリアは競争が激しい</li>
        <li>米子市 → エリア差が大きい</li>
        <li>倉吉市 → 手頃だが選択肢が少ない</li>
      </ul>

      <h3>④ 完成までに時間がかかる</h3>
      <p>急いで引っ越したい人には不向きです。</p>

      <h3>⑤ 住宅会社選びを間違えると後悔する</h3>
      <p><strong>比較せずに決めると後悔しやすい</strong>です。</p>

      <h2>まとめ</h2>
      <p>鳥取は気候の影響が大きいため、性能の高さを重視するなら新築は非常に相性が良い選択肢です。</p>
      <p>さらに、新築は性能・耐久性・保証が整っている分、将来の資産価値が落ちにくく、長期的な資産形成にもつながります。</p>
      <p>だからこそ、私は「鳥取で家を建てるなら、新築という選択肢に前向きになってほしい」と強く感じています。</p>

      <h2>次に読む記事</h2>
      <p>次は、新築と迷いやすい<br />👉 <a href="/articles/14">「鳥取で戸建てとマンションどっちが良い？後悔しない選び方を解説」</a>をチェックしておくと、より判断しやすくなります。</p>
    `
  },
  {
    id: 14,
    step: '01',
    title: "鳥取で戸建てとマンションどっちが良い？後悔しない選び方を解説",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約12分",
    image: "https://public.readdy.ai/ai/img_res/edited_315db09bb8866dec261e2ebc1cfb2842_b36efeb5.jpg",
    excerpt: "「鳥取で家を買うなら、戸建てとマンションどっちが良いんだろう…」鳥取の地域事情に合わせた判断基準で、どんな人がどちらに向いているかを解説します。",
    tags: ["鳥取", "戸建て", "マンション"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を買うなら、戸建てとマンションどっちが良いんだろう…」<br />そんな悩みを抱えていませんか？</p>
      <p>実際、鳥取は他県と違い、<strong>マンションが少なく、戸建てが主流の地域</strong>です。そのため、<strong>鳥取の地域事情に合わせた判断基準</strong>が必要になります。</p>

      <h2>結論：鳥取では戸建てが選ばれやすい理由</h2>
      <ul>
        <li>土地が広く、戸建てが建てやすい</li>
        <li>マンションの供給が少ない</li>
        <li>車移動が中心で戸建ての方が便利</li>
        <li>冬の寒さ対策がしやすい</li>
      </ul>

      <h2>① 鳥取で戸建てを選ぶメリット・デメリット</h2>
      <h3>戸建てのメリット</h3>
      <p><strong>① 土地が自分の資産になる</strong><br />鳥取は土地価格が安定しているため、<strong>土地を所有するメリットが大きい地域</strong>です。</p>
      <p><strong>② 駐車場が広く取れる</strong><br />鳥取は車移動が中心。戸建てなら2〜3台の駐車スペースを確保しやすいです。</p>
      <p><strong>③ 冬の寒さ対策がしやすい</strong><br />断熱性能・気密性能を高めた家を建てられるため、<strong>マンションより暖かい家を作りやすい</strong>のが特徴。</p>
      <p><strong>④ 間取りを自由に決められる</strong><br />子育て世帯は特に、広いリビング・収納・家事動線などを自由に設計できるメリットがあります。</p>

      <h3>戸建てのデメリット</h3>
      <p><strong>① 維持管理が必要</strong></p>
      <ul>
        <li>外壁塗装</li>
        <li>屋根のメンテナンス</li>
        <li>庭の手入れ</li>
      </ul>
      <p><strong>② 冬の光熱費が高くなる可能性</strong><br />性能が低い家を建てると、<strong>マンションより寒く、光熱費が高くなる</strong>ことも。</p>
      <p><strong>③ 立地が郊外になりやすい</strong><br />鳥取市中心部や米子駅周辺は土地が少なく、郊外に建てるケースが多いです。</p>

      <h2>② 鳥取でマンションを選ぶメリット・デメリット</h2>
      <h3>マンションのメリット</h3>
      <p><strong>① 立地が良い</strong><br />鳥取市・米子市のマンションは<strong>駅近・中心部に集中</strong>しています。</p>
      <p><strong>② 管理が楽</strong><br />外壁・屋根・共用部分などは管理会社が対応してくれるため、<strong>忙しい共働き世帯に向いています</strong>。</p>
      <p><strong>③ 防犯性が高い</strong><br />オートロックや管理人がいる物件もあり、防犯面で安心感があります。</p>

      <h3>マンションのデメリット</h3>
      <p><strong>① 物件数が少ない（鳥取最大のデメリット）</strong><br />鳥取はマンションの供給が少なく、<strong>選択肢が非常に限られます</strong>。特に鳥取市は新築マンションがほぼ出ません。</p>
      <p><strong>② 駐車場が別料金</strong><br />車移動が中心の鳥取では、駐車場代が毎月かかるのはデメリット。</p>
      <p><strong>③ 管理費・修繕積立金が必要</strong><br />毎月1〜2万円の固定費がかかるため、長期的には戸建てより高くなるケースもあります。</p>
      <p><strong>④ ペット・リフォームの制限</strong><br />自由度が低く、「好きなようにリフォームしたい」という人には不向きです。</p>

      <h2>③ 鳥取で戸建てが向いている人</h2>
      <ul>
        <li>子育て世帯</li>
        <li>車移動が中心の生活</li>
        <li>広い家・庭が欲しい</li>
        <li>将来の資産価値を重視したい</li>
        <li>冬でも暖かい家に住みたい</li>
      </ul>

      <h2>④ 鳥取でマンションが向いている人</h2>
      <ul>
        <li>駅近・中心部で暮らしたい</li>
        <li>管理の手間を減らしたい</li>
        <li>防犯性を重視したい</li>
        <li>単身・DINKS（夫婦2人）</li>
        <li>車をあまり使わない生活</li>
      </ul>

      <h2>まとめ</h2>
      <p>鳥取は戸建てが主流ですが、ライフスタイルによって最適な選択は変わります。</p>

      <h2>次に読む記事</h2>
      <p>次は、戸建てを検討する人が気になる<br />👉 <a href="/articles/15">「鳥取の家づくり期間はどれくらい？契約〜引き渡しまでの全工程」</a>をチェックしておくと、スケジュールが明確になります。</p>
    `
  },
  {
    id: 15,
    step: '01',
    title: "鳥取の家づくりはどれくらいの期間？契約〜引き渡しまでの全工程",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約13分",
    image: "https://readdy.ai/api/search-image?query=A%20construction%20timeline%20concept%20showing%20a%20modern%20Japanese%20house%20being%20built%20in%20Tottori%2C%20sequential%20stages%20from%20foundation%20to%20completed%20home%2C%20workers%20in%20safety%20helmets%20at%20work%20site%2C%20wooden%20frame%20structure%20under%20clear%20sky%20with%20mountains%20in%20distance%2C%20natural%20daylight%2C%20realistic%20construction%20photography%2C%20warm%20earthy%20tones%2C%20organized%20and%20professional%20building%20process%2C%20green%20countryside%20background&width=600&height=400&seq=art015&orientation=landscape",
    excerpt: "「鳥取で家づくりをすると、どれくらいの期間がかかるんだろう…」土地探しから契約・着工・引き渡しまで、全工程のスケジュールを初心者でもわかりやすく解説します。",
    tags: ["鳥取", "期間", "スケジュール"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家づくりをすると、どれくらいの期間がかかるんだろう…」<br />そんな疑問を持っていませんか？</p>
      <p>実際、家づくりは"思っているより時間がかかる"ことが多く、期間を知らずに動くと、引っ越しや仕事の予定が狂ってしまうこともあります。</p>

      <h2>結論：鳥取で家づくりにかかる期間の目安</h2>
      <p>鳥取で家づくりにかかる期間は、一般的に <strong>6〜12ヶ月</strong>。土地探しから含めると <strong>8〜18ヶ月</strong> が目安です。</p>
      <table>
        <thead>
          <tr><th>工程</th><th>期間の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>① 土地探し</td><td>1〜6ヶ月</td></tr>
          <tr><td>② 住宅会社選び</td><td>1〜2ヶ月</td></tr>
          <tr><td>③ 間取り・見積もり</td><td>1〜2ヶ月</td></tr>
          <tr><td>④ 契約〜着工準備</td><td>1ヶ月</td></tr>
          <tr><td>⑤ 着工〜完成</td><td>4〜6ヶ月</td></tr>
          <tr><td>⑥ 引き渡し</td><td>1週間〜1ヶ月</td></tr>
        </tbody>
      </table>
      <p>鳥取は冬季の工事が遅れやすいため、<strong>季節によって期間が変わる点が特徴</strong>です。</p>

      <h2>① 土地探し（1〜6ヶ月）※鳥取は地域差が大きい</h2>
      <h3>鳥取市</h3>
      <p>→ 3〜6ヶ月かかるケースが多い</p>
      <h3>米子市</h3>
      <p>→ 2〜4ヶ月が目安</p>
      <h3>倉吉市</h3>
      <p>→ 1〜3ヶ月で見つかることが多い</p>

      <h2>② 住宅会社選び（1〜2ヶ月）</h2>
      <table>
        <thead>
          <tr><th>ステップ</th><th>期間の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>資料請求</td><td>1週間</td></tr>
          <tr><td>見学会</td><td>2〜3週間</td></tr>
          <tr><td>見積もり比較</td><td>2〜4週間</td></tr>
        </tbody>
      </table>
      <p>焦って1社で決めると後悔しやすいため、<strong>最低3社は比較するのがおすすめ</strong>です。</p>

      <h2>③ 間取り・見積もり（1〜2ヶ月）</h2>
      <p>特に鳥取は冬の寒さ対策が重要なため、<strong>窓の位置・断熱材・間取りの工夫</strong>に時間をかける人が多いです。</p>

      <h2>④ 契約〜着工準備（1ヶ月）</h2>
      <ul>
        <li>地盤調査</li>
        <li>建築確認申請</li>
        <li>最終仕様の決定</li>
        <li>ローン本審査</li>
      </ul>

      <h2>⑤ 着工〜完成（4〜6ヶ月）※冬は遅れやすい</h2>
      <table>
        <thead>
          <tr><th>工事内容</th><th>期間の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>基礎工事</td><td>1ヶ月</td></tr>
          <tr><td>上棟</td><td>1日</td></tr>
          <tr><td>内装工事</td><td>2〜3ヶ月</td></tr>
          <tr><td>外構工事</td><td>1ヶ月</td></tr>
        </tbody>
      </table>
      <p>特に <strong>12〜2月着工は遅延リスクが高い</strong>ため、スケジュールに余裕を持つことが大切です。</p>

      <h2>⑥ 引き渡し（1週間〜1ヶ月）</h2>
      <ul>
        <li>完成検査</li>
        <li>施主検査</li>
        <li>引き渡し書類の準備</li>
        <li>ローン実行</li>
      </ul>

      <h2>まとめ</h2>
      <p>合計すると、<strong>6〜12ヶ月（最短）〜18ヶ月（ゆっくり）</strong> が目安になります。</p>
      <p>鳥取は冬季の工事遅延や土地探しの難易度があるため、<strong>スケジュールは余裕を持って計画することが大切</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>次は、家づくりの初期段階で役立つ<br />👉 <a href="/articles/16">「鳥取の住宅見学会は行くべき？参加するメリットと注意点を徹底解説」</a>をチェックしておくと、効率よく情報収集できます。</p>
    `
  },
  {
    id: 16,
    step: '01',
    title: "鳥取の住宅見学会は行くべき？参加するメリットと注意点を徹底解説",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約13分",
    image: "https://public.readdy.ai/ai/img_res/edited_fa1c5260f4bc2b31e1f6a026aa5c3fe7_6aa0c13d.jpg",
    excerpt: "「住宅見学会って行ったほうがいいの？」鳥取で家づくりをするなら見学会は必須の理由を、メリット5つと注意点3つで徹底解説。冬の暖かさや間取りのリアルな感覚は体感してこそわかります。",
    tags: ["鳥取", "見学会", "メリット"],
    content: `
      <h2>はじめに</h2>
      <p>「住宅見学会って行ったほうがいいの？」<br />「行くと営業されそうで不安…」<br />そんな気持ちを抱えていませんか？</p>
      <p>私自身も家づくりを始めた頃、見学会に行く意味がわからない、ネットの情報だけで十分では？、営業されるのが嫌だ、と感じていました。</p>
      <p>しかし、実際に見学会に参加してみると、<strong>ネットでは絶対にわからない"リアルな住み心地"が体感できる</strong>ことに気づきました。</p>
      <p>特に鳥取は、冬の厳しい寒さ、夏の蒸し暑さ、そして年間を通した湿気の多さに加え、土地条件の差が家の快適性に大きく影響する地域です。そのため、実際の温度感や空気の流れを体感できる見学会の価値は、他県以上に高いと言えます。</p>

      <h2>結論：鳥取で家づくりをするなら、住宅見学会には必ず行くべき</h2>
      <ol>
        <li>冬の暖かさ・夏の涼しさを体感できる（鳥取では最重要）</li>
        <li>間取り・動線・収納の使いやすさがリアルにわかる</li>
        <li>住宅会社ごとの"本当の実力"が見える</li>
        <li>ネットではわからない生活イメージが湧く</li>
        <li>営業されない見学会も増えていて安心</li>
      </ol>

      <h2>① 冬の暖かさ・夏の涼しさを体感できる（鳥取では最重要）</h2>
      <h3>見学会でわかること</h3>
      <ul>
        <li>冬に玄関や脱衣所が寒くないか</li>
        <li>リビングの暖かさが均一か</li>
        <li>夏にムワッとしないか</li>
        <li>湿気がこもりにくいか</li>
        <li>結露しにくいか</li>
      </ul>

      <h2>② 間取り・動線・収納の使いやすさがリアルにわかる</h2>
      <h3>見学会で気づくポイント</h3>
      <ul>
        <li>洗濯動線が短いか</li>
        <li>キッチンからダイニングの距離</li>
        <li>収納が足りるか</li>
        <li>玄関の広さは十分か</li>
        <li>子どもが走り回れるか</li>
      </ul>
      <p>鳥取は車移動が中心のため、<strong>玄関まわりの広さ・収納量・車からの動線</strong>が生活の快適さに直結します。</p>

      <h2>③ 住宅会社ごとの"本当の実力"が見える</h2>
      <h3>見学会でわかる住宅会社の実力</h3>
      <ul>
        <li>施工の丁寧さ</li>
        <li>デザインのセンス</li>
        <li>標準仕様のレベル</li>
        <li>断熱材・窓の質</li>
        <li>収納の作り方</li>
      </ul>

      <h2>④ ネットではわからない生活イメージが湧く</h2>
      <h3>見学会で得られる気づき</h3>
      <ul>
        <li>家事が楽かどうか</li>
        <li>子どもが遊びやすいか</li>
        <li>家族の距離感</li>
        <li>家具の配置イメージ</li>
        <li>日当たりの良さ</li>
      </ul>

      <h2>⑤ 営業されない見学会も増えていて安心</h2>
      <h3>営業が苦手な人でも安心な理由</h3>
      <ul>
        <li>アンケートなしの見学会が増えている</li>
        <li>営業しない方針の会社も多い</li>
        <li>予約制でゆっくり見られる</li>
        <li>質問したいときだけ話せる</li>
      </ul>

      <h2>⑥ 見学会に行くときの注意点</h2>
      <h3>注意点① 1社だけで判断しない</h3>
      <p>最低3社は比較しましょう。</p>

      <h3>注意点② 完成見学会と構造見学会の両方に行く</h3>
      <table>
        <thead>
          <tr><th>種類</th><th>わかること</th></tr>
        </thead>
        <tbody>
          <tr><td>完成見学会</td><td>住み心地・デザイン・動線がわかる</td></tr>
          <tr><td>構造見学会</td><td>性能・施工の質がわかる</td></tr>
        </tbody>
      </table>

      <h3>注意点③ 冬と夏の両方で体感する</h3>
      <p>鳥取は季節差が大きいため、<strong>冬の暖かさ・夏の涼しさ</strong>を両方チェックするのが理想です。</p>

      <h2>まとめ</h2>
      <p>鳥取で家づくりをするなら、住宅見学会には <strong>必ず行くべき</strong>です。</p>
      <p>鳥取の気候は"体感して判断すべき"だからこそ、見学会は後悔しない家づくりのための最強の情報源です。</p>

      <h2>次に読む記事</h2>
      <p>次は、見学会とセットで知っておきたい<br />👉 <a href="/articles/17">「鳥取で住宅会社を選ぶポイント｜比較すべき5つの基準」</a>をチェックしておくと、さらに失敗しにくくなります。</p>
    `
  },
  {
    id: 17,
    step: '01',
    title: "鳥取で住宅会社を選ぶポイント｜比較すべき5つの基準",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約13分",
    image: "https://public.readdy.ai/ai/img_res/edited_21d65d4039af5697254c3f0bfba6d247_55c3bcbf.jpg",
    excerpt: "「鳥取でどの住宅会社を選べばいいかわからない…」断熱性能・標準仕様・価格の透明性・施工品質・担当者の相性、比較すべき5つの基準をわかりやすく解説します。",
    tags: ["鳥取", "住宅会社", "選び方"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を建てたいけれど、どの住宅会社を選べばいいのかわからない…」<br />そんな悩みを抱えていませんか？</p>
      <p>私自身も家づくりを始めたとき、どの会社も良さそうに見える、価格の違いがわからない、性能の数字をどう見ればいいのか不明、と、判断基準がわからず混乱しました。</p>
      <p>特に鳥取は、<strong>冬の寒さ・夏の蒸し暑さ・湿気・土地条件の差が大きい地域</strong>のため、住宅会社選びを間違えると「住んでからの後悔」が起こりやすいのが特徴です。</p>
      <p>この記事では、鳥取で住宅会社を選ぶ際に比較すべき5つの基準を、初心者でもわかりやすく解説します。</p>

      <h2>結論：比較すべき5つの基準</h2>
      <ol>
        <li>断熱性能・気密性能（UA値・C値）</li>
        <li>標準仕様のレベル</li>
        <li>価格の透明性（見積もりのわかりやすさ）</li>
        <li>施工品質・現場の丁寧さ</li>
        <li>営業担当との相性・会社の姿勢</li>
      </ol>
      <p>この5つを押さえるだけで、住宅会社選びの失敗はほぼ防げます。</p>

      <h2>① 断熱性能・気密性能（UA値・C値）を必ず確認する</h2>
      <p>鳥取の家づくりで最も重要なのが<strong>性能</strong>です。冬は底冷えし、夏は湿気で蒸し暑いため、性能が低い家は本当に住みにくいです。</p>

      <h3>比較すべきポイント</h3>
      <ul>
        <li><strong>UA値（断熱性能）</strong>→ 数字が低いほど暖かい家</li>
        <li><strong>C値（気密性能）</strong>→ 数字が低いほど隙間が少なく快適</li>
        <li><strong>窓の性能</strong>（樹脂窓・Low-Eガラス）</li>
        <li><strong>断熱材の種類</strong>（グラスウール・吹付断熱など）</li>
      </ul>

      <h3>なぜ重要なのか？</h3>
      <ul>
        <li>冬の暖房効率が大きく変わる</li>
        <li>夏の湿気がこもりにくい</li>
        <li>光熱費が安くなる</li>
        <li>結露しにくい</li>
      </ul>
      <p>鳥取の気候に合う家かどうかは、<strong>性能で決まる</strong>と言っても過言ではありません。</p>

      <h2>② 標準仕様のレベルを比較する</h2>
      <p>住宅会社ごとに「標準仕様」が大きく違います。ここを見落とすと、後からオプションだらけになり、予算オーバーの原因になります。</p>

      <h3>チェックすべき標準仕様</h3>
      <ul>
        <li>断熱材のグレード</li>
        <li>窓の種類（アルミ？樹脂？）</li>
        <li>キッチン・浴室・トイレのグレード</li>
        <li>外壁材の種類</li>
        <li>外構費用が含まれているか</li>
        <li>照明・カーテンの有無</li>
      </ul>

      <h3>注意点</h3>
      <p>「坪単価が安い＝お得」ではありません。<strong>標準仕様が低い会社は、結局オプションで高くなります</strong>。</p>

      <h2>③ 価格の透明性（見積もりのわかりやすさ）</h2>
      <p>見積もりがわかりにくい会社は、後から追加費用が発生しやすいです。</p>

      <h3>比較ポイント</h3>
      <ul>
        <li>見積もりが細かく書かれているか</li>
        <li>"一式"が多すぎないか</li>
        <li>追加費用が発生する条件が明確か</li>
        <li>諸費用が含まれているか</li>
      </ul>

      <h3>鳥取でよくある追加費用</h3>
      <ul>
        <li>地盤改良費</li>
        <li>外構費</li>
        <li>冬季施工の追加費用</li>
        <li>カーテン・照明</li>
      </ul>
      <p>価格の透明性は、<strong>信頼できる会社かどうかの判断材料</strong>になります。</p>

      <h2>④ 施工品質・現場の丁寧さを見る</h2>
      <p>家は「誰が建てるか」で品質が決まります。施工が雑な会社は、どれだけ性能が良くても意味がありません。</p>

      <h3>チェックポイント</h3>
      <ul>
        <li>構造見学会に参加する</li>
        <li>現場が整理整頓されているか</li>
        <li>大工さんの対応が丁寧か</li>
        <li>施工写真を見せてもらえるか</li>
      </ul>
      <p>鳥取は湿気が多いため、<strong>施工の丁寧さが家の寿命に直結</strong>します。</p>

      <h2>⑤ 営業担当との相性・会社の姿勢</h2>
      <p>住宅会社選びで意外と重要なのが、<strong>営業担当との相性</strong>です。</p>

      <h3>相性が悪いと起こること</h3>
      <ul>
        <li>話が噛み合わない</li>
        <li>要望が伝わらない</li>
        <li>予算を無視した提案をされる</li>
        <li>打ち合わせがストレスになる</li>
      </ul>

      <h3>良い担当者の特徴</h3>
      <ul>
        <li>話をよく聞いてくれる</li>
        <li>メリット・デメリットを正直に話す</li>
        <li>予算内で提案してくれる</li>
        <li>返信が早い</li>
        <li>押し売りしない</li>
      </ul>
      <p>家づくりは半年〜1年以上の長い付き合いになるため、<strong>信頼できる担当者かどうかは最重要ポイント</strong>です。</p>
      <p>もし担当営業との相性が合わないと感じた場合は、遠慮せず店長や上司に相談してみることをおすすめします。担当者を変えてもらうくらいのことは、決して失礼でも迷惑でもありません。むしろ、会社側も「お互いに気持ちよく家づくりを進めてほしい」と考えています。</p>
      <p>だからこそ、<strong>"この人なら安心して任せられる"と思える担当者と出会うことが、後悔しない家づくりの第一歩</strong>です。</p>

      <h2>まとめ</h2>
      <p>鳥取で住宅会社を選ぶときに比較すべき基準は次の5つです。</p>
      <ol>
        <li>断熱性能・気密性能</li>
        <li>標準仕様のレベル</li>
        <li>価格の透明性</li>
        <li>施工品質</li>
        <li>営業担当との相性</li>
      </ol>
      <p>この5つを押さえることで、住宅会社選びの失敗はほぼ防げます。</p>
      <p>鳥取は気候の影響が大きい地域だからこそ、<strong>性能・施工・担当者の質をしっかり見極めることが、後悔しない家づくりの第一歩</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>次は、住宅会社選びとセットで知っておきたい<br />👉 <a href="/articles/18">「鳥取の家づくりQ&amp;A｜初心者が知っておくべき疑問をまとめて解説」</a>をチェックしておくと、さらに判断しやすくなります。</p>
    `
  },
  {
    id: 18,
    step: '01',
    title: "鳥取の家づくりQ&A｜初心者が知っておくべき疑問をまとめて解説",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約12分",
    image: "https://readdy.ai/api/search-image?query=A%20calm%20Japanese%20family%20couple%20looking%20at%20house%20blueprints%20and%20asking%20questions%20in%20a%20bright%20minimal%20consultation%20room%20in%20Tottori%2C%20clean%20white%20table%20with%20documents%20and%20small%20architectural%20model%2C%20warm%20natural%20light%20from%20large%20window%2C%20relaxed%20and%20informative%20atmosphere%2C%20earthy%20tones%20with%20wood%20accents%2C%20no%20faces%20visible%2C%20modern%20Japanese%20interior%20design%2C%20soft%20ambient%20lighting%2C%20professional%20and%20welcoming%20real%20estate%20office%20setting&width=600&height=400&seq=art017qa&orientation=landscape",
    excerpt: "「何から始めればいいの？」「鳥取の寒さに合う家の性能は？」家づくりで必ず湧く5つの疑問に、鳥取の地域事情を踏まえてわかりやすく答えます。",
    tags: ["鳥取", "Q&A", "よくある質問"],
    content: `
      <h2>はじめに</h2>
      <p>家づくりを始めると、「何から始めればいいの？」「どの会社がいいの？」「鳥取って寒いけど大丈夫？」など、次々と疑問が湧いてきますよね。</p>
      <p>私自身も家づくりを始めた頃は、ネットで調べても答えがバラバラで、何を信じればいいのかわからず不安でした。</p>
      <p>特に鳥取は、<strong>冬の寒さ・夏の蒸し暑さ・湿気・土地条件の差が大きい地域</strong>のため、一般的な情報だけでは判断できないポイントが多くあります。</p>
      <p>そこでこの記事では、<strong>鳥取で家づくりをする人が必ず知っておきたいQ&amp;A</strong>をまとめました。</p>

      <h2>結論：よくある疑問5選</h2>
      <ol>
        <li>家づくりは何から始めればいい？</li>
        <li>鳥取の気候に合う家の性能は？</li>
        <li>土地探しはどれくらい時間がかかる？</li>
        <li>住宅会社はどう選べばいい？</li>
        <li>予算はどう決めればいい？</li>
      </ol>
      <p>これらを理解するだけで、家づくりの不安は大きく減ります。</p>

      <h2>Q1：家づくりは何から始めればいい？</h2>
      <p><strong>A：最初にやるべきは「予算決め」と「情報収集」です。</strong></p>
      <p>家づくりは、①予算 → ②住宅会社選び → ③土地探し → ④間取り → ⑤契約、という流れで進みます。</p>

      <h3>鳥取で最初にやるべきこと</h3>
      <ul>
        <li>月々いくら払えるか決める</li>
        <li>見学会に行って性能を体感する</li>
        <li>3社以上の住宅会社を比較する</li>
      </ul>
      <p>特に鳥取は気候の影響が大きいため、<strong>見学会で"暖かさ・涼しさ"を体感することが最優先</strong>です。</p>

      <h2>Q2：鳥取の気候に合う家の性能は？</h2>
      <p><strong>A：UA値0.46以下、C値1.0以下が目安です。</strong></p>
      <p>鳥取は冬は底冷えする寒さ、夏は湿気で蒸し暑い、という気候のため、性能が低い家は本当に住みにくいです。</p>

      <h3>性能の目安</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>推奨値</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>UA値（断熱性能）</td><td>0.46以下</td><td>数値が低いほど断熱性能が高い</td></tr>
          <tr><td>C値（気密性能）</td><td>1.0以下</td><td>数値が低いほど隙間が少ない</td></tr>
          <tr><td>窓</td><td>樹脂窓＋Low-Eガラス</td><td>熱を逃がさない高性能窓</td></tr>
          <tr><td>断熱材</td><td>高性能グラスウール or 吹付断熱</td><td>鳥取の寒さ・湿気対策に有効</td></tr>
        </tbody>
      </table>

      <h3>なぜ重要？</h3>
      <ul>
        <li>冬の暖房効率が大きく変わる</li>
        <li>夏の湿気がこもりにくい</li>
        <li>光熱費が安くなる</li>
        <li>結露しにくい</li>
      </ul>
      <p>鳥取の家づくりは、<strong>性能が命</strong>です。</p>

      <h2>Q3：土地探しはどれくらい時間がかかる？</h2>
      <p><strong>A：鳥取は地域差が大きく、1〜6ヶ月が目安です。</strong></p>

      <h3>地域別の傾向</h3>
      <table>
        <thead>
          <tr><th>エリア</th><th>期間の目安</th><th>特徴</th></tr>
        </thead>
        <tbody>
          <tr><td>鳥取市</td><td>3〜6ヶ月</td><td>人気エリアは競争が激しい</td></tr>
          <tr><td>米子市</td><td>2〜4ヶ月</td><td>エリア差が大きい</td></tr>
          <tr><td>倉吉市</td><td>1〜3ヶ月</td><td>比較的探しやすい</td></tr>
        </tbody>
      </table>
      <p>鳥取は冬の日照時間が短いため、<strong>日当たりの良い土地は特に人気</strong>です。</p>

      <h2>Q4：住宅会社はどう選べばいい？</h2>
      <p><strong>A：次の5つを比較すれば失敗しません。</strong></p>
      <ol>
        <li>断熱性能・気密性能（UA値・C値）</li>
        <li>標準仕様のレベル</li>
        <li>価格の透明性（見積もりのわかりやすさ）</li>
        <li>施工品質（現場の丁寧さ）</li>
        <li>営業担当との相性</li>
      </ol>

      <h3>特に重要なのは担当者の相性</h3>
      <p>家づくりは半年〜1年以上の長い付き合いになるため、<strong>信頼できる担当者かどうかは最重要ポイント</strong>です。</p>
      <p>もし相性が合わないと感じたら、店長や上司に相談する、担当を変更してもらう、など、遠慮せず伝えてOKです。</p>
      <p>住宅購入は大きな買い物だからこそ、<strong>お互いに気持ちよく契約できる関係性が大切</strong>です。</p>

      <h2>Q5：予算はどう決めればいい？</h2>
      <p><strong>A：月々の返済額から逆算するのが正解です。</strong></p>
      <p>鳥取は物価が比較的安いため、<strong>年収350〜450万円でも家を建てられる</strong>のが特徴です。</p>

      <h3>予算の決め方</h3>
      <ul>
        <li>月々の返済額を決める</li>
        <li>ボーナス払いは基本なし</li>
        <li>諸費用は総額の10％前後</li>
        <li>外構費用も忘れずに</li>
      </ul>

      <h3>よくある失敗</h3>
      <ul>
        <li>土地にお金をかけすぎる</li>
        <li>オプションで予算オーバー</li>
        <li>諸費用を見落とす</li>
      </ul>
      <p>予算を決めるだけで、家づくりの失敗は大幅に減ります。</p>

      <h2>まとめ</h2>
      <p>鳥取の家づくりでよくある疑問は次の5つ。</p>
      <ol>
        <li>家づくりは何から始める？ → 予算決め＋情報収集</li>
        <li>鳥取の気候に合う性能は？ → UA値0.46以下・C値1.0以下</li>
        <li>土地探しの期間は？ → 1〜6ヶ月（地域差あり）</li>
        <li>住宅会社の選び方は？ → 5つの基準で比較</li>
        <li>予算の決め方は？ → 月々の返済額から逆算</li>
      </ol>
      <p>鳥取は気候の影響が大きい地域だからこそ、<strong>性能・土地・担当者の質をしっかり見極めることが、後悔しない家づくりのカギ</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>次は、家づくりの最終判断に役立つ<br />👉 <a href="/articles/19">「鳥取の家づくり予算はいくら必要？年収別の目安と失敗しない決め方」</a>をチェックしておくと、より納得して進められます。</p>
    `
  },
  {
    id: 19,
    step: '01',
    title: "鳥取の家づくり予算はいくら必要？年収別の目安と失敗しない決め方",
    category: "知識をつける",
    date: "2026年3月24日",
    readTime: "約13分",
    image: "https://readdy.ai/api/search-image?query=A%20tidy%20financial%20planning%20scene%20for%20Japanese%20home%20building%20in%20Tottori%2C%20a%20white%20desk%20with%20budget%20documents%2C%20a%20calculator%2C%20small%20house%20model%20and%20a%20notepad%20with%20handwritten%20numbers%2C%20warm%20natural%20light%2C%20clean%20minimal%20Japanese%20interior%2C%20earthy%20beige%20tones%2C%20no%20people%2C%20calm%20and%20organized%20atmosphere%2C%20soft%20focus%20background%20with%20blurred%20greenery%20outside%20the%20window%2C%20professional%20and%20practical%20home%20finance%20advisory%20setting&width=600&height=400&seq=art018&orientation=landscape",
    excerpt: "「鳥取で家を建てるには、どれくらいの予算が必要？」年収別の目安・総額の内訳・よくある失敗まで、鳥取版の予算の決め方をわかりやすく解説します。",
    tags: ["鳥取", "予算", "資金計画"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を建てるには、どれくらいの予算が必要なんだろう…」<br />そんな不安を感じていませんか？</p>
      <p>私自身も家づくりを始めたとき、年収でどれくらい借りられるのか、土地代と建物代のバランス、諸費用ってどれくらい？など、予算の決め方が全くわからず戸惑いました。</p>
      <p>特に鳥取は、<strong>土地価格が安定している一方で、冬の寒さ対策による性能アップ費用が必要</strong>という特徴があるため、予算の考え方が他県と少し違います。</p>
      <p>この記事では、鳥取で家づくりをする際の<strong>予算の決め方・年収別の目安・よくある失敗</strong>をわかりやすく解説します。</p>

      <h2>結論：予算を決める3つのポイント</h2>
      <ol>
        <li>月々の返済額から逆算する（年収より重要）</li>
        <li>土地＋建物＋諸費用＋外構の総額で考える</li>
        <li>性能（断熱・気密）に必要な費用は削らない</li>
      </ol>
      <p>鳥取は土地価格が安定しているため、<strong>予算の差は"性能"と"住宅会社選び"で大きく変わります</strong>。</p>

      <h2>① 鳥取の家づくりに必要な総額の目安</h2>
      <p>鳥取で家を建てる場合、総額は次のようになります。</p>

      <h3>総額の内訳</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>目安金額</th><th>備考</th></tr>
        </thead>
        <tbody>
          <tr><td>土地代</td><td>300〜800万円</td><td>地域差あり</td></tr>
          <tr><td>建物代</td><td>1,800〜2,800万円</td><td>性能・仕様により変動</td></tr>
          <tr><td>外構費</td><td>100〜200万円</td><td>駐車場・フェンス等</td></tr>
          <tr><td>諸費用</td><td>150〜250万円</td><td>登記・保険・手数料等</td></tr>
        </tbody>
      </table>
      <p><strong>👉 2,300〜3,800万円が一般的なライン</strong></p>
      <p>鳥取市は土地が高め、倉吉市は比較的手頃、米子市はエリア差が大きい傾向があります。</p>

      <h2>② 年収別の予算目安（鳥取版）</h2>
      <p>鳥取は物価が比較的安いため、<strong>年収350〜450万円でも家を建てられる</strong>のが特徴です。</p>

      <h3>年収別の現実的な総予算</h3>
      <table>
        <thead>
          <tr><th>年収</th><th>月々の返済目安</th><th>借入可能額の目安</th><th>総予算の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>300〜350万円</td><td>6〜7万円</td><td>2,000〜2,500万円</td><td>2,300〜2,800万円</td></tr>
          <tr><td>350〜450万円</td><td>7〜9万円</td><td>2,500〜3,000万円</td><td>2,800〜3,500万円</td></tr>
          <tr><td>450〜550万円</td><td>9〜11万円</td><td>3,000〜3,800万円</td><td>3,500〜4,300万円</td></tr>
        </tbody>
      </table>

      <h3>ポイント</h3>
      <ul>
        <li>ボーナス払いは基本なし</li>
        <li>月々の支払いが"家賃＋1〜2万円"なら無理がない</li>
        <li>車のローンがある場合は要調整</li>
      </ul>

      <h2>③ 予算の決め方は「月々の返済額」から逆算する</h2>
      <p>家づくりの予算は、<strong>年収ではなく"月々いくら払えるか"で決めるのが正解</strong>です。</p>

      <h3>月々の返済額の決め方</h3>
      <p>次の4つを踏まえて、<em>無理なく払える金額＝家づくりの予算</em>と考えます。</p>
      <ul>
        <li>今の家賃</li>
        <li>車のローン</li>
        <li>教育費</li>
        <li>貯金したい金額</li>
      </ul>

      <h2>④ 鳥取でよくある予算の失敗</h2>

      <h3>失敗① 土地にお金をかけすぎる</h3>
      <p>土地に予算を使いすぎると、建物の性能を下げざるを得なくなります。鳥取は冬が寒いため、<strong>性能を削ると住み心地が大きく悪化</strong>します。</p>

      <h3>失敗② 諸費用を見落とす</h3>
      <p>諸費用は総額の10％前後。登記・火災保険・ローン手数料・引っ越し費用などが含まれます。</p>

      <h3>失敗③ 外構費を後回しにする</h3>
      <p>外構は100〜200万円かかることが多く、後回しにすると生活が不便になります。</p>

      <h3>失敗④ オプションで予算オーバー</h3>
      <p>キッチン・床材・照明など、気づけばオプションだらけに。最初から優先順位を決めておくことが大切です。</p>

      <h2>⑤ 鳥取の家づくりで"削ってはいけない費用"</h2>
      <p>鳥取は冬の寒さ・夏の蒸し暑さ・湿気が厳しいため、<strong>性能に関わる費用は削ると後悔します</strong>。</p>

      <h3>削ってはいけない項目</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>理由</th></tr>
        </thead>
        <tbody>
          <tr><td>断熱材のグレード</td><td>冬の光熱費と住み心地に直結</td></tr>
          <tr><td>樹脂窓</td><td>結露・熱損失を大幅に軽減</td></tr>
          <tr><td>気密施工</td><td>隙間をなくして暖房効率を高める</td></tr>
          <tr><td>換気システム</td><td>湿気対策・空気質の改善</td></tr>
        </tbody>
      </table>
      <p>ここを削ると、<strong>光熱費が高くなり、住み心地も悪くなる</strong>ため、結果的に損をします。</p>

      <h2>まとめ</h2>
      <p>鳥取の家づくり予算は、次の3つを押さえれば失敗しません。</p>
      <ol>
        <li>月々の返済額から逆算する</li>
        <li>土地＋建物＋諸費用＋外構の総額で考える</li>
        <li>性能に必要な費用は削らない</li>
      </ol>
      <p>鳥取は土地価格が安定しているため、<strong>予算の差は"性能"と"住宅会社選び"で決まる</strong>と言えます。</p>

      <h2>次に読む記事</h2>
      <p>次は、予算とセットで知っておきたい<br />👉 <a href="/articles/20">「鳥取で家づくりに失敗しないコツ｜後悔を防ぐ7つの実践ポイント」</a>をチェックしておくと、さらに安心して進められます。</p>
    `
  },
  {
    id: 20,
    step: '01',
    title: "鳥取で家づくりに失敗しないコツ｜後悔を防ぐ7つの実践ポイント",
    category: "知識をつける",
    date: "2026年3月27日",
    readTime: "約14分",
    image: "https://public.readdy.ai/ai/img_res/edited_21d65d4039af5697254c3f0bfba6d247_c61a0c50.jpg",
    excerpt: "「鳥取で家づくりに失敗したくない」という方へ。冬夏の体感確認・土地選び・間取り・収納・外構計画など、後悔を防ぐ7つの実践的なコツを解説します。",
    tags: ["鳥取", "失敗しない", "7つのコツ"],
    content: `
      <h2>はじめに</h2>
      <p>家づくりは一生に一度の大きな買い物。だからこそ、誰もが「絶対に失敗したくない」と思いますよね。</p>
      <p>ただ、実際に鳥取で家を建てた人の話を聞くと、寒さ対策が不十分だった、土地選びで後悔した、住宅会社選びを急ぎすぎた、間取りが暮らしに合わなかった、など、<strong>地域特有の気候や生活スタイルが原因の失敗</strong>がとても多いのが現実です。</p>
      <p>この記事では、鳥取で家づくりをする人が<strong>本当に押さえておくべき「失敗しないコツ」</strong>を7つに絞って解説します。</p>

      <h2>結論：後悔を防ぐ7つの実践ポイント</h2>
      <ol>
        <li>冬と夏の"体感"を必ず確認する</li>
        <li>土地は"湿気・日当たり・風の抜け"で選ぶ</li>
        <li>間取りは"生活のクセ"から逆算する</li>
        <li>収納は"使う場所の近くに"つくる</li>
        <li>外構計画を後回しにしない</li>
        <li>住宅会社は"比較"ではなく"見極め"で選ぶ</li>
        <li>担当者との相性が悪ければ遠慮なく変更する</li>
      </ol>
      <p>予算の話ではなく、<strong>「どうすれば後悔しないか」だけに絞った実践的な内容</strong>です。</p>

      <h2>① 冬と夏の"体感"を必ず確認する（数字より体感が大事）</h2>
      <p>鳥取は冬は底冷えする寒さ、夏は湿気でムシッと蒸し暑い、という気候が特徴です。この気候に合う家かどうかは、<strong>見学会で実際に体感しないと絶対にわかりません</strong>。</p>

      <h3>体感すべきポイント</h3>
      <ul>
        <li>冬の玄関・脱衣所が寒くないか</li>
        <li>夏にムワッとしないか</li>
        <li>空気の流れが良いか</li>
        <li>結露しにくいか</li>
        <li>日当たり・風通し</li>
      </ul>
      <p>数字（UA値・C値）も大事ですが、<strong>体感のほうが後悔を防ぐ力が強い</strong>です。</p>

      <h2>② 土地は"湿気・日当たり・風の抜け"で選ぶ</h2>
      <p>鳥取は湿気が多く、冬は日照時間が短い地域。そのため、土地選びで失敗すると住み心地が大きく変わります。</p>

      <h3>失敗しやすい土地</h3>
      <ul>
        <li>冬に日が入らず寒い</li>
        <li>湿気がこもりやすい</li>
        <li>風が抜けず夏が蒸し暑い</li>
        <li>地盤が弱く改良費が高い</li>
      </ul>

      <h3>コツ</h3>
      <ul>
        <li>冬の日当たりを必ず確認</li>
        <li>周辺の家の影にならないかチェック</li>
        <li>風の通り道を現地で感じる</li>
        <li>住宅会社と一緒に土地を見る</li>
      </ul>
      <p>土地選びは「立地」より<strong>"日当たり × 湿気 × 風通し"のほうが後悔を防げます</strong>。</p>

      <h2>③ 間取りは"生活のクセ"から逆算する</h2>
      <p>間取りの失敗は本当に多いです。特に鳥取は車移動が中心なので、動線の工夫が必要です。</p>

      <h3>よくある後悔</h3>
      <ul>
        <li>洗濯動線が長くて冬に乾かない</li>
        <li>玄関が狭くて靴が散らかる</li>
        <li>車から荷物を運びにくい</li>
        <li>キッチンが孤立して家事がしにくい</li>
      </ul>

      <h3>コツ</h3>
      <ul>
        <li>朝の動き方を紙に書き出す</li>
        <li>洗濯→干す→しまうの動線を最短に</li>
        <li>車→玄関→キッチンの動線を短く</li>
        <li>家族の生活リズムを間取りに反映</li>
      </ul>
      <p>間取りは<strong>"家族のクセ"を反映させると失敗しません</strong>。</p>

      <h2>④ 収納は"使う場所の近くに"つくる</h2>
      <p>収納量よりも、<strong>収納の"位置"が暮らしやすさを決めます</strong>。</p>

      <h3>失敗例</h3>
      <ul>
        <li>収納は多いのに使いにくい</li>
        <li>玄関に収納が足りない</li>
        <li>洗面所にタオル置き場がない</li>
        <li>子どもの荷物がリビングに散らかる</li>
      </ul>

      <h3>コツ</h3>
      <ul>
        <li>使う場所のすぐ近くに収納をつくる</li>
        <li>玄関収納は広めに</li>
        <li>洗面所にタオル・洗剤の収納</li>
        <li>リビングに"子どもゾーン"をつくる</li>
      </ul>
      <p>収納は<strong>"量より配置"が失敗を防ぎます</strong>。</p>

      <h2>⑤ 外構計画を後回しにしない</h2>
      <p>外構は後回しにすると、生活が不便になり、追加費用も高くなります。</p>

      <h3>よくある後悔</h3>
      <ul>
        <li>駐車場が狭くて停めにくい</li>
        <li>玄関までの動線が雨で濡れる</li>
        <li>砂利のままで泥が家に入る</li>
        <li>予算が足りず後で高くつく</li>
      </ul>

      <h3>コツ</h3>
      <ul>
        <li>建物と同時に外構も計画する</li>
        <li>車の出し入れをシミュレーション</li>
        <li>雨の日の動線を考える</li>
        <li>玄関前はコンクリート推奨</li>
      </ul>
      <p>外構は<strong>"暮らしの快適さ"を左右する重要ポイント</strong>です。</p>

      <h2>⑥ 住宅会社は"比較"ではなく"見極め"で選ぶ</h2>
      <p>比較するだけでは不十分で、<strong>「自分の暮らしに合う会社か」を見極めることが大切</strong>です。</p>

      <h3>見極めポイント</h3>
      <ul>
        <li>性能（冬暖かい・夏涼しい）</li>
        <li>標準仕様のレベル</li>
        <li>施工の丁寧さ</li>
        <li>担当者の誠実さ</li>
        <li>価格の透明性</li>
      </ul>
      <p>"安いから"ではなく、<strong>"信頼できるか"で選ぶと失敗しません</strong>。</p>

      <h2>⑦ 担当者との相性が悪ければ遠慮なく変更する</h2>
      <p>家づくりは半年〜1年以上の長い付き合い。担当者との相性は、家づくりの満足度を大きく左右します。</p>

      <h3>コツ</h3>
      <ul>
        <li>違和感を感じたらすぐ相談</li>
        <li>店長や上司に担当変更を依頼</li>
        <li>遠慮する必要は一切ない</li>
        <li>大きな買い物だからこそ"気持ちよく契約"が大切</li>
      </ul>
      <p>担当者は<strong>"家づくりのパートナー"</strong>。相性が合う人と進めることが、最大の失敗防止策です。</p>

      <h2>まとめ</h2>
      <p>鳥取で家づくりに失敗しないためのコツは次の7つ。</p>
      <ol>
        <li>冬と夏の"体感"を確認</li>
        <li>土地は湿気・日当たり・風で選ぶ</li>
        <li>間取りは生活のクセから逆算</li>
        <li>収納は使う場所の近くに</li>
        <li>外構を後回しにしない</li>
        <li>住宅会社は"見極め"で選ぶ</li>
        <li>担当者との相性を大切にする</li>
      </ol>
      <p>鳥取は気候の影響が大きい地域だからこそ、<strong>"暮らしやすさ"を基準に家づくりを進めることが、後悔しない最大のコツ</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>次は、予算と一緒に考えるべき住宅ローンの話で、<br />👉 <a href="/articles/21">「鳥取で住宅ローンはいくら借りられる？年収別の借入目安を公開」</a>をチェックしておくと、より納得して進められます。</p>
    `
  },
  {
    id: 21,
    step: '02',
    title: "鳥取で住宅ローンはいくら借りられる？年収別の借入目安を公開",
    category: "予算を決める",
    date: "2026年3月27日",
    readTime: "約13分",
    image: "https://public.readdy.ai/ai/img_res/edited_21d65d4039af5697254c3f0bfba6d247_daabaa0b.jpg",
    excerpt: "「鳥取で住宅ローンはいくら借りられるの？」年収別の借入可能額・月々の返済目安・無理のない借入額の決め方を、鳥取の地域事情を踏まえてわかりやすく解説します。",
    tags: ["鳥取", "住宅ローン", "借入目安"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を建てたいけれど、住宅ローンはいくら借りられるんだろう…」<br />そんな不安を感じていませんか？</p>
      <p>私自身も家づくりを始めたとき、年収でどれくらい借りられるのか、月々の返済はいくらになるのか、鳥取の相場はどれくらい？など、ローンのイメージが全く掴めず不安でした。</p>
      <p>特に鳥取は、<strong>土地価格が比較的安い一方で、冬の寒さ対策による性能アップ費用が必要</strong>という特徴があるため、借入額の考え方が他県と少し違います。</p>
      <p>この記事では、鳥取で住宅ローンを組む際の<strong>年収別の借入目安・月々の返済額・無理のない借入額の決め方</strong>をわかりやすく解説します。</p>

      <h2>結論：鳥取の住宅ローン3つのポイント</h2>
      <ul>
        <li>年収の5〜6倍が借入額の目安</li>
        <li>月々の返済は"家賃＋1〜2万円"が無理のないライン</li>
        <li>年収350〜450万円でも家は建てられる（鳥取の特徴）</li>
      </ul>
      <p>借りられる額よりも、<strong>無理なく返せる額を基準にすることが最重要</strong>です。</p>

      <h2>① 鳥取で住宅ローンはいくら借りられる？年収別の借入目安</h2>
      <p>鳥取の家計感覚・物価・土地価格を踏まえた、<strong>現実的な借入額の目安</strong>をまとめました。</p>

      <h3>年収別の借入可能額（鳥取版）</h3>
      <table>
        <thead>
          <tr><th>年収</th><th>借入目安</th><th>月々の返済目安</th><th>備考</th></tr>
        </thead>
        <tbody>
          <tr><td>300〜350万円</td><td>1,500〜2,000万円</td><td>6〜7万円</td><td>土地価格が安いエリアで検討</td></tr>
          <tr><td>350〜450万円</td><td>2,000〜2,700万円</td><td>7〜9万円</td><td>鳥取で現実的なメインライン</td></tr>
          <tr><td>450〜550万円</td><td>2,700〜3,300万円</td><td>9〜11万円</td><td>余裕のある返済計画が可能</td></tr>
        </tbody>
      </table>

      <h3>鳥取の特徴</h3>
      <ul>
        <li>家賃が安いため、月々7〜9万円の返済が現実的</li>
        <li>土地価格が安定しているため、借入額は建物の性能で変わる</li>
        <li>車2台持ちが多いため、家計のバランスが重要</li>
      </ul>

      <h2>② 鳥取で家を建てる総額の目安（借入額の参考に）</h2>
      <p>鳥取で家を建てる場合、総額は次のようになります。</p>

      <h3>総額の内訳</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>目安金額</th></tr>
        </thead>
        <tbody>
          <tr><td>土地</td><td>300〜800万円</td></tr>
          <tr><td>建物</td><td>1,800〜2,800万円</td></tr>
          <tr><td>外構</td><td>100〜200万円</td></tr>
          <tr><td>諸費用</td><td>150〜250万円</td></tr>
        </tbody>
      </table>
      <p><strong>👉 総額2,300〜3,800万円が一般的なライン</strong><br />つまり、<strong>借入額は2,000〜3,500万円が現実的</strong>ということになります。</p>

      <h2>③ 借りられる額より"返せる額"で考えるのが正解</h2>
      <p>住宅ローンで最も大切なのは、<strong>借りられる額ではなく、無理なく返せる額</strong>です。</p>

      <h3>月々の返済額の決め方</h3>
      <ul>
        <li>今の家賃</li>
        <li>車のローン</li>
        <li>子どもの教育費</li>
        <li>貯金したい金額</li>
      </ul>
      <p>これらを踏まえて、<strong>家賃＋1〜2万円以内</strong>に収まる返済額が理想です。</p>
      <p><em>例：家賃6.5万円の家庭 → 月々7.5〜8.5万円が無理のないライン → 借入額は2,200〜2,600万円が目安</em></p>

      <h2>④ 鳥取で住宅ローンを組むときの注意点</h2>

      <h3>注意点① 車の維持費を忘れない</h3>
      <p>鳥取は車移動が中心のため、車検・ガソリン・保険・タイヤ交換などの費用が家計に影響します。</p>

      <h3>注意点② 性能を削ると光熱費が高くなる</h3>
      <p>借入額を抑えようとして性能を下げると、<strong>冬の暖房費・夏の冷房費が高くなり逆効果</strong>です。</p>

      <h3>注意点③ ボーナス払いは基本なし</h3>
      <p>ボーナスは変動するため、<strong>ボーナス払いなしの返済計画</strong>が安全です。</p>

      <h2>⑤ 鳥取で住宅ローンを無理なく返すコツ</h2>
      <ol>
        <li>月々の返済額を最優先で決める</li>
        <li>車のローンはできれば完済しておく</li>
        <li>性能に必要な費用は削らない</li>
        <li>外構費を後回しにしない（追加費用が高くなる）</li>
        <li>住宅会社に"総額"で見積もりを出してもらう</li>
      </ol>
      <p>住宅ローンは<strong>"家計のバランス"がすべて</strong>です。</p>

      <h2>まとめ</h2>
      <p>鳥取で住宅ローンはいくら借りられるのか？結論は次のとおりです。</p>
      <ul>
        <li>年収の5〜6倍が借入額の目安</li>
        <li>月々の返済は家賃＋1〜2万円が理想</li>
        <li>借入額は2,000〜3,500万円が現実的</li>
        <li>年収350〜450万円でも家は建てられる（鳥取の特徴）</li>
      </ul>
      <p>鳥取は土地価格が安定しているため、<strong>借入額の差は"建物の性能"と"住宅会社選び"で決まる</strong>と言えます。</p>

      <h2>次に読む記事</h2>
      <p>次は、住宅ローンをもう少し掘り下げて、<br />👉 <a href="/articles/22">「鳥取の住宅ローン月々の支払い目安｜年収別シミュレーション付き」</a>をチェックしておくと、さらに安心して進められます。</p>
    `
  },
  {
    id: 22,
    step: '02',
    title: "鳥取の住宅ローン月々の支払い目安｜年収別シミュレーション付き",
    category: "予算を決める",
    date: "2026年3月27日",
    readTime: "約13分",
    image: "https://public.readdy.ai/ai/img_res/edited_21d65d4039af5697254c3f0bfba6d247_c678e134.jpg",
    excerpt: "「月々いくら払えば家が建てられるの？」鳥取の家賃・車2台持ち・子育て事情を踏まえた年収別の月々返済シミュレーションと、無理なく返すための5つのコツを解説します。",
    tags: ["鳥取", "月々の返済", "シミュレーション"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を建てると、住宅ローンの月々の支払いはいくらになるんだろう…」<br />そんな不安を感じていませんか？</p>
      <p>私自身も家づくりを始めたとき、月々いくらなら無理なく返せるのか、年収でどれくらいの返済額が適正なのか、鳥取の相場はどれくらい？など、ローンの"リアルな支払い感覚"がわからず不安でした。</p>
      <p>特に鳥取は、<strong>家賃が比較的安く、車2台持ちが多い地域</strong>のため、住宅ローンの月々の支払いは"家計のバランス"がとても重要です。</p>
      <p>この記事では、鳥取で住宅ローンを組む際の<strong>月々の支払い目安・年収別シミュレーション・無理なく返すコツ</strong>をわかりやすく解説します。</p>

      <h2>結論：鳥取の月々返済3つのポイント</h2>
      <ul>
        <li>家賃＋1〜2万円以内が無理のないライン</li>
        <li>月々7〜10万円の返済が最も多い</li>
        <li>年収350〜450万円でも月々7〜8万円で家は建てられる</li>
      </ul>
      <p>借りられる額より、<strong>"月々いくら払えるか"を基準にすることが最重要</strong>です。</p>

      <h2>① 鳥取の住宅ローン「月々の支払い」相場はどれくらい？</h2>
      <p>鳥取県内で家を建てた人の多くは、<strong>月々7〜10万円の返済</strong>に収まっています。</p>

      <h3>鳥取の返済相場</h3>
      <table>
        <thead>
          <tr><th>月々の返済額</th><th>どんな家庭？</th></tr>
        </thead>
        <tbody>
          <tr><td>6〜7万円</td><td>コンパクト住宅・土地が安いエリア</td></tr>
          <tr><td>7〜9万円</td><td>最も多いゾーン・標準的な家</td></tr>
          <tr><td>9〜11万円</td><td>性能を高めた家・土地が高いエリア</td></tr>
        </tbody>
      </table>

      <h3>なぜ7〜10万円が多いのか？</h3>
      <ul>
        <li>家賃が5〜7万円の家庭が多い</li>
        <li>車2台持ちの維持費がある</li>
        <li>子育て世帯が多く教育費も必要</li>
        <li>無理なく返せるラインがこの範囲</li>
      </ul>

      <h2>② 年収別｜鳥取の月々返済シミュレーション</h2>
      <p>鳥取の家計感覚に合わせた、<strong>年収別の"無理のない返済額"</strong>をまとめました。</p>

      <h3>年収別 月々返済の目安（35年ローン）</h3>
      <table>
        <thead>
          <tr><th>年収</th><th>無理のない月々返済</th><th>借入目安</th><th>備考</th></tr>
        </thead>
        <tbody>
          <tr><td>300〜350万円</td><td>6〜7万円</td><td>1,800〜2,100万円</td><td>コンパクト設計で十分可能</td></tr>
          <tr><td>350〜450万円</td><td>7〜9万円</td><td>2,100〜2,700万円</td><td>鳥取で最も多い標準ライン</td></tr>
          <tr><td>450〜550万円</td><td>9〜11万円</td><td>2,700〜3,300万円</td><td>性能も予算も余裕のある計画</td></tr>
        </tbody>
      </table>

      <h3>ポイント</h3>
      <ul>
        <li>ボーナス払いなしで計算</li>
        <li>車のローンがある場合は月々1〜2万円引いて考える</li>
        <li>子どもの年齢によっても調整が必要</li>
      </ul>

      <h2>③ 月々の返済額は"家賃＋1〜2万円"が最適</h2>
      <p>住宅ローンの月々の支払いは、<strong>今の家賃を基準に考えるのが最も失敗しない方法</strong>です。</p>

      <h3>例：家賃6.5万円の家庭</h3>
      <ul>
        <li>月々7.5〜8.5万円が無理のないライン</li>
        <li>借入額は2,200〜2,600万円が目安</li>
      </ul>

      <h3>なぜ家賃基準が良いのか？</h3>
      <ul>
        <li>生活リズムが変わらない</li>
        <li>家計の負担が急に増えない</li>
        <li>教育費・車の維持費と両立しやすい</li>
      </ul>

      <h2>④ 鳥取で月々の支払いを無理なくするコツ</h2>

      <h3>コツ① 車のローンをできれば完済しておく</h3>
      <p>車2台持ちが多い鳥取では、<strong>車のローンが家計を圧迫しやすい</strong>です。住宅ローンを組む前に完済しておくのが理想的です。</p>

      <h3>コツ② 性能を削らない</h3>
      <p>性能を下げると、<strong>冬の暖房費・夏の冷房費が高くなり逆効果</strong>です。月々の支払いを抑えようとして性能を妥協すると、光熱費で損をします。</p>

      <h3>コツ③ 外構費を最初から予算に入れる</h3>
      <p>後回しにすると追加費用が高くなります。最初から100〜200万円を外構費として計上しておきましょう。</p>

      <h3>コツ④ 住宅会社に"総額"で見積もりを出してもらう</h3>
      <p>建物だけの見積もりだと、後から追加費用で月々が増えることがあります。<strong>諸費用・外構・地盤改良費を含めた総額</strong>で確認しましょう。</p>

      <h2>⑤ 鳥取で月々の支払いを抑える方法</h2>
      <ol>
        <li>土地を広げすぎない（300〜700万円が現実的）</li>
        <li>間取りをコンパクトにする（30〜33坪が人気）</li>
        <li>標準仕様が充実した住宅会社を選ぶ</li>
        <li>補助金（こどもエコすまい等）を活用する</li>
        <li>太陽光で光熱費を抑える</li>
      </ol>
      <p>月々の支払いは、<strong>"家の性能 × 間取り × 土地"で大きく変わります</strong>。</p>

      <h2>まとめ</h2>
      <p>鳥取の住宅ローン月々の支払い目安は次のとおりです。</p>
      <ul>
        <li>月々7〜10万円が最も多い</li>
        <li>家賃＋1〜2万円以内が無理のないライン</li>
        <li>年収350〜450万円でも月々7〜8万円で家は建てられる</li>
        <li>借入額は2,000〜3,500万円が現実的</li>
      </ul>
      <p>鳥取は車2台持ちが多く、家計のバランスが重要な地域だからこそ、<strong>"月々いくら払えるか"を基準に住宅ローンを考えることが、後悔しない家づくりのカギ</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>次は、家づくりの総額を具体的に知りたい人向けに<br />👉 <a href="/articles/23">「鳥取の家づくり総額はいくら？建物・土地・諸費用のリアルな相場」</a>を読むと、より正確に予算がイメージできます。</p>
    `
  },
  {
    id: 23,
    step: '02',
    title: "鳥取の家づくり総額はいくら？建物・土地・諸費用のリアルな相場",
    category: "予算を決める",
    date: "2026年3月27日",
    image: "https://public.readdy.ai/ai/img_res/edited_21d65d4039af5697254c3f0bfba6d247_08b0d49a.jpg",
    readTime: "約13分",
    excerpt: "「鳥取で家を建てると総額いくら必要？」建物・土地・外構・諸費用を含めたリアルな相場を地域別データで解説。性能コストや土地価格の差まで、総額を正確にイメージできます。",
    tags: ["鳥取", "総額", "相場"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を建てると、総額はいくら必要なんだろう…？」<br />家づくりを考え始めた人が最初に気になるのが、この"総額"ですよね。</p>
      <p>私自身も家づくりを始めたとき、建物代はいくら？土地代はどれくらい？諸費用って何が含まれるの？など、総額のイメージが全く掴めず不安でした。</p>
      <p>特に鳥取は、<strong>土地価格が比較的安い一方で、冬の寒さ対策による性能アップ費用が必要</strong>という特徴があるため、総額の考え方が他県と少し違います。</p>
      <p>この記事では、鳥取で家づくりをする際の<strong>建物・土地・外構・諸費用をすべて含めた"リアルな総額"</strong>をわかりやすく解説します。</p>

      <h2>結論：鳥取の家づくり総額の目安</h2>
      <table>
        <thead>
          <tr><th>項目</th><th>目安金額</th></tr>
        </thead>
        <tbody>
          <tr><td>建物代</td><td>1,800〜2,800万円</td></tr>
          <tr><td>土地代</td><td>300〜800万円</td></tr>
          <tr><td>外構費</td><td>100〜200万円</td></tr>
          <tr><td>諸費用</td><td>150〜250万円</td></tr>
          <tr><td><strong>合計（総額）</strong></td><td><strong>2,300〜3,800万円</strong></td></tr>
        </tbody>
      </table>
      <p>鳥取は土地が比較的安いため、<strong>総額の差は"建物の性能"と"住宅会社選び"で大きく変わります</strong>。</p>

      <h2>① 鳥取の家づくり総額の内訳（まずは全体像）</h2>
      <p>鳥取で家を建てる場合、総額は次の4つで構成されます。</p>

      <h3>総額の内訳</h3>
      <ul>
        <li>建物代：1,800〜2,800万円</li>
        <li>土地代：300〜800万円</li>
        <li>外構費：100〜200万円</li>
        <li>諸費用：150〜250万円</li>
      </ul>
      <p><strong>👉 合計：2,300〜3,800万円</strong></p>
      <p>鳥取市は土地が高め、倉吉市は比較的手頃、米子市はエリア差が大きい傾向があります。</p>

      <h2>② 建物代の相場（1,800〜2,800万円）</h2>
      <p>鳥取の建物価格は、<strong>性能（断熱・気密）と住宅会社の仕様</strong>によって大きく変わります。</p>

      <h3>建物価格の目安</h3>
      <table>
        <thead>
          <tr><th>タイプ</th><th>価格帯</th><th>特徴</th></tr>
        </thead>
        <tbody>
          <tr><td>ローコスト住宅</td><td>1,600〜2,000万円</td><td>コスト重視・性能は標準</td></tr>
          <tr><td>中間価格帯</td><td>2,000〜2,500万円</td><td>バランスが良く最も選ばれる</td></tr>
          <tr><td>高性能住宅</td><td>2,500〜3,000万円</td><td>断熱・気密・設備のグレードが高い</td></tr>
        </tbody>
      </table>

      <h3>鳥取で建物価格が上がりやすい理由</h3>
      <ul>
        <li>冬の寒さ対策（断熱・窓の性能）</li>
        <li>夏の湿気対策（換気・気密）</li>
        <li>雪・雨に強い外壁・屋根材</li>
      </ul>
      <p>性能を下げると光熱費が高くなるため、<strong>建物価格は"安さ"より"性能"で選ぶのが正解</strong>です。</p>

      <h2>③ 土地代の相場（300〜800万円）</h2>
      <p>鳥取は全国的に見ても土地が手頃ですが、<strong>地域差が大きいのが特徴</strong>です。</p>

      <h3>地域別の土地相場</h3>
      <table>
        <thead>
          <tr><th>エリア</th><th>土地相場の目安</th><th>特徴</th></tr>
        </thead>
        <tbody>
          <tr><td>鳥取市</td><td>500〜900万円</td><td>人気エリアは競争が激しく高め</td></tr>
          <tr><td>米子市</td><td>400〜800万円</td><td>エリアによって価格差が大きい</td></tr>
          <tr><td>倉吉市</td><td>300〜600万円</td><td>比較的手頃で探しやすい</td></tr>
        </tbody>
      </table>

      <h3>土地価格が変わるポイント</h3>
      <ul>
        <li>日当たり</li>
        <li>地盤の強さ</li>
        <li>道路幅</li>
        <li>周辺環境</li>
        <li>冬の日照時間</li>
      </ul>
      <p>鳥取は冬の日照時間が短いため、<strong>日当たりの良い土地は特に人気</strong>です。</p>

      <h2>④ 外構費の相場（100〜200万円）</h2>
      <p>外構は後回しにすると、<strong>生活が不便になり、追加費用も高くなる</strong>ため注意が必要です。</p>

      <h3>外構費の内訳（目安）</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>費用の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>駐車場コンクリート</td><td>30〜60万円</td></tr>
          <tr><td>玄関アプローチ</td><td>20〜40万円</td></tr>
          <tr><td>フェンス・塀</td><td>20〜50万円</td></tr>
          <tr><td>土間・砂利</td><td>10〜30万円</td></tr>
          <tr><td>植栽</td><td>10〜30万円</td></tr>
        </tbody>
      </table>
      <p>外構は<strong>"暮らしやすさ"に直結するため、建物と同時に計画するのが失敗しないコツ</strong>です。</p>

      <h2>⑤ 諸費用の相場（150〜250万円）</h2>
      <p>諸費用は見落としがちな項目です。<strong>総額の10％前後が目安</strong>になります。</p>

      <h3>諸費用の内訳</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>費用の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>登記費用</td><td>30〜60万円</td></tr>
          <tr><td>火災保険</td><td>20〜40万円（10年分）</td></tr>
          <tr><td>ローン手数料</td><td>30〜50万円</td></tr>
          <tr><td>地盤調査・改良</td><td>20〜100万円</td></tr>
          <tr><td>引っ越し費用</td><td>10〜30万円</td></tr>
          <tr><td>水道加入金</td><td>15〜30万円</td></tr>
        </tbody>
      </table>

      <h2>⑥ 鳥取で家づくり総額が変わるポイント</h2>
      <ol>
        <li>建物の性能（断熱・気密）→ 性能が高いほど建物代UP・光熱費DOWN</li>
        <li>土地の条件（日当たり・地盤）→ 地盤改良が必要な場合は+30〜100万円</li>
        <li>外構の内容 → 後回しにすると費用が増えやすい</li>
        <li>住宅会社の標準仕様 → 標準仕様が高い会社はオプション費が少ない</li>
        <li>補助金の活用 → こどもエコすまい支援等で100万円以上変わる場合も</li>
      </ol>
      <p>特に鳥取は気候の影響が大きいため、<strong>性能に必要な費用は削らないことが重要</strong>です。</p>

      <h2>まとめ</h2>
      <p>鳥取で家づくりに必要な総額は次のとおりです。</p>
      <ul>
        <li>総額：2,300〜3,800万円</li>
        <li>建物：1,800〜2,800万円</li>
        <li>土地：300〜800万円</li>
        <li>外構：100〜200万円</li>
        <li>諸費用：150〜250万円</li>
      </ul>
      <p>鳥取は土地価格が安定しているため、<strong>総額の差は"建物の性能"と"住宅会社選び"で決まる</strong>と言えます。</p>

      <h2>次に読む記事</h2>
      <p>次は、建物価格をさらに詳しく知りたい人向けに<br />👉 <a href="/articles/24">「鳥取の新築費用相場2026｜坪単価・総額のリアルデータを公開」</a>を読むと、より具体的な金額がイメージできます。</p>
    `
  },
  {
    id: 24,
    step: '02',
    title: "鳥取の新築費用相場2026｜坪単価・総額のリアルデータを公開",
    category: "予算を決める",
    date: "2026年3月27日",
    readTime: "約13分",
    image: "https://public.readdy.ai/ai/img_res/edited_5ed41a00461d7c86dafc866d0c790422_76bc39af.jpg",
    excerpt: "「鳥取で新築を建てると総額いくら？」2026年最新の坪単価55〜80万円、建物・土地・外構・諸費用の内訳と地域別データを公開。性能別の価格差まで詳しく解説します。",
    tags: ["鳥取", "坪単価", "費用相場"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で新築を建てると、総額はいくらかかるんだろう…？」<br />家づくりを考え始めた人が最初に気になるのが、この"費用相場"ですよね。</p>
      <p>私自身も家づくりを始めたとき、坪単価ってどれくらい？建物と土地のバランスは？鳥取の相場は全国と比べて高い？安い？など、金額のイメージが全く掴めず不安でした。</p>
      <p>特に鳥取は、<strong>土地価格が比較的安い一方で、冬の寒さ対策による性能アップ費用が必要</strong>という特徴があるため、費用相場の考え方が他県と少し違います。</p>
      <p>この記事では、2026年時点の<strong>鳥取の新築費用相場（坪単価・建物価格・土地価格・総額）</strong>をわかりやすく解説します。</p>

      <h2>結論：鳥取の新築費用相場（2026年）</h2>
      <table>
        <thead>
          <tr><th>項目</th><th>相場</th></tr>
        </thead>
        <tbody>
          <tr><td>坪単価</td><td>55〜80万円</td></tr>
          <tr><td>建物価格</td><td>1,900〜2,900万円（30〜36坪）</td></tr>
          <tr><td>土地価格</td><td>300〜800万円（地域差あり）</td></tr>
          <tr><td>外構費</td><td>100〜200万円</td></tr>
          <tr><td><strong>総額</strong></td><td><strong>2,300〜3,900万円</strong></td></tr>
        </tbody>
      </table>
      <p>鳥取は土地が比較的安いため、<strong>総額の差は"建物の性能"と"住宅会社選び"で大きく変わります</strong>。</p>

      <h2>① 鳥取の坪単価相場（2026年版）</h2>
      <p>2026年時点の鳥取県内の坪単価は、<strong>55〜80万円</strong>が一般的です。</p>

      <h3>坪単価の目安</h3>
      <table>
        <thead>
          <tr><th>タイプ</th><th>坪単価の目安</th><th>特徴</th></tr>
        </thead>
        <tbody>
          <tr><td>ローコスト住宅</td><td>50〜60万円</td><td>コスト重視・性能は標準</td></tr>
          <tr><td>中間価格帯</td><td>60〜70万円</td><td>性能とコストのバランスが良い</td></tr>
          <tr><td>高性能住宅</td><td>70〜85万円</td><td>断熱・気密・設備のグレードが高い</td></tr>
        </tbody>
      </table>

      <h3>坪単価が上がりやすい理由</h3>
      <ul>
        <li>断熱性能の強化（冬の寒さ対策）</li>
        <li>樹脂窓・高性能ガラスの標準化</li>
        <li>物価・資材高騰の影響</li>
        <li>大工不足による人件費上昇</li>
      </ul>
      <p>坪単価は<strong>"建物の性能"と"標準仕様"で大きく変わります</strong>。</p>

      <h2>② 建物価格の相場（1,900〜2,900万円）</h2>
      <p>鳥取で建てる家の平均的な広さは<strong>30〜36坪</strong>。坪単価を掛け合わせると、建物価格は次のようになります。</p>

      <h3>建物価格の目安</h3>
      <table>
        <thead>
          <tr><th>広さ</th><th>坪単価</th><th>建物価格</th></tr>
        </thead>
        <tbody>
          <tr><td>30坪</td><td>60万円</td><td>約1,800万円</td></tr>
          <tr><td>32坪</td><td>65万円</td><td>約2,080万円</td></tr>
          <tr><td>35坪</td><td>70万円</td><td>約2,450万円</td></tr>
          <tr><td>36坪</td><td>80万円</td><td>約2,880万円</td></tr>
        </tbody>
      </table>

      <h3>建物価格が変わるポイント</h3>
      <ul>
        <li>断熱性能（UA値・C値）</li>
        <li>標準仕様のレベル</li>
        <li>間取りの複雑さ</li>
        <li>外壁・屋根材のグレード</li>
      </ul>
      <p>鳥取は冬の寒さが厳しいため、<strong>性能を上げると建物価格も上がる傾向</strong>があります。</p>

      <h2>③ 土地価格の相場（300〜800万円）</h2>
      <p>鳥取は全国的に見ても土地が手頃ですが、<strong>地域差が大きいのが特徴</strong>です。</p>

      <h3>地域別の土地相場</h3>
      <table>
        <thead>
          <tr><th>エリア</th><th>土地相場の目安</th><th>特徴</th></tr>
        </thead>
        <tbody>
          <tr><td>鳥取市</td><td>500〜900万円</td><td>人気エリアは競争が激しく高め</td></tr>
          <tr><td>米子市</td><td>400〜800万円</td><td>エリアによって価格差が大きい</td></tr>
          <tr><td>倉吉市</td><td>300〜600万円</td><td>比較的手頃で探しやすい</td></tr>
        </tbody>
      </table>
      <p>鳥取は冬の日照時間が短いため、<strong>日当たりの良い土地は特に人気</strong>です。</p>

      <h2>④ 外構費の相場（100〜200万円）</h2>
      <p>外構は後回しにすると、<strong>生活が不便になり、追加費用も高くなる</strong>ため注意が必要です。</p>

      <h3>外構費の内訳</h3>
      <ul>
        <li>駐車場コンクリート：30〜60万円</li>
        <li>玄関アプローチ：20〜40万円</li>
        <li>フェンス・塀：20〜50万円</li>
        <li>砂利・土間：10〜30万円</li>
        <li>植栽：10〜30万円</li>
      </ul>
      <p>外構は<strong>"暮らしやすさ"に直結するため、建物と同時に計画するのが失敗しないコツ</strong>です。</p>

      <h2>⑤ 諸費用の相場（150〜250万円）</h2>
      <p>諸費用は見落としがちな項目です。<strong>総額の10％前後が目安</strong>です。</p>

      <h3>諸費用の内訳</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>費用の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>登記費用</td><td>30〜60万円</td></tr>
          <tr><td>火災保険</td><td>20〜40万円（10年分）</td></tr>
          <tr><td>ローン手数料</td><td>30〜50万円</td></tr>
          <tr><td>地盤調査・改良</td><td>20〜100万円</td></tr>
          <tr><td>引っ越し費用</td><td>10〜30万円</td></tr>
          <tr><td>水道加入金</td><td>15〜30万円</td></tr>
        </tbody>
      </table>

      <h2>⑥ 鳥取の新築費用が変わるポイント</h2>
      <ol>
        <li>建物の性能（断熱・気密）→ 性能が高いほど建物代UP・光熱費DOWN</li>
        <li>土地の条件（日当たり・地盤）→ 地盤改良が必要な場合は+30〜100万円</li>
        <li>外構の内容 → 後回しにすると費用が増えやすい</li>
        <li>住宅会社の標準仕様 → 標準仕様が高い会社はオプション費が少ない</li>
        <li>補助金の活用 → こどもエコすまい支援等で100万円以上変わる場合も</li>
      </ol>
      <p>特に鳥取は気候の影響が大きいため、<strong>性能に必要な費用は削らないことが重要</strong>です。</p>

      <h2>まとめ</h2>
      <p>鳥取の新築費用相場（2026年）は次のとおりです。</p>
      <ul>
        <li>坪単価：55〜80万円</li>
        <li>建物価格：1,900〜2,900万円</li>
        <li>土地価格：300〜800万円</li>
        <li>外構費：100〜200万円</li>
        <li>総額：2,300〜3,900万円</li>
      </ul>
      <p>鳥取は土地価格が安定しているため、<strong>総額の差は"建物の性能"と"住宅会社選び"で決まる</strong>と言えます。</p>

      <h2>次に読む記事</h2>
      <p>次は、土地価格を詳しく知りたい人向けに<br />👉 <a href="/articles/25">「鳥取の土地価格相場2026｜鳥取市・米子市・倉吉市の最新データ」</a>を読むと、より正確に総額がイメージできます。</p>
    `
  },
  {
    id: 25,
    step: '02',
    title: "鳥取の土地価格相場2026｜鳥取市・米子市・倉吉市の最新データ",
    category: "予算を決める",
    date: "2026年3月27日",
    readTime: "約12分",
    image: "https://public.readdy.ai/ai/img_res/edited_5d4bf96b8066994d26aee45d54ee51c8_d08d4e79.jpg",
    excerpt: "「鳥取の土地相場は？」2026年最新データで鳥取市・米子市・倉吉市のエリア別坪単価と総額を公開。日当たり・地盤・道路幅が価格に与える影響まで詳しく解説します。",
    tags: ["鳥取", "土地価格", "相場"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取の土地価格ってどれくらいなんだろう？」<br />「鳥取市と米子市、どっちが高いの？」<br />「家づくりの総額を考えるために土地相場を知りたい」</p>
      <p>家づくりを考え始めると、まず気になるのが<strong>土地価格の相場</strong>ですよね。</p>
      <p>特に鳥取は、<strong>市ごとの価格差が大きく、日当たり・地盤・利便性で大きく変動する地域</strong>。そのため、相場を知らずに土地探しをすると、後悔しやすいのが特徴です。</p>
      <p>この記事では、2026年時点の<strong>鳥取市・米子市・倉吉市の土地価格相場を最新データでわかりやすく解説</strong>します。</p>

      <h2>結論：鳥取の土地価格相場（2026年）</h2>
      <table>
        <thead>
          <tr><th>エリア</th><th>坪単価</th><th>60〜70坪の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>鳥取市</td><td>10〜20万円（平均15万円）</td><td>600〜1,200万円</td></tr>
          <tr><td>米子市</td><td>8〜18万円（平均13万円）</td><td>500〜1,000万円</td></tr>
          <tr><td>倉吉市</td><td>6〜12万円（平均9万円）</td><td>350〜700万円</td></tr>
        </tbody>
      </table>
      <p>鳥取は全国的に見ても土地が手頃ですが、<strong>エリア差が大きいのが特徴</strong>です。</p>

      <h2>① 鳥取市の土地価格相場（10〜20万円／坪）</h2>
      <p>鳥取県内で最も土地価格が高いのが<strong>鳥取市</strong>。</p>

      <h3>鳥取市の坪単価</h3>
      <table>
        <thead>
          <tr><th>エリア</th><th>坪単価の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>中心部（吉成・湖山・南吉方）</td><td>15〜20万円</td></tr>
          <tr><td>郊外（河原・気高・用瀬）</td><td>8〜12万円</td></tr>
        </tbody>
      </table>

      <h3>鳥取市の土地価格の特徴</h3>
      <ul>
        <li>生活利便性が高く人気</li>
        <li>冬の日照時間が短いため"日当たりの良い土地"は高め</li>
        <li>地盤が強いエリアは価格が上がりやすい</li>
      </ul>
      <p><strong>👉 60〜70坪で600〜1,200万円が目安</strong></p>

      <h2>② 米子市の土地価格相場（8〜18万円／坪）</h2>
      <p>米子市は<strong>エリアによって価格差が大きいのが特徴</strong>。</p>

      <h3>米子市の坪単価</h3>
      <table>
        <thead>
          <tr><th>エリア</th><th>坪単価の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>中心部（加茂町・旗ヶ崎・両三柳）</td><td>12〜18万円</td></tr>
          <tr><td>郊外（淀江・大篠津・夜見）</td><td>7〜12万円</td></tr>
        </tbody>
      </table>

      <h3>米子市の特徴</h3>
      <ul>
        <li>山陰道・米子駅周辺は人気で価格が高め</li>
        <li>車移動が中心のため"道路幅"が価格に影響</li>
        <li>湿気がこもりやすいエリアは価格が抑えめ</li>
      </ul>
      <p><strong>👉 60〜70坪で500〜1,000万円が目安</strong></p>

      <h2>③ 倉吉市の土地価格相場（6〜12万円／坪）</h2>
      <p>倉吉市は<strong>鳥取県内で最も土地が探しやすいエリア</strong>。</p>

      <h3>倉吉市の坪単価</h3>
      <table>
        <thead>
          <tr><th>エリア</th><th>坪単価の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>中心部（上井・伊木・八屋）</td><td>8〜12万円</td></tr>
          <tr><td>郊外（関金・北谷・小鴨）</td><td>5〜8万円</td></tr>
        </tbody>
      </table>

      <h3>倉吉市の特徴</h3>
      <ul>
        <li>価格が安定しておりコスパが良い</li>
        <li>生活圏がコンパクトで暮らしやすい</li>
        <li>地盤が強いエリアが多い</li>
      </ul>
      <p><strong>👉 60〜70坪で350〜700万円が目安</strong></p>

      <h2>④ 鳥取で土地価格が変わる5つのポイント</h2>
      <p>土地価格は「立地」だけで決まりません。鳥取では次の5つが大きく影響します。</p>

      <h3>① 日当たり</h3>
      <p>冬の日照時間が短いため、<strong>南向きは高くなりやすい</strong>。南向きで日当たり良好な土地は人気が高く、相場より10〜20％高くなるケースもあります。</p>

      <h3>② 地盤の強さ</h3>
      <p>地盤改良が必要な土地は価格が安め。ただし、<strong>地盤改良費が30〜100万円かかることも</strong>あるため、安い土地の理由を確認することが重要です。</p>

      <h3>③ 道路幅</h3>
      <p>車社会の鳥取では、道路が狭い土地は人気が下がる。<strong>4m以上の道路に接している土地</strong>が理想的です。</p>

      <h3>④ 周辺環境</h3>
      <p>学校・スーパー・病院が近いと価格が上がる。<strong>子育て世帯に人気のエリア</strong>は特に競争が激しくなります。</p>

      <h3>⑤ 湿気・風通し</h3>
      <p>湿気がこもる土地は価格が抑えられる傾向。ただし、<strong>住んでから後悔するケースも多い</strong>ため注意が必要です。</p>

      <h2>⑤ 鳥取で土地を選ぶときの注意点</h2>

      <h3>注意点① 冬の日当たりを必ず確認</h3>
      <p>冬に日が入らない土地は寒く、光熱費が高くなります。<strong>周辺の家の影にならないか</strong>も確認しましょう。</p>

      <h3>注意点② 地盤調査は必須</h3>
      <p>地盤改良費は30〜100万円かかることも。<strong>住宅会社と一緒に地盤チェック</strong>をするのが安心です。</p>

      <h3>注意点③ 住宅会社と一緒に土地を見る</h3>
      <p>プロの視点で"良い土地かどうか"を判断してもらえます。<strong>一人で決めずに必ずプロに同行してもらう</strong>ことをおすすめします。</p>

      <h3>注意点④ 安い土地には理由がある</h3>
      <p>湿気・地盤・道路幅など、必ず理由があります。<strong>「なぜ安いのか」を必ず確認</strong>することが大切です。</p>

      <h2>まとめ</h2>
      <p>鳥取の土地価格相場（2026年）は次のとおりです。</p>
      <table>
        <thead>
          <tr><th>エリア</th><th>坪単価</th><th>60〜70坪の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>鳥取市</td><td>10〜20万円</td><td>600〜1,200万円</td></tr>
          <tr><td>米子市</td><td>8〜18万円</td><td>500〜1,000万円</td></tr>
          <tr><td>倉吉市</td><td>6〜12万円</td><td>350〜700万円</td></tr>
        </tbody>
      </table>
      <p>鳥取は土地が手頃な地域ですが、<strong>日当たり・地盤・道路幅・湿気などで価格が大きく変わる</strong>のが特徴です。</p>

      <h2>次に読む記事</h2>
      <p>次は、土地とセットで知っておきたい<br />👉 <a href="/articles/26">「鳥取の住宅補助金2026年版｜みらいエコ住宅2026事業・NE-ST・とっとり住まいる支援事業まとめ」</a>を読むと、総額をさらに抑える方法がわかります。</p>
    `
  },
  {
    id: 26,
    step: '02',
    title: "鳥取の住宅補助金2026年版｜みらいエコ住宅2026事業・NE-ST・とっとり住まいる支援事業まとめ",
    category: "予算を決める",
    date: "2026年3月27日",
    readTime: "約14分",
    image: "https://public.readdy.ai/ai/img_res/edited_4a172d60305593f035058f8180568b23_83df82c2.jpg",
    excerpt: "2026年に鳥取で家を建てるなら、国・県・市町村の補助金を正しく組み合わせれば100〜200万円以上の支援が狙えます。みらいエコ住宅2026・NE-ST・とっとり住まいる支援事業を徹底解説。",
    tags: ["鳥取", "補助金", "みらいエコ住宅"],
    content: `
      <h2>はじめに</h2>
      <p>2026年の住宅補助金は、国・県・市町村の制度が大きく変わり、<strong>正しく理解すれば100〜200万円以上の支援が狙える年</strong>です。</p>
      <p>特に2026年は、</p>
      <ul>
        <li>国の新制度「みらいエコ住宅2026事業」</li>
        <li>鳥取県独自の「NE-ST」</li>
        <li>県の新築支援「とっとり住まいる支援事業」</li>
      </ul>
      <p>が併用できる可能性があり、例年以上にチャンスが大きい年。ただし、国の予算は<strong>2100億円 → 1750億円に減少</strong>しており、早期終了リスクが非常に高い点には注意が必要です。</p>

      <h2>結論：2026年に鳥取で使える補助金まとめ</h2>
      <table>
        <thead>
          <tr><th>制度</th><th>補助額</th><th>対象</th></tr>
        </thead>
        <tbody>
          <tr><td>GX志向型住宅（国）</td><td>110万円</td><td>省エネ基準を満たす住宅・年齢制限なし</td></tr>
          <tr><td>長期優良住宅（国）</td><td>75万円</td><td>子育て世帯・若者夫婦世帯</td></tr>
          <tr><td>ZEH水準住宅（国）</td><td>35万円</td><td>子育て世帯・若者夫婦世帯</td></tr>
          <tr><td>NE-ST（県）</td><td>市町村加算あり</td><td>鳥取県の高性能住宅基準を満たす住宅</td></tr>
          <tr><td>とっとり住まいる支援事業（県）</td><td>最大30万円＋加算</td><td>県内で新築住宅を取得する人</td></tr>
        </tbody>
      </table>
      <p>👉 <strong>合計100〜200万円以上の補助が狙える年</strong></p>

      <h2>① 国の補助金｜みらいエコ住宅2026事業</h2>
      <p>2026年の住宅補助金の中心となる制度です。</p>

      <h3>🔵 GX志向型住宅（110万円）</h3>
      <p>最も対象が広く、年齢制限なし。</p>
      <ul>
        <li>省エネ基準を満たす住宅</li>
        <li>子育て世帯でなくてもOK</li>
        <li>2026年で最も使いやすい補助金</li>
      </ul>
      <p>👉 <strong>ほぼ全ての新築が対象になる見込み</strong></p>

      <h3>🔵 長期優良住宅（75万円）</h3>
      <p>対象は限定されるが、性能の高い家向け。</p>
      <ul>
        <li>子育て世帯（18歳未満の子ども）</li>
        <li>若者夫婦世帯（夫婦のどちらか39歳以下）</li>
      </ul>

      <h3>🔵 ZEH水準住宅（35万円）</h3>
      <ul>
        <li>子育て世帯 or 若者夫婦</li>
        <li>ZEH水準の断熱性能が必要</li>
        <li>太陽光は必須ではない</li>
      </ul>

      <h2>② 国の予算が減少 → 早期終了リスクが高い</h2>
      <table>
        <thead>
          <tr><th>年度</th><th>予算規模</th></tr>
        </thead>
        <tbody>
          <tr><td>2025年</td><td>2,100億円</td></tr>
          <tr><td>2026年</td><td>1,750億円（350億円減額）</td></tr>
        </tbody>
      </table>
      <p>そのため、</p>
      <ul>
        <li>GXは特に早期終了の可能性が高い</li>
        <li>2025年のように「年度途中で締め切り」の可能性あり</li>
      </ul>
      <p>👉 <strong>2026年はスピード勝負</strong></p>

      <h2>③ 鳥取県のNE-ST（ネスト）</h2>
      <p>鳥取県独自の高性能住宅基準です。</p>

      <h3>NE-STの特徴</h3>
      <ul>
        <li>鳥取の気候（寒さ・湿気）に最適化</li>
        <li>断熱・気密・耐震性能が高い</li>
        <li>県が認定する「鳥取版の高性能住宅」</li>
      </ul>
      <p>👉 <strong>NE-ST × GX の組み合わせが最強</strong></p>
      <p>（参考：<a href="https://www.pref.tottori.lg.jp/308449.htm" rel="nofollow" target="_blank">鳥取県公式</a>）</p>

      <h2>④ 鳥取県｜とっとり住まいる支援事業（R7年度）</h2>

      <h3>🟡 補助金額</h3>
      <ul>
        <li>基本：最大30万円</li>
        <li>条件により加算あり（例：子育て世帯・移住者など）</li>
      </ul>
      <p>※市町村によって加算内容が異なる場合あり。</p>

      <h3>🟡 対象者</h3>
      <ul>
        <li>鳥取県内で新築住宅を取得する人</li>
        <li>一定の所得要件あり</li>
        <li>子育て世帯・若者夫婦世帯は優遇</li>
        <li>移住者も対象になるケースあり</li>
      </ul>

      <h3>🟡 対象住宅</h3>
      <ul>
        <li>新築住宅</li>
        <li>一定の省エネ性能を満たす住宅</li>
        <li>NE-ST基準を満たすと加算される場合あり</li>
      </ul>

      <h3>🟡 注意点</h3>
      <ul>
        <li>予算枠があり、早期終了の可能性あり</li>
        <li>申請は住宅会社と連携して行う必要がある</li>
        <li>県と市町村の制度が併用できる場合がある</li>
      </ul>
      <p>👉 <strong>国＋県＋市町村の三重取りが可能なケースもある</strong></p>

      <h2>⑤ 補助金の併用について</h2>
      <h3>併用できるもの</h3>
      <ul>
        <li>国（みらいエコ住宅2026）</li>
        <li>県（NE-ST）</li>
        <li>県（とっとり住まいる支援事業）</li>
        <li>市町村の独自補助</li>
        <li>太陽光・蓄電池補助</li>
      </ul>
      <p>👉 <strong>最大化すれば100〜200万円以上の補助も可能</strong></p>

      <h2>⑥ 補助金の申請の流れ</h2>
      <ol>
        <li>住宅会社に相談（最重要）</li>
        <li>対象補助金の確認</li>
        <li>必要書類の準備</li>
        <li>住宅会社が代理申請</li>
        <li>完成後に補助金が振り込まれる（後払い）</li>
      </ol>
      <p>👉 <strong>申請はほぼ住宅会社が代行してくれるので安心。</strong></p>

      <h2>まとめ</h2>
      <p>2026年に鳥取で家を建てるなら、次の補助金を組み合わせるのが最強です。</p>
      <table>
        <thead>
          <tr><th>区分</th><th>制度</th><th>補助額</th></tr>
        </thead>
        <tbody>
          <tr><td>🔵 国</td><td>GX志向型住宅</td><td>110万円</td></tr>
          <tr><td>🔵 国</td><td>長期優良住宅</td><td>75万円</td></tr>
          <tr><td>🔵 国</td><td>ZEH水準住宅</td><td>35万円</td></tr>
          <tr><td>🟢 県</td><td>NE-ST（高性能住宅認定）</td><td>市町村加算あり</td></tr>
          <tr><td>🟡 県</td><td>とっとり住まいる支援事業</td><td>最大30万円＋加算</td></tr>
        </tbody>
      </table>
      <p>👉 <strong>2026年は補助金の"重ね取り"ができるチャンスの年</strong></p>

      <h2>次に読む記事</h2>
      <p>次は、補助金とセットで知っておきたい<br />👉 <a href="/articles/27">「鳥取で家を買う頭金はいくら必要？ゼロでも買えるのか徹底解説」</a>を読むと、あなたの予算に合わせた最適な家づくりが見えてきます。</p>
    `
  },
  {
    id: 27,
    step: '02',
    title: "鳥取で家を買う頭金はいくら必要？ゼロでも買えるのか徹底解説",
    category: "予算を決める",
    date: "2026年3月27日",
    readTime: "約13分",
    image: "https://public.readdy.ai/ai/img_res/edited_5ed41a00461d7c86dafc866d0c790422_f9244316.jpg",
    excerpt: "「鳥取で家を買うとき頭金はいくら必要？」「頭金ゼロでも家って買えるの？」鳥取の地域事情に合わせた頭金の目安・ゼロでも買えるのか・頭金のメリット/デメリットをわかりやすく解説します。",
    tags: ["鳥取", "頭金", "住宅購入"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を買うとき、頭金はいくら必要なんだろう…？」<br />「頭金ゼロでも家って買えるの？」</p>
      <p>家づくりを考え始めると、まず気になるのが<strong>頭金の必要額</strong>ですよね。</p>
      <p>特に鳥取は、<strong>土地価格が比較的安く、頭金ゼロで購入する人も多い地域</strong>。そのため、全国の情報よりも"鳥取に合った頭金の考え方"が必要です。</p>
      <p>この記事では、鳥取で家を買うときの<strong>頭金の目安・ゼロでも買えるのか・頭金のメリット/デメリット</strong>をわかりやすく解説します。</p>

      <h2>結論：鳥取の頭金の考え方</h2>
      <ul>
        <li>頭金は0〜200万円が一般的</li>
        <li>頭金ゼロでも購入は可能（実際に多い）</li>
        <li>ただし、諸費用分は現金で用意しておくと安心</li>
        <li>頭金より"月々の返済額"を優先して考えるべき</li>
      </ul>
      <p>👉 <strong>鳥取では「頭金ゼロで建てる」人が珍しくありません。</strong></p>

      <h2>① 鳥取で家を買う頭金はいくら必要？</h2>
      <p>結論から言うと、鳥取では<strong>頭金0〜200万円が最も多い</strong>です。</p>

      <h3>なぜ頭金が少なくても買えるのか？</h3>
      <ul>
        <li>土地価格が安い（300〜800万円）</li>
        <li>建物価格も全国平均より低い</li>
        <li>銀行の住宅ローン審査が比較的柔軟</li>
        <li>車社会で貯金より現金流動性を重視する家庭が多い</li>
      </ul>
      <p>👉 <strong>頭金ゼロで建てる人も普通にいる</strong></p>

      <h2>② 頭金ゼロでも家は買えるのか？</h2>
      <p><strong>結論：買えます。しかも珍しくない。</strong></p>
      <p>鳥取では、若い夫婦・子育て世帯・貯金より月々の返済を重視する家庭を中心に、<strong>頭金ゼロでの新築購入が増えています</strong>。</p>

      <h3>頭金ゼロのメリット</h3>
      <ul>
        <li>家賃を払い続ける期間を短縮できる</li>
        <li>貯金を残せる（車・教育費に回せる）</li>
        <li>補助金（GX110万円など）で実質負担が軽くなる</li>
      </ul>

      <h2>③ 頭金ゼロのデメリット</h2>
      <h3>デメリット</h3>
      <ul>
        <li>借入額が増える</li>
        <li>月々の返済がやや高くなる</li>
        <li>金利負担が増える</li>
        <li>諸費用ローンを組むと金利が高い場合がある</li>
      </ul>
      <p>👉 <strong>頭金ゼロでもOKだが、月々の返済額は慎重に設定する必要がある</strong></p>

      <h2>④ 鳥取で頭金を入れるならいくらが最適？</h2>
      <p>鳥取の家計感覚を踏まえると、<strong>頭金は100〜200万円が最もバランスが良い</strong>です。</p>

      <h3>理由</h3>
      <ul>
        <li>月々の返済が安定する</li>
        <li>諸費用を現金で払える</li>
        <li>車の買い替え・教育費に余裕を残せる</li>
      </ul>
      <p>👉 <strong>無理に300〜500万円を貯める必要はない</strong></p>

      <h2>⑤ 頭金より大事なのは"月々の返済額"</h2>
      <p>頭金の金額よりも、<strong>月々いくら払えるか</strong>のほうが圧倒的に重要です。</p>

      <h3>鳥取の無理のない返済額</h3>
      <ul>
        <li>月々7〜10万円が最も多い</li>
        <li>家賃＋1〜2万円以内が理想</li>
      </ul>

      <h3>例：家賃6.5万円の家庭</h3>
      <ul>
        <li>→ 月々7.5〜8.5万円が無理のないライン</li>
        <li>→ 借入額は2,200〜2,600万円が目安</li>
      </ul>
      <p>👉 <strong>頭金より"返済計画"が家計を左右する</strong></p>

      <h2>⑥ 頭金を入れるべき人・入れなくていい人</h2>

      <h3>🔵 頭金を入れるべき人</h3>
      <ul>
        <li>月々の返済を少しでも減らしたい</li>
        <li>車のローンが残っている</li>
        <li>教育費がこれから増える家庭</li>
      </ul>

      <h3>🟢 頭金ゼロでもOKな人</h3>
      <ul>
        <li>貯金が少ない若い夫婦</li>
        <li>家賃が高く、早く家を建てたい</li>
        <li>補助金（GX110万円など）を活用できる</li>
        <li>月々の返済に余裕がある</li>
      </ul>
      <p>👉 <strong>鳥取では"頭金ゼロで建てる若い世帯"が増えている</strong></p>

      <h2>⑦ 頭金の代わりに準備しておくべきもの</h2>
      <p>頭金よりも重要なのが<strong>諸費用の現金</strong>です。</p>

      <h3>諸費用の目安</h3>
      <ul>
        <li>150〜250万円</li>
      </ul>
      <p>これを現金で払えると、</p>
      <ul>
        <li>ローンが増えない</li>
        <li>月々の返済が安定</li>
        <li>金利負担が減る</li>
      </ul>
      <p>👉 <strong>頭金より"諸費用の現金"を優先して貯めるのが賢い</strong></p>

      <h2>まとめ</h2>
      <p>鳥取で家を買う頭金は次のとおりです。</p>
      <table>
        <thead>
          <tr><th>項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>頭金の相場</td><td>0〜200万円が一般的</td></tr>
          <tr><td>頭金ゼロ</td><td>可能・鳥取では珍しくない</td></tr>
          <tr><td>最適な頭金額</td><td>100〜200万円がバランス良い</td></tr>
          <tr><td>最重要ポイント</td><td>月々の返済額を先に決める</td></tr>
          <tr><td>諸費用</td><td>150〜250万円の現金を用意</td></tr>
        </tbody>
      </table>
      <p>鳥取は土地価格が安く、補助金（GX110万円など）も充実しているため、<strong>頭金ゼロでも無理なく家を建てられる地域</strong>と言えます。</p>

      <h2>次に読む記事</h2>
      <p>次は、頭金とセットで知っておきたい<br />👉 <a href="/articles/21">「鳥取の住宅ローンはいくら借りられる？年収別の借入目安」</a>を読むと、あなたの予算がより明確になります。</p>
      <p>パートナー（ハウスメーカー、工務店）についてに進みたい場合は、<br />👉 <a href="/articles/28">「鳥取でおすすめの工務店5選｜特徴・価格帯・選び方を徹底比較」</a>を読むと、工務店の特徴がよくわかります。</p>
    `
  },
  {
    id: 28,
    step: '03',
    title: "鳥取でおすすめの工務店5選｜特徴・価格帯・選び方を徹底比較",
    category: "パートナーを選ぶ",
    date: "2026年3月27日",
    readTime: "約14分",
    image: "https://public.readdy.ai/ai/img_res/edited_b0d959008f6a53c3ea14dedbf5463ffc_4eabb9f5.jpg",
    excerpt: "「鳥取でどの工務店がいいの？」性能・デザイン・コスパ・地域密着の観点から厳選した5社（アート建工・CLAVIS・ヤマタホーム・ウッズカンパニー・ひらぎの）を徹底比較します。",
    tags: ["鳥取", "工務店", "おすすめ"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を建てるなら、どの工務店がいいの？」<br />「性能・デザイン・価格のバランスが良い会社を知りたい」<br />「地元の評判が良い工務店を比較したい」</p>
      <p>鳥取には、全国ブランドのハウスメーカーだけでなく、<strong>地域密着で高性能な家づくりをする工務店が多数あります</strong>。その中でも今回は、鳥取県内で特に人気・実績のある<strong>5社を厳選して紹介</strong>します。</p>
      <ul>
        <li>高性能</li>
        <li>デザイン</li>
        <li>コスパ</li>
        <li>地域密着</li>
        <li>若い世帯との相性</li>
      </ul>
      <p>これらを総合的に評価し、鳥取で家づくりを検討する人にとって最も比較しやすい形でまとめました。</p>

      <h2>結論：鳥取でおすすめの工務店5社</h2>
      <ol>
        <li>アート建工（米子・鳥取）</li>
        <li>CLAVIS（倉吉）</li>
        <li>ヤマタホーム（鳥取）</li>
        <li>ウッズカンパニー（鳥取）</li>
        <li>ひらぎの（鳥取・米子）</li>
      </ol>
      <p>👉 「性能 × デザイン × コスパ × 地域性」のバランスで厳選。</p>

      <h2>① アート建工（米子・鳥取）</h2>
      <p><strong>高性能 × デザイン × コスパの三拍子が揃う人気工務店</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>UA値0.3台の高断熱住宅</li>
        <li>デザイン性が高く若い世帯に人気</li>
        <li>モデルハウスが豊富</li>
        <li>土地探しにも強い</li>
        <li>ZEH・長期優良住宅にも対応</li>
      </ul>

      <h3>価格帯</h3>
      <p>2,000〜3,000万円台</p>

      <h3>向いている人</h3>
      <ul>
        <li>性能もデザインも妥協したくない</li>
        <li>モデルハウスを見て決めたい</li>
        <li>コスパの良い高性能住宅が欲しい</li>
      </ul>

      <h2>② CLAVIS（倉吉）</h2>
      <p><strong>若い世帯に寄り添う"等身大の家づくり"が強みの地域密着工務店</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>20〜30代の家づくりに特化した提案</li>
        <li>シンプルで暮らしやすい間取り</li>
        <li>コスパの良い高性能住宅</li>
        <li>デザインは"温かみのあるナチュラル系"が得意</li>
        <li>相談しやすい雰囲気で初めての家づくりに最適</li>
      </ul>

      <h3>価格帯</h3>
      <p>1,800〜2,800万円</p>

      <h3>向いている人</h3>
      <ul>
        <li>若い夫婦・子育て世帯</li>
        <li>無理のない予算で性能の良い家を建てたい</li>
        <li>地元の会社とじっくり相談しながら進めたい</li>
      </ul>

      <h2>③ ヤマタホーム（鳥取）</h2>
      <p><strong>鳥取市で人気の"高性能 × デザイン"工務店</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>高断熱・高気密の家づくり</li>
        <li>モダンで洗練されたデザイン</li>
        <li>ZEH・長期優良住宅に強い</li>
        <li>土地探しからワンストップで対応</li>
        <li>住宅展示場が充実</li>
      </ul>

      <h3>価格帯</h3>
      <p>2,200〜3,500万円</p>

      <h3>向いている人</h3>
      <ul>
        <li>デザイン性の高い家が欲しい</li>
        <li>鳥取市で土地探しから相談したい</li>
        <li>性能とデザインの両立を求める</li>
      </ul>

      <h2>④ ウッズカンパニー（鳥取）</h2>
      <p><strong>自然素材 × 高性能の"木の家"が得意な工務店</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>無垢材・自然素材を使った家づくり</li>
        <li>断熱性能が高く冬も暖かい</li>
        <li>大工の技術力が高い</li>
        <li>住んでからの心地よさにこだわる</li>
        <li>地元での信頼が厚い</li>
      </ul>

      <h3>価格帯</h3>
      <p>2,000〜3,500万円</p>

      <h3>向いている人</h3>
      <ul>
        <li>自然素材の家が好き</li>
        <li>木の温もりを感じる家に住みたい</li>
        <li>丁寧な施工を求める</li>
      </ul>

      <h2>⑤ ひらぎの（鳥取・米子）</h2>
      <p><strong>デザイン性 × 高性能のバランスが良い人気工務店</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>モダンで洗練されたデザイン</li>
        <li>ZEH対応の高性能住宅</li>
        <li>企画住宅と注文住宅の両方に対応</li>
        <li>モデルハウスが多く比較しやすい</li>
        <li>若い世帯にも人気</li>
      </ul>

      <h3>価格帯</h3>
      <p>2,200〜3,500万円</p>

      <h3>向いている人</h3>
      <ul>
        <li>デザイン重視</li>
        <li>モデルハウスを見て選びたい</li>
        <li>性能とデザインのバランスを求める</li>
      </ul>

      <h2>⑥ 鳥取で工務店を選ぶときのポイント</h2>
      <ol>
        <li>性能（UA値・C値）を確認する</li>
        <li>標準仕様の内容を比較する</li>
        <li>総額で見積もりを出してもらう</li>
        <li>モデルハウス・完成見学会に行く</li>
        <li>担当者との相性を重視する</li>
        <li>アフター対応の評判を見る</li>
      </ol>
      <p>👉 <strong>工務店選びは「性能 × 担当者 × 価格」のバランスが重要。</strong></p>

      <h2>まとめ</h2>
      <p>鳥取でおすすめの工務店は次の5社。</p>
      <table>
        <thead>
          <tr><th>工務店名</th><th>特徴</th><th>価格帯</th></tr>
        </thead>
        <tbody>
          <tr><td>アート建工</td><td>高性能×デザイン×コスパ</td><td>2,000〜3,000万円台</td></tr>
          <tr><td>CLAVIS</td><td>若い世帯に寄り添う地域密着</td><td>1,800〜2,800万円</td></tr>
          <tr><td>ヤマタホーム</td><td>鳥取市で人気の高性能住宅</td><td>2,200〜3,500万円</td></tr>
          <tr><td>ウッズカンパニー</td><td>自然素材の家が得意</td><td>2,000〜3,500万円</td></tr>
          <tr><td>ひらぎの</td><td>デザイン性が高い</td><td>2,200〜3,500万円</td></tr>
        </tbody>
      </table>
      <p>鳥取は地域密着の工務店が多く、<strong>性能・価格・デザインのバランスが良い会社が揃っている地域</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>次は、工務店と比較されることが多い<br />👉 <a href="/articles/29">「鳥取のハウスメーカー比較｜価格・性能・保証で選ぶ最適な1社」</a>を読むと、より自分に合った住宅会社が見つかります。</p>
    `
  },
  {
    id: 29,
    step: '03',
    title: "鳥取のハウスメーカー比較｜価格・性能・保証で選ぶ最適な1社",
    category: "パートナーを選ぶ",
    date: "2026年3月27日",
    readTime: "約14分",
    image: "https://public.readdy.ai/ai/img_res/edited_1d09bd6c00d4af9254049d14b11254b8_d126bfa2.jpg",
    excerpt: "「鳥取でどのハウスメーカーがいい？」一条工務店・積水ハウス・ダイワハウス・セキスイハイム・ミサワホームを価格・性能・保証・特徴の4軸で徹底比較します。",
    tags: ["鳥取", "ハウスメーカー", "比較"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を建てるなら、どのハウスメーカーがいいの？」<br />「性能・価格・保証の違いが知りたい」<br />「工務店とハウスメーカー、どっちが自分に合う？」</p>
      <p>鳥取には全国展開のハウスメーカーが多数あり、<strong>性能・保証・ブランド力の高さが魅力</strong>です。ただし、ハウスメーカーごとに価格帯・性能（断熱・耐震）・標準仕様・保証内容・デザインの方向性が大きく異なるため、比較せずに選ぶと後悔しやすいのも事実。</p>
      <p>この記事では、鳥取で人気のハウスメーカーを<strong>価格・性能・保証・特徴の4軸で徹底比較</strong>します。</p>

      <h2>結論：鳥取で人気のハウスメーカー5社</h2>
      <table>
        <thead>
          <tr><th>会社名</th><th>強み</th><th>価格帯</th></tr>
        </thead>
        <tbody>
          <tr><td>一条工務店</td><td>性能トップクラス</td><td>2,500〜3,800万円</td></tr>
          <tr><td>積水ハウス</td><td>ブランド力・保証が強い</td><td>3,000〜4,500万円</td></tr>
          <tr><td>ダイワハウス</td><td>耐震性に強い</td><td>3,000〜4,800万円</td></tr>
          <tr><td>セキスイハイム</td><td>工場品質×太陽光</td><td>2,800〜4,200万円</td></tr>
          <tr><td>ミサワホーム</td><td>蔵のある家・収納力</td><td>2,800〜4,200万円</td></tr>
        </tbody>
      </table>
      <p>👉 「性能」「保証」「価格」のどれを重視するかで最適な1社が変わります。</p>

      <h2>① 一条工務店（鳥取市・米子市）</h2>
      <p><strong>性能トップクラス。断熱・気密にこだわる人に最適</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>業界トップクラスの断熱性能（UA値0.25前後）</li>
        <li>標準仕様が圧倒的に充実</li>
        <li>太陽光＋蓄電池の提案が強い</li>
        <li>モデルハウスの完成度が高い</li>
      </ul>

      <h3>価格帯・保証</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>2,500〜3,800万円</td></tr>
          <tr><td>保証</td><td>構造30年保証（条件あり）</td></tr>
        </tbody>
      </table>

      <h3>向いている人</h3>
      <ul>
        <li>とにかく性能重視</li>
        <li>光熱費を抑えたい</li>
        <li>標準仕様が充実した家が良い</li>
      </ul>

      <h2>② 積水ハウス（鳥取市）</h2>
      <p><strong>ブランド力・保証・デザインの総合力が高い</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>業界最大手の安心感</li>
        <li>耐震・断熱・デザインのバランスが良い</li>
        <li>外観デザインが美しい</li>
        <li>営業・アフターの対応が安定</li>
      </ul>

      <h3>価格帯・保証</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>3,000〜4,500万円</td></tr>
          <tr><td>保証</td><td>60年サポート体制（条件あり）</td></tr>
        </tbody>
      </table>

      <h3>向いている人</h3>
      <ul>
        <li>大手の安心感を重視</li>
        <li>デザイン性の高い家が欲しい</li>
        <li>長期保証を重視する</li>
      </ul>

      <h2>③ ダイワハウス（鳥取市）</h2>
      <p><strong>耐震性に強い"鉄骨住宅"の代表格</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>鉄骨造の耐震性が非常に高い</li>
        <li>大空間・大開口の間取りが得意</li>
        <li>工場生産で品質が安定</li>
        <li>住宅展示場が充実</li>
      </ul>

      <h3>価格帯・保証</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>3,000〜4,800万円</td></tr>
          <tr><td>保証</td><td>構造・防水の長期保証あり</td></tr>
        </tbody>
      </table>

      <h3>向いている人</h3>
      <ul>
        <li>地震に強い家が欲しい</li>
        <li>大空間リビングに憧れる</li>
        <li>鉄骨住宅に興味がある</li>
      </ul>

      <h2>④ セキスイハイム（鳥取市）</h2>
      <p><strong>工場品質 × 太陽光の家づくりが強み</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>住宅の大部分を工場で生産（品質が安定）</li>
        <li>太陽光発電の搭載率が高い</li>
        <li>引き渡しまでが早い</li>
        <li>ZEH住宅に強い</li>
      </ul>

      <h3>価格帯・保証</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>2,800〜4,200万円</td></tr>
          <tr><td>保証</td><td>長期保証制度あり</td></tr>
        </tbody>
      </table>

      <h3>向いている人</h3>
      <ul>
        <li>太陽光で光熱費を抑えたい</li>
        <li>工場品質の家が良い</li>
        <li>工期の短さを重視したい</li>
      </ul>

      <h2>⑤ ミサワホーム（鳥取市）</h2>
      <p><strong>"蔵のある家"で収納力が圧倒的</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>大収納空間「蔵」が有名</li>
        <li>デザイン性が高い</li>
        <li>木質パネル工法で耐震性も高い</li>
        <li>モデルハウスが見やすい</li>
      </ul>

      <h3>価格帯・保証</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>2,800〜4,200万円</td></tr>
          <tr><td>保証</td><td>長期保証制度あり</td></tr>
        </tbody>
      </table>

      <h3>向いている人</h3>
      <ul>
        <li>収納力を重視したい</li>
        <li>デザイン性の高い家が好き</li>
        <li>木質パネル工法に興味がある</li>
      </ul>

      <h2>⑥ 鳥取でハウスメーカーを選ぶポイント</h2>
      <ol>
        <li>性能（UA値・C値）を確認する</li>
        <li>保証内容（何年？何が対象？）を比較する</li>
        <li>標準仕様のレベルをチェック</li>
        <li>総額で見積もりを出してもらう</li>
        <li>モデルハウスを複数見て比較する</li>
        <li>担当者との相性を重視する</li>
      </ol>
      <p>👉 <strong>ハウスメーカーは「性能 × 保証 × 標準仕様」で選ぶと失敗しない。</strong></p>

      <h2>工務店とハウスメーカー、どっちが向いてる？</h2>
      <table>
        <thead>
          <tr><th>比較項目</th><th>工務店</th><th>ハウスメーカー</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>1,800〜3,500万円</td><td>2,500〜4,800万円</td></tr>
          <tr><td>性能</td><td>会社による（高低差大）</td><td>全体的に安定</td></tr>
          <tr><td>デザイン自由度</td><td>高い</td><td>やや制限あり</td></tr>
          <tr><td>保証</td><td>会社による</td><td>長期保証が充実</td></tr>
          <tr><td>地域密着度</td><td>高い</td><td>やや低い</td></tr>
          <tr><td>ブランド安心感</td><td>やや低い</td><td>高い</td></tr>
        </tbody>
      </table>
      <p>鳥取は冬の寒さが厳しい地域のため、<strong>断熱性能・気密性能を重視したハウスメーカー選びが重要</strong>です。</p>

      <h2>まとめ</h2>
      <p>鳥取で人気のハウスメーカーは次の5社。</p>
      <table>
        <thead>
          <tr><th>会社名</th><th>最大の強み</th></tr>
        </thead>
        <tbody>
          <tr><td>一条工務店</td><td>性能トップ（UA値0.25前後）</td></tr>
          <tr><td>積水ハウス</td><td>ブランド力・60年保証</td></tr>
          <tr><td>ダイワハウス</td><td>鉄骨×耐震×大空間</td></tr>
          <tr><td>セキスイハイム</td><td>工場品質×太陽光ZEH</td></tr>
          <tr><td>ミサワホーム</td><td>蔵×収納力×デザイン</td></tr>
        </tbody>
      </table>
      <p>鳥取は冬の寒さが厳しい地域のため、<strong>断熱性能・気密性能を重視したハウスメーカー選びが重要</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>次は、価格を抑えて建てたい人向けに<br />👉 <a href="/articles/30">「ローコスト住宅おすすめ5社｜実店舗・展示場がある安心のメーカー【鳥取版】」</a>を読むと、あなたに合った住宅会社がより明確になります。</p>
    `
  },
  {
    id: 30,
    step: '03',
    title: "ローコスト住宅おすすめ5社｜実店舗・展示場がある安心のメーカー【鳥取版】",
    category: "パートナーを選ぶ",
    date: "2026年3月27日",
    readTime: "約12分",
    image: "https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_e435e881.jpg",
    excerpt: "「ローコスト住宅はどの会社なら安心？」実際に展示場・相談窓口がある5社（アイフルホーム・タマホーム・ニコニコ住宅・ユニバーサルホーム・サイエンスホーム）を1000〜2000万円台の価格帯で比較します。",
    tags: ["鳥取", "ローコスト", "1000万円台"],
    content: `
      <h2>はじめに</h2>
      <p>「ローコスト住宅を建てたいけど、どの会社なら安心？」<br />「実際に展示場で見られるメーカーだけ知りたい」<br />「1000〜2000万円台で現実的に建てられる会社を比較したい」</p>
      <p>ローコスト住宅は価格が魅力ですが、性能・標準仕様・追加費用・保証などの差が大きいため、<strong>実店舗・展示場がある会社を選ぶことが重要</strong>です。</p>
      <p>この記事では、実際に展示場・相談窓口があるローコスト住宅メーカー<strong>5社を厳選して紹介</strong>します。</p>

      <h2>結論：ローコスト住宅のおすすめ5社</h2>
      <table>
        <thead>
          <tr><th>会社名</th><th>強み</th><th>価格帯</th></tr>
        </thead>
        <tbody>
          <tr><td>アイフルホーム</td><td>子育て世帯に強い</td><td>1,600〜2,300万円</td></tr>
          <tr><td>タマホーム</td><td>全国トップのローコスト</td><td>1,500〜2,300万円</td></tr>
          <tr><td>ニコニコ住宅</td><td>1000万円台の超ローコスト</td><td>1,000〜1,800万円</td></tr>
          <tr><td>ユニバーサルホーム</td><td>地熱床システムで冬に強い</td><td>1,700〜2,600万円</td></tr>
          <tr><td>サイエンスホーム</td><td>木の家×ローコスト</td><td>1,600〜2,500万円</td></tr>
        </tbody>
      </table>
      <p>👉 すべて「実店舗・展示場あり」で安心して相談できる会社だけを厳選。</p>

      <h2>① アイフルホーム</h2>
      <p><strong>子育て世帯に強い、ローコスト住宅の定番メーカー</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>LIXILグループの安心感</li>
        <li>価格が明瞭で選びやすい</li>
        <li>子育て世帯向けの間取りが豊富</li>
        <li>地元工務店が施工するため対応が早い</li>
        <li>展示場で実物を確認できる</li>
      </ul>

      <h3>価格帯・向いている人</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>1,600〜2,300万円</td></tr>
          <tr><td>向いている人</td><td>コスパ重視・子育てしやすい家が欲しい・全国ブランドの安心感が欲しい人</td></tr>
        </tbody>
      </table>

      <h2>② タマホーム</h2>
      <p><strong>全国トップクラスのローコストメーカー</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>大量仕入れによる低価格</li>
        <li>標準仕様が比較的充実</li>
        <li>モデルハウスが見やすい</li>
        <li>若い世帯の建築実績が多い</li>
      </ul>

      <h3>価格帯・向いている人</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>1,500〜2,300万円</td></tr>
          <tr><td>向いている人</td><td>とにかく価格を抑えたい・標準仕様がしっかりした家が良い・大手の安心感が欲しい人</td></tr>
        </tbody>
      </table>

      <h2>③ ニコニコ住宅</h2>
      <p><strong>1000万円台で建てられる"超ローコスト"が魅力</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>1000万円台で建てられるプランが豊富</li>
        <li>シンプルで無駄のない間取り</li>
        <li>若い夫婦・子育て世帯に人気</li>
        <li>展示場で実物を確認できる</li>
        <li>ローコストでも暮らしやすさを重視した設計</li>
      </ul>

      <h3>価格帯・向いている人</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>1,000〜1,800万円</td></tr>
          <tr><td>向いている人</td><td>とにかく予算を抑えたい・シンプルで暮らしやすい家が良い・初めての家づくりで不安がある人</td></tr>
        </tbody>
      </table>

      <h2>④ ユニバーサルホーム</h2>
      <p><strong>"地熱床システム"で冬に強いローコスト住宅</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>地熱床暖房が人気</li>
        <li>冬の寒さに強い（鳥取の気候と相性が良い）</li>
        <li>外観デザインが豊富</li>
        <li>展示場で体感できる</li>
      </ul>

      <h3>価格帯・向いている人</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>1,700〜2,600万円</td></tr>
          <tr><td>向いている人</td><td>冬の寒さが気になる・床暖房に興味がある・デザインを選びたい人</td></tr>
        </tbody>
      </table>

      <h2>⑤ サイエンスホーム</h2>
      <p><strong>"木の家 × ローコスト"が魅力の人気メーカー</strong></p>

      <h3>特徴</h3>
      <ul>
        <li>国産ヒノキを使った"真壁づくり"が特徴</li>
        <li>木の温もりを感じるデザイン</li>
        <li>無垢材を使いながら価格を抑えた家づくり</li>
        <li>和モダン・ナチュラル系のデザインが得意</li>
        <li>展示場で木の質感を体感できる</li>
      </ul>

      <h3>価格帯・向いている人</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>1,600〜2,500万円</td></tr>
          <tr><td>向いている人</td><td>木の家が好き・自然素材を使った家に住みたい・デザイン性と価格のバランスを重視したい人</td></tr>
        </tbody>
      </table>

      <h2>ローコスト住宅を選ぶときの注意点</h2>
      <ol>
        <li><strong>本体価格だけで判断しない</strong>（追加費用に注意）</li>
        <li><strong>標準仕様の性能</strong>（断熱・気密）を確認する</li>
        <li><strong>総額で見積もり</strong>を出してもらう</li>
        <li><strong>展示場で実物を必ず見る</strong></li>
        <li><strong>アフター対応の評判</strong>を見る</li>
      </ol>
      <p>👉 <strong>ローコスト住宅は「本体価格」だけで判断すると失敗しやすい。</strong></p>

      <h3>鳥取でローコスト住宅を選ぶ際の追加チェックポイント</h3>
      <table>
        <thead>
          <tr><th>確認項目</th><th>理由</th></tr>
        </thead>
        <tbody>
          <tr><td>UA値（断熱性能）</td><td>鳥取の冬は寒いため、断熱性能が不十分だと光熱費が高くなる</td></tr>
          <tr><td>C値（気密性能）</td><td>気密性が低いと暖房効率が落ちる</td></tr>
          <tr><td>窓の種類</td><td>アルミ窓より樹脂窓のほうが鳥取の気候に向いている</td></tr>
          <tr><td>外構費の有無</td><td>本体価格に含まれているか必ず確認</td></tr>
        </tbody>
      </table>

      <h2>まとめ</h2>
      <p>ローコスト住宅を建てるなら、実店舗・展示場がある次の5社が安心です。</p>
      <table>
        <thead>
          <tr><th>会社名</th><th>最大の強み</th><th>価格帯</th></tr>
        </thead>
        <tbody>
          <tr><td>アイフルホーム</td><td>子育て世帯に強い</td><td>1,600〜2,300万円</td></tr>
          <tr><td>タマホーム</td><td>全国トップのローコスト</td><td>1,500〜2,300万円</td></tr>
          <tr><td>ニコニコ住宅</td><td>1000万円台の超ローコスト</td><td>1,000〜1,800万円</td></tr>
          <tr><td>ユニバーサルホーム</td><td>地熱床システムで冬に強い</td><td>1,700〜2,600万円</td></tr>
          <tr><td>サイエンスホーム</td><td>木の家×ローコスト</td><td>1,600〜2,500万円</td></tr>
        </tbody>
      </table>
      <p>土地価格が安い地域では、<strong>1000〜2000万円台で無理なく家を建てられる選択肢が豊富</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>次は、ローコスト住宅と比較されることが多い<br />👉 <a href="/articles/31">「工務店とハウスメーカーどっちが良い？違いと選び方を徹底解説」</a>を読むと、あなたに合った住宅会社がより明確になります。</p>
    `
  },
  {
    id: 31,
    step: '03',
    title: "工務店とハウスメーカーどっちが良い？違いと選び方を徹底解説",
    category: "パートナーを選ぶ",
    date: "2026年3月27日",
    readTime: "約13分",
    image: "https://public.readdy.ai/ai/img_res/edited_4d238f0f6f35a2f8344e66f76d6a4dd7_e7461e51.jpg",
    excerpt: "「工務店とハウスメーカー、どっちが自分に合っている？」性能・価格・保証・自由度・満足度の5軸で徹底比較。若い世帯・初めての家づくりに向いているのはどちらかを解説します。",
    tags: ["鳥取", "工務店", "ハウスメーカー"],
    content: `
      <h2>はじめに</h2>
      <p>「工務店とハウスメーカー、どっちが自分に合っているんだろう」<br />「性能・価格・保証の違いがよくわからない」<br />「最終的にどちらを選べば後悔しない？」</p>
      <p>家づくりを始めると、必ずぶつかるのが<strong>"工務店 vs ハウスメーカー"問題</strong>。どちらもメリット・デメリットがあり、どちらが正解というわけではありません。</p>
      <p>ただし、予算・性能・デザイン・担当者との距離感・家づくりのスタイルによって、向いている選択肢は大きく変わります。この記事では、工務店とハウスメーカーの違いをわかりやすく比較し、<strong>どちらがあなたに合うか判断できる内容</strong>にまとめました。</p>

      <h2>結論</h2>
      <ul>
        <li>コスパ・自由度・地域密着を重視するなら<strong>工務店</strong></li>
        <li>ブランド・保証・安定品質を重視するなら<strong>ハウスメーカー</strong></li>
      </ul>
      <p>👉 <strong>若い世帯・初めての家づくりには、工務店のほうが満足度が高いケースが多い。</strong></p>

      <h2>① 工務店とハウスメーカーの違いを一覧で比較</h2>
      <table>
        <thead>
          <tr><th>比較項目</th><th>工務店</th><th>ハウスメーカー</th></tr>
        </thead>
        <tbody>
          <tr><td>価格帯</td><td>1,800〜3,500万円</td><td>2,500〜4,800万円</td></tr>
          <tr><td>性能</td><td>会社による（高低差大）</td><td>全体的に安定</td></tr>
          <tr><td>デザイン自由度</td><td>高い</td><td>やや制限あり</td></tr>
          <tr><td>保証</td><td>会社による（短い場合も）</td><td>長期保証が充実</td></tr>
          <tr><td>地域密着度</td><td>高い</td><td>やや低い</td></tr>
          <tr><td>ブランド安心感</td><td>やや低い</td><td>高い</td></tr>
          <tr><td>担当者との距離</td><td>近い・相談しやすい</td><td>やや遠い・担当制</td></tr>
          <tr><td>モデルハウス数</td><td>少ない場合も</td><td>多い</td></tr>
        </tbody>
      </table>
      <p>👉 「自由度・コスパ」なら工務店、「ブランド・保証」ならハウスメーカー。</p>

      <h2>② 工務店のメリット・デメリット</h2>

      <h3>🔵 工務店のメリット</h3>
      <ul>
        <li>価格が抑えやすい（コスパが良い）</li>
        <li>間取り・デザインの自由度が高い</li>
        <li>地域の気候に合わせた家づくりが得意</li>
        <li>担当者との距離が近く相談しやすい</li>
        <li>柔軟な対応ができる（細かい要望に強い）</li>
      </ul>

      <h3>🔴 工務店のデメリット</h3>
      <ul>
        <li>会社ごとに性能・品質の差が大きい</li>
        <li>保証内容がハウスメーカーより短い場合がある</li>
        <li>モデルハウスが少ないこともある</li>
      </ul>
      <p>👉 <strong>「会社選び」が最重要ポイント。</strong></p>

      <h2>③ ハウスメーカーのメリット・デメリット</h2>

      <h3>🔵 ハウスメーカーのメリット</h3>
      <ul>
        <li>保証が長い（安心感がある）</li>
        <li>工場生産で品質が安定</li>
        <li>モデルハウスが多くイメージしやすい</li>
        <li>ブランド力が高い</li>
        <li>営業・アフターの仕組みが整っている</li>
      </ul>

      <h3>🔴 ハウスメーカーのデメリット</h3>
      <ul>
        <li>価格が高い（工務店より200〜500万円差が出ることも）</li>
        <li>自由度が低い（規格が多い）</li>
        <li>地域の気候に完全対応できない場合もある</li>
      </ul>
      <p>👉 <strong>「安心感はあるが、価格は高め」。</strong></p>

      <h2>④ どっちが向いている？タイプ別に解説</h2>

      <h3>🟢 工務店が向いている人</h3>
      <ul>
        <li>予算を抑えたい</li>
        <li>自由な間取り・デザインにしたい</li>
        <li>地元の気候に合った家が欲しい</li>
        <li>担当者と密に相談したい</li>
        <li>若い世帯・初めての家づくり</li>
      </ul>
      <p>👉 <strong>コスパ・自由度・地域性を重視する人は工務店一択。</strong></p>

      <h3>🔵 ハウスメーカーが向いている人</h3>
      <ul>
        <li>ブランド力を重視したい</li>
        <li>長期保証を重視する</li>
        <li>モデルハウスでしっかり確認したい</li>
        <li>工場品質の安定感が欲しい</li>
        <li>予算に余裕がある</li>
      </ul>
      <p>👉 <strong>安心感とブランドを求める人向け。</strong></p>

      <h2>⑤ 実際の満足度はどっちが高い？</h2>
      <p>全国の住宅満足度調査では、<strong>工務店の満足度がハウスメーカーを上回るケースが多い</strong>と言われています。</p>

      <h3>工務店の満足度が高い理由</h3>
      <table>
        <thead>
          <tr><th>理由</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>担当者との距離が近い</td><td>要望が通りやすく、打ち合わせがストレスになりにくい</td></tr>
          <tr><td>要望が通りやすい</td><td>間取り・仕様の細かい調整ができる</td></tr>
          <tr><td>コスパが良い</td><td>同じ予算でより高い性能・仕様を実現しやすい</td></tr>
          <tr><td>地域の気候に合った家</td><td>鳥取の冬・夏・湿気に対応した設計ができる</td></tr>
        </tbody>
      </table>
      <p>特に若い世帯では、<strong>「工務店で良かった」という声が多い</strong>のが特徴。</p>

      <h2>⑥ 迷ったらどう選べばいい？</h2>
      <ol>
        <li><strong>まずは「予算」を決める</strong><br />→ 予算が限られるなら工務店が有利。</li>
        <li><strong>次に「性能」を比較する</strong><br />→ UA値・C値を必ず確認。</li>
        <li><strong>「担当者との相性」を重視する</strong><br />→ 家づくりは1年以上の長い付き合い。</li>
        <li><strong>モデルハウス・見学会に行く</strong><br />→ 実物を見ると判断が早い。</li>
        <li><strong>最後は「総額」で比較する</strong><br />→ 本体価格ではなく"総額"で判断。</li>
      </ol>

      <h2>まとめ</h2>
      <table>
        <thead>
          <tr><th>重視するもの</th><th>おすすめ</th></tr>
        </thead>
        <tbody>
          <tr><td>コスパ・自由度・地域密着</td><td>工務店</td></tr>
          <tr><td>ブランド・保証・安定品質</td><td>ハウスメーカー</td></tr>
          <tr><td>若い世帯・初めての家づくり</td><td>工務店（満足度が高い傾向）</td></tr>
          <tr><td>予算に余裕がある</td><td>ハウスメーカーも選択肢</td></tr>
        </tbody>
      </table>
      <p>どちらが良いかは人によって違いますが、<strong>若い世帯・初めての家づくりでは工務店の満足度が高い傾向</strong>があります。大切なのは、「どちらが優れているか」ではなく<strong>"あなたの価値観に合っているか"</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>次は、転勤などの理由で家を購入するべきかどうか迷っていたら、<br />👉 <a href="/articles/32">「転勤の可能性がある人の家づくり｜売却・賃貸でも困らない住宅会社の選び方」</a>を読むと、判断材料の一つになると思います。</p>
    `
  },
  {
    id: 32,
    step: '03',
    title: "転勤の可能性がある人の家づくり｜売却・賃貸でも困らない住宅会社の選び方",
    category: "パートナーを選ぶ",
    date: "2026年3月27日",
    readTime: "約12分",
    image: "https://public.readdy.ai/ai/img_res/edited_1d09bd6c00d4af9254049d14b11254b8_0fe1a093.jpg",
    excerpt: "「転勤の可能性があっても家を建てていい？」売却・賃貸でも困らない家づくりのポイントを解説。資産価値を左右するのは「性能×証明×メンテナンス」で、工務店でも売れる家は建てられます。",
    tags: ["鳥取", "転勤", "資産価値"],
    content: `
      <h2>はじめに</h2>
      <p>「転勤の可能性があるけど、家を建てても大丈夫？」<br />「将来売るかもしれないし、賃貸に出すかもしれない…」<br />「工務店とハウスメーカー、どっちが有利なの？」</p>
      <p>仕事の都合で転勤の可能性がある人にとって、家づくりは<strong>"資産としての価値"も考える必要があります</strong>。</p>
      <p>ただし、<strong>転勤＝ハウスメーカー一択</strong>というわけではありません。実は、工務店でも"売れる家・貸せる家"を建てることは十分可能です。</p>
      <p>この記事では、転勤の可能性がある人が<strong>後悔しない家づくりのポイント</strong>をわかりやすく解説します。</p>

      <h2>結論</h2>
      <ul>
        <li>売却・賃貸のしやすさは「性能 × 証明 × メンテナンス」で決まる</li>
        <li>工務店でも資産価値の高い家は建てられる</li>
        <li>ブランド力を重視するならハウスメーカーも選択肢</li>
      </ul>
      <p>👉 <strong>転勤の可能性があっても、"売れる家"を建てればどちらでも問題なし。</strong></p>

      <h2>① 転勤の可能性がある人が気にすべきポイントは「資産価値」だけ</h2>
      <p>家を売る・貸すときに重要なのは、<strong>どの会社で建てたかよりも、どんな家を建てたか</strong>です。</p>

      <h3>資産価値を左右する3つのポイント</h3>
      <table>
        <thead>
          <tr><th>ポイント</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>① 性能（断熱・気密・耐震）</td><td>UA値・C値が良い家は中古でも人気</td></tr>
          <tr><td>② 証明（長期優良住宅・ZEHなど）</td><td>書類があると査定が上がる</td></tr>
          <tr><td>③ メンテナンス履歴</td><td>点検記録があると売却時に有利</td></tr>
        </tbody>
      </table>
      <p>つまり、<strong>工務店でも性能の高い家を建てれば資産価値は落ちにくい</strong>のです。</p>

      <h2>② 売却・賃貸のしやすさで比較すると？</h2>

      <h3>🟦 ハウスメーカーの強み</h3>
      <ul>
        <li>ブランド名で検索されやすい</li>
        <li>中古市場での認知度が高い</li>
        <li>保証が長く、買い手が安心しやすい</li>
      </ul>
      <p>👉 売却スピードは速い傾向。</p>

      <h3>🟩 工務店の強み</h3>
      <ul>
        <li>コスパが良い</li>
        <li>性能の高い家を建てやすい</li>
        <li>地元の気候に合った家は中古でも人気</li>
        <li>長期優良住宅なら評価が高い</li>
      </ul>
      <p>👉 <strong>性能が高ければ資産価値は十分残る。</strong></p>

      <h2>③ 転勤の可能性がある人が"工務店で建てる"場合のポイント</h2>
      <p>工務店で建てても、以下を満たせば<strong>売却・賃貸で困ることはほぼない</strong>です。</p>

      <h3>① 長期優良住宅にする</h3>
      <ul>
        <li>耐震・省エネ・劣化対策・維持管理のしやすさが証明される</li>
        <li>中古市場での評価が高い</li>
      </ul>

      <h3>② 断熱性能を高める（UA値・C値）</h3>
      <ul>
        <li>鳥取のような寒冷地では特に重要</li>
        <li>UA値・C値が良いだけで中古の問い合わせが増える</li>
      </ul>

      <h3>③ ZEHにしておく</h3>
      <ul>
        <li>光熱費が安い → 賃貸でも入居者がつきやすい</li>
        <li>太陽光の価値が残る</li>
        <li>若い世帯に人気</li>
      </ul>

      <h3>④ メンテナンス記録を残す</h3>
      <ul>
        <li>点検・修繕・設備交換の記録があると査定が上がる</li>
      </ul>

      <h3>⑤ 間取りは"万人受け"を意識</h3>
      <table>
        <thead>
          <tr><th>売れやすい間取りの特徴</th></tr>
        </thead>
        <tbody>
          <tr><td>4LDK（家族向け）</td></tr>
          <tr><td>回遊動線（暮らしやすさが伝わる）</td></tr>
          <tr><td>収納多め</td></tr>
          <tr><td>南向きリビング</td></tr>
        </tbody>
      </table>
      <p>クセの強い間取りは売却時に不利になります。</p>

      <h2>④ 賃貸に出す場合のポイント</h2>
      <p>賃貸に出す場合は、<strong>性能 × 立地 × 管理会社</strong>が重要です。</p>

      <h3>賃貸で人気の家の特徴</h3>
      <ul>
        <li>断熱性能が高い</li>
        <li>光熱費が安い</li>
        <li>駐車場2台以上</li>
        <li>ファミリー向け間取り</li>
        <li>収納が多い</li>
      </ul>
      <p>工務店の家は"暮らしやすさ"が強みなので、<strong>賃貸でも入居者がつきやすい</strong>のが特徴です。</p>

      <h2>⑤ どっちが向いている？タイプ別に解説</h2>
      <table>
        <thead>
          <tr><th>タイプ</th><th>おすすめ</th></tr>
        </thead>
        <tbody>
          <tr><td>転勤の可能性が高い・売却・賃貸を前提・ブランド価値重視</td><td>ハウスメーカー</td></tr>
          <tr><td>コスパ重視・自由な間取り・性能をしっかり確保したい</td><td>工務店（長期優良住宅取得）</td></tr>
        </tbody>
      </table>

      <h2>まとめ</h2>
      <p>転勤の可能性がある人でも、工務店で<strong>"性能の高い家"を建てれば売却・賃貸は十分可能</strong>です。</p>
      <ul>
        <li>資産価値を決めるのは「性能 × 証明 × メンテナンス」</li>
        <li>ハウスメーカーはブランド力で売却しやすい</li>
        <li>工務店は性能とコスパで資産価値を残しやすい</li>
      </ul>
      <p>どちらを選ぶにしても、<strong>"売れる家"を建てることが最重要</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>次は、住宅会社選びで絶対後悔したくない方へ、<br />👉 <a href="/articles/33">「鳥取で住宅会社の選び方｜後悔しないための7つのチェックポイント」</a>を読むと、満足のできる住宅会社を選ぶことができます。</p>
    `
  },
  {
    id: 33,
    step: '03',
    title: "鳥取で住宅会社の選び方｜後悔しないための7つのチェックポイント",
    category: "パートナーを選ぶ",
    date: "2026年3月27日",
    readTime: "約14分",
    image: "https://public.readdy.ai/ai/img_res/edited_5ed41a00461d7c86dafc866d0c790422_8add847a.jpg",
    excerpt: "「鳥取でどの住宅会社を選べばいい？」断熱性能・気密性能・標準仕様・総額見積り・担当者・保証・地域理解の7つのチェックポイントで、後悔しない住宅会社選びを徹底解説します。",
    tags: ["鳥取", "住宅会社", "チェックポイント"],
    content: `
      <h2>はじめに</h2>
      <p>「鳥取で家を建てたいけど、どの住宅会社を選べばいいのか分からない」<br />「工務店・ハウスメーカー・ローコスト住宅…違いが多すぎて決めきれない」<br />「失敗したくないけど、何から見ればいいの？」</p>
      <p>鳥取は、全国展開のハウスメーカーから地域密着の工務店まで、選択肢が多い"恵まれたエリア"である一方、選び方を間違えると後悔しやすいエリアでもあります。冬の寒さが厳しく、雪・湿気があり、土地は比較的安いという鳥取ならではの条件を考えると、<strong>「なんとなく有名だから」「営業さんが感じ良かったから」だけで決めるのは危険</strong>です。</p>
      <p>この記事では、鳥取で家づくりを考える人が<strong>住宅会社選びで後悔しないための7つのチェックポイント</strong>を、具体例を交えながらわかりやすく解説します。</p>

      <h2>結論：後悔しないための7つのチェックポイント</h2>
      <ol>
        <li>断熱性能（UA値）が明確かどうか</li>
        <li>気密性能（C値）を測っているかどうか</li>
        <li>標準仕様のレベルが高いかどうか</li>
        <li>"総額"で見積もりを出してくれるかどうか</li>
        <li>担当者が信頼できるかどうか</li>
        <li>アフター・保証の中身が現実的かどうか</li>
        <li>鳥取の気候・暮らし方を理解しているかどうか</li>
      </ol>
      <p>👉 <strong>この7つを押さえれば、工務店でもハウスメーカーでも「自分に合った1社」が自然と絞れてきます。</strong></p>

      <h2>① 断熱性能（UA値）が明確かどうか</h2>
      <p>鳥取で家を建てるなら、<strong>断熱性能は"オプション"ではなく"必須条件"</strong>です。</p>

      <h3>なぜ断熱性能が重要なのか</h3>
      <ul>
        <li>冬の寒さが厳しい</li>
        <li>暖房費がかさみやすい</li>
        <li>結露・カビのリスクがある</li>
      </ul>
      <p>断熱性能が低い家は、「建てた瞬間からずっと我慢を強いられる家」になります。</p>

      <h3>UA値の目安</h3>
      <table>
        <thead>
          <tr><th>UA値</th><th>評価</th></tr>
        </thead>
        <tbody>
          <tr><td>0.46以下</td><td>今の基準としては欲しいライン</td></tr>
          <tr><td>0.34以下</td><td>高性能と言えるレベル</td></tr>
          <tr><td>0.26前後</td><td>かなり性能にこだわった家</td></tr>
        </tbody>
      </table>

      <h3>チェックするポイント</h3>
      <ul>
        <li>UA値はいくつか？</li>
        <li>その数値は"標準仕様"で出せるのか？</li>
        <li>説明が"なんとなく暖かいです"で終わっていないか？</li>
      </ul>

      <h2>② 気密性能（C値）を"測っているかどうか"</h2>
      <p>断熱とセットで必ず見るべきなのが<strong>気密性能（C値）</strong>です。</p>

      <h3>なぜC値が大事なのか</h3>
      <p>どれだけ断熱材を入れても、家に隙間が多ければ暖かい空気は逃げ、冷たい空気は入ってきます。すきま風・エアコンの効きにくさ・光熱費の高さはすべて「気密不足」が原因のことが多いです。</p>

      <h3>C値の目安</h3>
      <table>
        <thead>
          <tr><th>C値</th><th>評価</th></tr>
        </thead>
        <tbody>
          <tr><td>1.0以下</td><td>最低限ここは欲しい</td></tr>
          <tr><td>0.5以下</td><td>高気密と言えるレベル</td></tr>
        </tbody>
      </table>

      <h3>チェックするポイント</h3>
      <ul>
        <li>C値を実測しているか？</li>
        <li>平均値や実績を公開しているか？</li>
        <li>"気密測定はしていません"と言われないか？</li>
      </ul>

      <h2>③ 標準仕様のレベルを"細かく"確認する</h2>
      <p>本体価格が安く見えても、<strong>標準仕様がスカスカだと、結局オプションだらけで高くつきます</strong>。</p>

      <h3>標準仕様で必ず確認したい項目</h3>
      <table>
        <thead>
          <tr><th>確認項目</th><th>チェックポイント</th></tr>
        </thead>
        <tbody>
          <tr><td>断熱材の種類・厚み</td><td>高性能グラスウール or 吹付断熱か</td></tr>
          <tr><td>サッシの種類</td><td>樹脂サッシか、アルミ樹脂複合か</td></tr>
          <tr><td>ガラス</td><td>ペアガラスかトリプルガラスか</td></tr>
          <tr><td>換気システム</td><td>第1種 or 第3種か</td></tr>
          <tr><td>外壁材</td><td>サイディングのグレード・塗り壁など</td></tr>
          <tr><td>耐震等級</td><td>等級3が取れるか</td></tr>
        </tbody>
      </table>

      <h3>よくある失敗例</h3>
      <p>「標準仕様で十分です」と言われて契約 → 住んでみたら冬が寒くて、後から内窓や断熱リフォームで余計な出費。</p>
      <p>👉 <strong>"標準仕様のレベル"は、その会社の本気度そのものです。</strong></p>

      <h2>④ 見積もりは"本体価格"ではなく"総額"で比較する</h2>
      <p>住宅会社選びで一番多い失敗が、<strong>「本体価格だけを見て決めてしまうこと」</strong>です。</p>

      <h3>総額に含めておくべきもの</h3>
      <ul>
        <li>本体工事</li>
        <li>付帯工事（給排水・電気・外部配管など）</li>
        <li>諸費用（登記・ローン・火災保険など）</li>
        <li>外構工事</li>
        <li>地盤改良費（見込みでもいいので入れてもらう）</li>
        <li>カーテン・照明</li>
      </ul>

      <h3>要注意パターン</h3>
      <ul>
        <li>「本体価格は安いけど、付帯工事・諸費用が別」</li>
        <li>「外構は別で考えてください」</li>
      </ul>
      <p>→ 結果、500〜800万円くらい平気で膨らむこともあります。</p>

      <h2>⑤ 担当者との相性は"スペック以上に重要"</h2>
      <p>どれだけ会社のスペックが良くても、<strong>担当者との相性が悪いと家づくりはほぼ確実にストレスになります</strong>。</p>

      <h3>良い担当者の特徴</h3>
      <ul>
        <li>こちらの話を遮らずに聞いてくれる</li>
        <li>メリットだけでなくデメリットも話してくれる</li>
        <li>予算の上限を守ろうとしてくれる</li>
        <li>「やめたほうがいい」と言ってくれる場面がある</li>
        <li>レスポンスが早く、約束を守る</li>
      </ul>

      <h3>要注意な担当者</h3>
      <ul>
        <li>とにかく契約を急がせる</li>
        <li>他社の悪口が多い</li>
        <li>「大丈夫です」「問題ないです」で終わらせる</li>
        <li>話が毎回変わる</li>
      </ul>

      <h2>⑥ アフター・保証の"中身"を見る</h2>
      <p>「◯年保証です」と言われても、<strong>何が・どこまで・どの条件で保証されるのかを見ないと意味がありません</strong>。</p>

      <h3>チェックするポイント</h3>
      <table>
        <thead>
          <tr><th>確認項目</th><th>内容</th></tr>
        </thead>
        <tbody>
          <tr><td>構造・防水保証</td><td>何年保証か</td></tr>
          <tr><td>定期点検</td><td>何年目まであるか</td></tr>
          <tr><td>無償対応範囲</td><td>どこまで無償か</td></tr>
          <tr><td>不具合対応</td><td>スピードと対応方針</td></tr>
        </tbody>
      </table>
      <p>地元で長く続いている工務店は、アフターが良いから続いています。逆に、歴史が浅く実績も少ない会社は慎重に見るべきです。</p>

      <h2>⑦ 鳥取の気候・暮らし方を理解しているか</h2>
      <p>鳥取は冬の寒さ・雪・湿気・夏の蒸し暑さと、外気条件がかなり厳しい地域です。</p>

      <h3>地域を理解している会社のサイン</h3>
      <ul>
        <li>日射取得・日射遮蔽を設計に組み込んでいる</li>
        <li>結露リスクを考えた断熱・気密計画をしている</li>
        <li>「とりあえず高気密高断熱」ではなく、バランスを説明できる</li>
      </ul>

      <h3>逆に危ないサイン</h3>
      <ul>
        <li>「全国どこでも同じ仕様です」と言い切る</li>
        <li>「鳥取はそこまで寒くないですよ」と軽く言う</li>
        <li>夏の暑さ・冬の結露について具体的な話が出てこない</li>
      </ul>
      <p>👉 <strong>地域密着の工務店は、ここが最大の強みになるポイントです。</strong></p>

      <h2>よくある"失敗パターン"と、その防ぎ方</h2>
      <table>
        <thead>
          <tr><th>失敗パターン</th><th>防ぎ方</th></tr>
        </thead>
        <tbody>
          <tr><td>価格だけで決めてしまう → 入居後に寒さ・結露・光熱費で後悔</td><td>性能（UA値・C値）と標準仕様を必ず確認する</td></tr>
          <tr><td>営業トークに押されて即決 → 後から後悔</td><td>最低3社は話を聞き、1回は持ち帰る</td></tr>
          <tr><td>担当者との違和感を無視 → 打ち合わせがストレスに</td><td>違和感があれば候補から外す勇気を持つ</td></tr>
        </tbody>
      </table>

      <h2>まとめ</h2>
      <p>鳥取で住宅会社を選ぶときは、次の7つをチェックすれば後悔する可能性は大きく減ります。</p>
      <ol>
        <li>断熱性能（UA値）が明確かどうか</li>
        <li>気密性能（C値）を測っているかどうか</li>
        <li>標準仕様のレベルが高いかどうか</li>
        <li>見積もりを"総額"で出してくれるかどうか</li>
        <li>担当者が信頼できるかどうか</li>
        <li>アフター・保証の中身が現実的かどうか</li>
        <li>鳥取の気候・暮らし方を理解しているかどうか</li>
      </ol>
      <p>この7つを軸に見ていくと、「なんとなく有名だから」ではなく、<strong>「自分たちに合っているから」選べるようになります</strong>。</p>

      <h2>次に読む記事</h2>
      <p>具体的な会社名も知りたい方へ<br />👉 <a href="/articles/28">「鳥取のおすすめ工務店5選｜性能・価格・デザインで比較」</a></p>
      <p>価格を抑えたい人向け<br />👉 <a href="/articles/30">「ローコスト住宅おすすめ5社｜実店舗・展示場がある安心のメーカー」</a></p>
    `
  },
  {
    id: 35,
    step: '02',
    title: "【完全版】家づくりに必要な費用のすべて｜総額・諸費用・見落としがちな費用一覧",
    category: "予算を決める",
    date: "2026年3月27日",
    readTime: "約14分",
    image: "https://public.readdy.ai/ai/img_res/edited_5d4bf96b8066994d26aee45d54ee51c8_91937509.jpg",
    excerpt: "「家づくりって、結局いくらかかるの？」本体価格以外の費用が分からず不安な方へ。付帯工事・諸費用・外構・見落としがちな費用まで、総額を構成する5つをわかりやすく完全解説します。",
    tags: ["費用", "総額", "諸費用"],
    content: `
      <h2>はじめに</h2>
      <p>「家づくりって、結局いくらかかるの？」<br />「本体価格以外にどんな費用が必要なのか分からない…」<br />「見積もりを見ても専門用語ばかりで不安になる…」</p>
      <p>鳥取市・米子市・倉吉市で家づくりを始める20〜30代の方から、こうした悩みを本当によく聞きます。私自身も最初は「本体価格＝家の値段」だと思っていましたが、実際は<strong>総額で＋400〜800万円ほど必要</strong>になることを後から知り、驚いた経験があります。</p>
      <p>この記事では、家づくりに必要な費用を総額・内訳・見落としがちな費用まで<strong>"初心者でも理解できるように"完全解説</strong>します。読み終えるころには、「自分たちの家づくりにいくら必要なのか」「どこで費用が増えやすいのか」が明確になり、後悔しない予算計画ができるようになります。</p>

      <h2>結論：家づくりの総額は「5つの費用」で決まる</h2>
      <p>家づくりの総額は、<strong>本体価格＋付帯工事＋諸費用＋外構＋見落としがちな費用</strong>の5つで決まります。そして、最も失敗しやすいのは<strong>本体価格だけで判断してしまうこと</strong>。総額で比較すれば、住宅会社選びもスムーズになります。</p>

      <h2>家づくりの総額は「5つの費用」で決まる</h2>
      <table>
        <thead>
          <tr><th>費用の種類</th><th>目安金額</th></tr>
        </thead>
        <tbody>
          <tr><td>① 本体工事費</td><td>1,800〜2,800万円</td></tr>
          <tr><td>② 付帯工事費</td><td>100〜300万円</td></tr>
          <tr><td>③ 諸費用</td><td>150〜250万円</td></tr>
          <tr><td>④ 外構工事費</td><td>100〜200万円</td></tr>
          <tr><td>⑤ 見落としがちな費用</td><td>50〜150万円</td></tr>
          <tr><td><strong>合計（総額）</strong></td><td><strong>2,200〜3,700万円</strong></td></tr>
        </tbody>
      </table>
      <p>👉 鳥取で30坪の家を建てる場合、<strong>総額1,900〜2,700万円が現実的なライン</strong>です。</p>

      <h2>① 本体工事費｜建物そのものの費用</h2>
      <p>本体工事費には、以下が含まれます。</p>
      <ul>
        <li>基礎工事</li>
        <li>木工事</li>
        <li>屋根・外壁</li>
        <li>内装</li>
        <li>キッチン・お風呂・トイレ</li>
        <li>電気・給排水工事</li>
      </ul>

      <h3>本体価格で失敗しやすい理由</h3>
      <ul>
        <li>会社によって「含まれる範囲」が違う</li>
        <li>標準仕様が弱いとオプションだらけになる</li>
        <li>モデルハウスは豪華仕様で参考にならない</li>
      </ul>
      <p>私自身も「本体価格が安い会社」を選びかけましたが、標準仕様が弱く、オプションで総額が跳ね上がることを知り断念しました。</p>
      <p>👉 <strong>本体価格だけで比較すると必ず後悔します。</strong></p>

      <h2>② 付帯工事費｜見落としがちな追加費用</h2>
      <p>付帯工事費は、家を建てるために必要な"外側の工事"です。</p>

      <h3>主な項目</h3>
      <ul>
        <li>外部給排水工事</li>
        <li>電気引き込み</li>
        <li>ガス工事</li>
        <li>仮設工事（足場・仮設トイレ）</li>
        <li>地盤調査</li>
        <li>地盤改良（必要な場合）</li>
        <li>解体工事（建て替えの場合）</li>
      </ul>

      <h3>鳥取で特に注意すべき点</h3>
      <p>鳥取市・米子市は地盤が弱いエリアもあり、<strong>地盤改良で＋50〜150万円かかるケースが多い</strong>です。</p>
      <p>👉 見積もりに「地盤改良：別途」と書かれていたら要注意。</p>

      <h2>③ 諸費用｜家づくりの"事務的な費用"</h2>
      <p>諸費用は、家を建てるための手続きに必要な費用です。</p>

      <h3>主な項目</h3>
      <ul>
        <li>登記費用</li>
        <li>ローン手数料</li>
        <li>火災保険</li>
        <li>水道加入金</li>
        <li>印紙代</li>
        <li>引っ越し費用</li>
        <li>地鎮祭・上棟式（任意）</li>
      </ul>

      <h3>注意点</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>費用の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>火災保険（10年一括）</td><td>35〜45万円</td></tr>
          <tr><td>ローン手数料</td><td>銀行によって大きく違う（5〜50万円）</td></tr>
          <tr><td>地鎮祭・上棟式（任意）</td><td>合計5〜10万円</td></tr>
        </tbody>
      </table>
      <p>👉 <strong>諸費用は"見積もりに入っていないことが多い"ので要注意。</strong></p>

      <h2>④ 外構工事費｜駐車場・庭・フェンス</h2>
      <p>外構は後回しにされがちですが、<strong>生活に直結する重要な工事</strong>です。</p>

      <h3>主な項目</h3>
      <ul>
        <li>駐車場コンクリート（30〜60万円）</li>
        <li>アプローチ（20〜40万円）</li>
        <li>フェンス（20〜50万円）</li>
        <li>砂利敷き（10〜20万円）</li>
        <li>ウッドデッキ（20〜50万円）</li>
        <li>植栽（10〜30万円）</li>
      </ul>

      <h3>よくある失敗</h3>
      <ul>
        <li>外構を後回しにして予算が足りず、砂利だけで終わる</li>
        <li>駐車場が狭くて使いにくい</li>
        <li>フェンスがなくてプライバシーが確保できない</li>
      </ul>
      <p>👉 <strong>外構は最初から予算に入れておくべき。</strong></p>

      <h2>⑤ 見落としがちな費用｜初心者が最も失敗する部分</h2>
      <p>以下は、見積もりに入っていないことが多い"落とし穴"です。</p>
      <table>
        <thead>
          <tr><th>項目</th><th>費用の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>カーテン</td><td>10〜30万円</td></tr>
          <tr><td>照明</td><td>10〜20万円</td></tr>
          <tr><td>エアコン（1台）</td><td>10〜20万円</td></tr>
          <tr><td>テレビアンテナ</td><td>3〜10万円</td></tr>
          <tr><td>ネット工事</td><td>1〜3万円</td></tr>
          <tr><td>家具・家電の買い替え</td><td>20〜100万円</td></tr>
          <tr><td>引っ越し費用</td><td>5〜20万円</td></tr>
        </tbody>
      </table>
      <p>👉 <strong>これらを入れると総額はさらに＋50〜150万円。</strong></p>

      <h2>まとめ</h2>
      <p>家づくりの費用は、<strong>本体価格だけでは絶対に判断できません</strong>。</p>
      <ul>
        <li>本体工事費</li>
        <li>付帯工事費</li>
        <li>諸費用</li>
        <li>外構工事費</li>
        <li>見落としがちな費用</li>
      </ul>
      <p>これらをすべて含めて、<strong>総額で比較することが後悔しない家づくりの第一歩</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>家づくりの総額が分かったら、次に気になるのが「どの住宅ローンを選べば損しないのか？」という点だと思います。金利タイプの違いを理解しておくと、総返済額が数百万円変わることもあります。</p>
      <p>👉 <a href="/articles/36">「【保存版】住宅ローンの金利タイプを徹底比較｜変動・固定・2段階固定・フラット35」</a>を読むと、あなたに最適なローンが見つかり、家づくりの不安が一気に減ります。</p>
    `
  },
  {
    id: 36,
    step: '02',
    title: "【保存版】住宅ローンの金利タイプを徹底比較｜変動・固定・2段階固定・フラット35",
    category: "予算を決める",
    date: "2026年3月27日",
    readTime: "約14分",
    image: "https://public.readdy.ai/ai/img_res/edited_5ed41a00461d7c86dafc866d0c790422_9a106dc1.jpg",
    excerpt: "「住宅ローンってどれを選べばいいの？」変動・固定・2段階固定・フラット35の4タイプを初心者でも理解できるように徹底比較。鳥取の若い世帯に多い選択肢と、後悔しないローン選びのポイントを解説します。",
    tags: ["住宅ローン", "金利", "フラット35"],
    content: `
      <h2>はじめに</h2>
      <p>「住宅ローンってどれを選べばいいの？」<br />「変動と固定、どっちが得なのか分からない…」<br />「銀行の説明が難しくて理解できない…」</p>
      <p>鳥取市・米子市・倉吉市で家づくりを始めた20〜30代の方から、こうした悩みを本当に多く聞きます。私自身も最初は"なんとなく変動でいいのかな？"と思っていましたが、金利タイプによって<strong>総返済額が数百万円変わる</strong>ことを知り、真剣に調べ直した経験があります。</p>
      <p>この記事では、住宅ローンの金利タイプ「変動金利・固定金利・2段階固定・フラット35」を初心者でも理解できるように徹底比較します。</p>

      <h2>結論</h2>
      <p>金利タイプは<strong>"家計の安定性"と"リスク許容度"で選ぶのが正解</strong>です。</p>
      <table>
        <thead>
          <tr><th>優先すること</th><th>おすすめ金利タイプ</th></tr>
        </thead>
        <tbody>
          <tr><td>安さ重視</td><td>変動金利</td></tr>
          <tr><td>返済額を固定したい</td><td>固定金利</td></tr>
          <tr><td>最初の10年だけ安くしたい</td><td>2段階固定</td></tr>
          <tr><td>長期の安心と審査の通りやすさ</td><td>フラット35</td></tr>
        </tbody>
      </table>
      <p>特に鳥取の若い世帯は、<strong>変動金利 or フラット35</strong>を選ぶケースが多いです。</p>

      <h2>変動金利｜最も安いが"金利上昇リスク"あり</h2>
      <p>変動金利は、金利が半年ごとに見直されるタイプです。</p>

      <h3>メリット</h3>
      <ul>
        <li>金利が最も安い</li>
        <li>月々の返済額が低く抑えられる</li>
        <li>若い世帯に人気</li>
      </ul>

      <h3>デメリット</h3>
      <ul>
        <li>将来金利が上がる可能性がある</li>
        <li>返済額が増えるリスクがある</li>
        <li>長期的な予測が難しい</li>
      </ul>

      <h3>鳥取で変動金利が選ばれる理由</h3>
      <p>鳥取市・米子市は土地が比較的安く、総借入額が少ないため<strong>金利上昇リスクを許容しやすい</strong>という背景があります。借入額が少なければ、金利が上がっても月々の増加分が抑えられます。</p>

      <h2>固定金利｜返済額がずっと変わらない安心タイプ</h2>
      <p>固定金利は、借入時の金利が返済終了まで変わりません。</p>

      <h3>メリット</h3>
      <ul>
        <li>返済額がずっと一定</li>
        <li>家計管理がしやすい</li>
        <li>金利上昇の影響を受けない</li>
      </ul>

      <h3>デメリット</h3>
      <ul>
        <li>変動金利より金利が高い</li>
        <li>借入額が大きいと総返済額が増える</li>
      </ul>

      <h3>こんな人におすすめ</h3>
      <ul>
        <li>共働きで収入が安定している</li>
        <li>将来の金利上昇が不安</li>
        <li>長期的な安心を優先したい</li>
      </ul>

      <h2>2段階固定｜最初の10年だけ安くしたい人向け</h2>
      <p>2段階固定は、<strong>最初の10年は固定金利 → その後は変動金利</strong>という仕組みです。</p>

      <h3>メリット</h3>
      <ul>
        <li>最初の10年は返済額が安い</li>
        <li>固定金利より金利が低い</li>
        <li>子育て初期の負担を減らせる</li>
      </ul>

      <h3>デメリット</h3>
      <ul>
        <li>10年後に金利が上がる可能性</li>
        <li>将来の返済額が読みにくい</li>
      </ul>

      <h3>鳥取での利用例</h3>
      <p>米子市の子育て世帯が「子どもが小さい10年間だけ返済を抑えたい」という理由で選ぶケースが多いです。</p>

      <h2>フラット35｜長期固定＋審査が通りやすい</h2>
      <p>フラット35は、<strong>35年間ずっと金利が変わらない国の住宅ローン</strong>です。</p>

      <h3>メリット</h3>
      <ul>
        <li>返済額がずっと一定</li>
        <li>審査が比較的通りやすい</li>
        <li>団信（団体信用生命保険）が選べる</li>
        <li>中古住宅にも使いやすい</li>
      </ul>

      <h3>デメリット</h3>
      <ul>
        <li>金利は変動より高い</li>
        <li>手数料が高い場合がある</li>
      </ul>

      <h3>鳥取で人気の理由</h3>
      <ul>
        <li>自営業の方でも通りやすい</li>
        <li>長期の安心を重視する人が多い</li>
        <li>地方銀行より審査が柔軟</li>
      </ul>

      <h2>金利タイプの選び方｜初心者はここを見る</h2>
      <table>
        <thead>
          <tr><th>判断基準</th><th>おすすめ</th></tr>
        </thead>
        <tbody>
          <tr><td>収入が安定している</td><td>固定 or フラット35</td></tr>
          <tr><td>収入が増える見込みがある</td><td>変動</td></tr>
          <tr><td>リスクを取りたくない</td><td>固定</td></tr>
          <tr><td>少しのリスクは許容できる</td><td>変動</td></tr>
          <tr><td>借入額が大きい</td><td>固定のほうが安心</td></tr>
          <tr><td>借入額が少ない（鳥取は多い）</td><td>変動でもOK</td></tr>
          <tr><td>子どもが小さい（子育て初期）</td><td>2段階固定</td></tr>
          <tr><td>長期的な安定を重視</td><td>フラット35</td></tr>
        </tbody>
      </table>

      <h2>住宅ローンは"銀行比較"が最重要</h2>
      <p>金利タイプを決めたら、次は<strong>銀行比較</strong>が必須です。銀行によって金利・手数料・団信の内容が大きく違うため、必ず複数社を比較してください。</p>

      <h3>鳥取で比較すべき金融機関</h3>
      <ul>
        <li>鳥取銀行</li>
        <li>山陰合同銀行</li>
        <li>JAバンク</li>
        <li>ネット銀行（楽天・auじぶん・住信SBI）</li>
        <li>フラット35取扱金融機関</li>
      </ul>
      <p>👉 <strong>比較するだけで総返済額が100〜300万円変わることもあります。</strong></p>

      <h2>まとめ</h2>
      <p>住宅ローンの金利タイプは、変動・固定・2段階固定・フラット35の4種類。</p>
      <table>
        <thead>
          <tr><th>金利タイプ</th><th>向いている人</th></tr>
        </thead>
        <tbody>
          <tr><td>変動金利</td><td>安さ重視・借入額が少ない</td></tr>
          <tr><td>固定金利</td><td>安心重視・収入が安定</td></tr>
          <tr><td>2段階固定</td><td>子育て初期を楽にしたい</td></tr>
          <tr><td>フラット35</td><td>長期の安定・審査に不安がある</td></tr>
        </tbody>
      </table>
      <p>鳥取で家づくりをする20〜30代の方は、<strong>変動金利 or フラット35</strong>を選ぶケースが多いです。</p>

      <h2>次に読む記事</h2>
      <p>住宅ローンの金利タイプが決まると、次に準備すべきなのが<strong>火災保険・地震保険</strong>です。鳥取県は雪災・風災・地震のリスクもあるため、「どの補償を選ぶべきか？」と悩む方がとても多いです。</p>
      <p>👉 「【完全版】火災保険・地震保険の選び方｜補償内容・相場・注意点を初心者向けに解説」を読むと、家を守るために必要な保険が明確になります。</p>
    `
  },
  {
    id: 37,
    step: '02',
    title: "【完全版】火災保険・地震保険の選び方｜補償内容・相場・注意点を初心者向けに解説",
    category: "予算を決める",
    date: "2026年3月27日",
    readTime: "約14分",
    image: "https://public.readdy.ai/ai/img_res/edited_910de8159748b56776ec58106137d546_4eaed5d0.jpg",
    excerpt: "「火災保険ってどれを選べばいいの？」「地震保険は入るべき？」鳥取県は雪・風・水災・地震のリスクがあるため選び方が重要。補償内容・相場・注意点を初心者向けにわかりやすく解説します。",
    tags: ["火災保険", "地震保険", "保険選び"],
    content: `
      <h2>はじめに</h2>
      <p>「火災保険ってどれを選べばいいの？」<br />「地震保険は入るべき？それとも不要？」<br />「補償内容が多すぎて、どれが必要なのか分からない…」</p>
      <p>鳥取市・米子市・倉吉市で家づくりを進める20〜30代の方から、こうした悩みを本当に多く聞きます。私自身も、家づくりの終盤で火災保険の説明を受けたとき、専門用語だらけで「正直よく分からない…」と感じた経験があります。</p>
      <p>しかし、火災保険・地震保険は<strong>家を守る"最後の砦"であり、選び方を間違えると数十万円の損につながる</strong>とても重要な項目です。この記事では、初心者でも理解できるように火災保険・地震保険の基本・選び方・相場・注意点をわかりやすく解説します。</p>

      <h2>結論</h2>
      <p>火災保険は<strong>"必要な補償だけを選ぶ"ことが最も重要</strong>です。そして、鳥取県に住むなら<strong>地震保険は加入しておくほうが安心</strong>です。</p>
      <ul>
        <li>火災保険は補償を盛りすぎると無駄に高くなる</li>
        <li>鳥取県は地震リスクが低くはない（山陰沖地震など）</li>
        <li>火災保険だけでは地震による損害は1円も出ない</li>
        <li>住宅ローン利用者は加入がほぼ必須</li>
      </ul>

      <h2>火災保険とは？｜火災だけでなく"自然災害全般"を補償する保険</h2>
      <p>火災保険は、火事だけでなく<strong>風災・水災・雪災・盗難・破損</strong>など幅広いトラブルを補償します。</p>

      <h3>火災保険で補償される主な内容</h3>
      <table>
        <thead>
          <tr><th>補償内容</th><th>鳥取での重要度</th></tr>
        </thead>
        <tbody>
          <tr><td>火災・落雷</td><td>★★★（必須）</td></tr>
          <tr><td>風災（台風・強風）</td><td>★★★（必須）</td></tr>
          <tr><td>雪災</td><td>★★★（鳥取は雪が多い）</td></tr>
          <tr><td>水災（河川氾濫・土砂崩れ）</td><td>★★（地域による）</td></tr>
          <tr><td>盗難・ガラス破損</td><td>★★（検討推奨）</td></tr>
          <tr><td>給排水設備の故障による水漏れ</td><td>★★（子育て世帯は必須）</td></tr>
        </tbody>
      </table>
      <p>鳥取県は雪・風・水災のリスクが高いため、<strong>火災保険は必須レベル</strong>と言えます。</p>

      <h2>火災保険の選び方｜初心者が見るべきポイントは3つだけ</h2>

      <h3>① 補償範囲（必要なものだけ選ぶ）</h3>
      <p>火災保険は"選べる補償"が多いため、<strong>不要な補償を外すだけで数万円安くなる</strong>ことがあります。</p>
      <table>
        <thead>
          <tr><th>鳥取で必要な補償</th><th>鳥取で不要になりやすい補償</th></tr>
        </thead>
        <tbody>
          <tr><td>火災・風災・雪災</td><td>個人賠償責任（自動車保険で付けられる）</td></tr>
          <tr><td>水災（地域による）</td><td>類焼損害（火災保険でカバーされることが多い）</td></tr>
          <tr><td>破損・汚損（子育て世帯は必須）</td><td>　</td></tr>
        </tbody>
      </table>

      <h3>② 保険期間（10年一括が最もお得）</h3>
      <table>
        <thead>
          <tr><th>更新タイプ</th><th>特徴</th></tr>
        </thead>
        <tbody>
          <tr><td>1年更新</td><td>最も高い</td></tr>
          <tr><td>5年更新</td><td>中間</td></tr>
          <tr><td>10年更新</td><td>最安（最もお得）</td></tr>
        </tbody>
      </table>
      <p>住宅ローンを組む人は、<strong>10年一括＋地震保険5年更新</strong>が一般的です。</p>

      <h3>③ 保険会社の比較（必ず3社以上）</h3>
      <p>火災保険は会社によって同じ補償でも<strong>2〜5万円の差</strong>が出ます。比較すべき会社は以下の通りです。</p>
      <ul>
        <li>東京海上日動</li>
        <li>損保ジャパン</li>
        <li>三井住友海上</li>
        <li>楽天損保</li>
        <li>SBI損保</li>
      </ul>
      <p>👉 <strong>ネット系は安い、代理店系は手厚い</strong>という特徴があります。</p>

      <h2>地震保険とは？｜火災保険では補償されない"地震専用"の保険</h2>
      <p>地震保険は、<strong>地震・噴火・津波による損害を補償する保険</strong>です。火災保険だけでは、地震による火災・倒壊は1円も出ません。</p>

      <h3>鳥取県は地震リスクが低くない</h3>
      <p>鳥取県は過去に複数の大きな地震が発生しています。</p>
      <table>
        <thead>
          <tr><th>地震名</th><th>規模</th><th>年</th></tr>
        </thead>
        <tbody>
          <tr><td>鳥取県西部地震</td><td>M7.3</td><td>2000年</td></tr>
          <tr><td>鳥取県中部地震</td><td>M6.6</td><td>2016年</td></tr>
          <tr><td>鳥取県西部地震</td><td>M6.1</td><td>2018年</td></tr>
        </tbody>
      </table>
      <p>👉 そのため、<strong>地震保険は"加入推奨"</strong>です。</p>

      <h2>地震保険の選び方｜補償額と期間がポイント</h2>

      <h3>① 補償額は"火災保険の30〜50%"</h3>
      <p>地震保険は、火災保険の30〜50%までしか補償できません。</p>
      <p>例：火災保険2,000万円 → 地震保険は600〜1,000万円</p>

      <h3>② 保険期間は5年更新が最もお得</h3>
      <table>
        <thead>
          <tr><th>更新タイプ</th><th>特徴</th></tr>
        </thead>
        <tbody>
          <tr><td>1年更新</td><td>高い</td></tr>
          <tr><td>5年更新</td><td>安い（最もコスパが良い）</td></tr>
        </tbody>
      </table>

      <h2>火災保険・地震保険の相場（鳥取県の平均）</h2>
      <p>鳥取県で30坪の家を建てた場合の相場です。</p>
      <table>
        <thead>
          <tr><th>保険の種類</th><th>保険期間</th><th>相場金額</th></tr>
        </thead>
        <tbody>
          <tr><td>火災保険</td><td>10年一括</td><td>25〜35万円</td></tr>
          <tr><td>地震保険</td><td>5年更新</td><td>10〜15万円</td></tr>
          <tr><td><strong>合計</strong></td><td>　</td><td><strong>35〜50万円</strong></td></tr>
        </tbody>
      </table>
      <p>※近年自然災害が増加しており、建築資材・修理費も高騰しているため、昔よりもかなり高くなっています。</p>

      <h2>よくある失敗と注意点</h2>

      <h3>① 補償を盛りすぎて高額になる</h3>
      <p>→ 必要な補償だけに絞ること。</p>

      <h3>② 水災を外してしまう</h3>
      <p>→ 鳥取市・倉吉市は河川が多く、浸水リスクがある。</p>

      <h3>③ 住宅会社の"言われるがまま"に加入</h3>
      <p>→ 自分で比較したほうが安くなることが多い。</p>

      <h2>まとめ</h2>
      <p>火災保険・地震保険は、<strong>家を守るための最重要項目</strong>です。</p>
      <ul>
        <li>火災保険は必要な補償だけ選ぶ</li>
        <li>地震保険は鳥取県なら加入推奨</li>
        <li>10年一括（火災）＋5年更新（地震）が最もお得</li>
        <li>保険会社は必ず3社以上比較する</li>
      </ul>
      <p>これだけで、<strong>数万円〜十数万円の節約</strong>が可能になります。</p>

      <h2>次に読む記事</h2>
      <p>火災保険・地震保険の準備ができたら、次に気になるのは「家づくりの総額」だと思います。保険も含めた総額を把握しておくと、住宅ローンの返済計画が一気に立てやすくなります。</p>
      <p>👉 <a href="/articles/35">「【完全版】家づくりに必要な費用のすべて｜総額・諸費用・見落としがちな費用一覧」</a>を読むと、家づくりの全体像がよりクリアになります。</p>
    `
  },
  {
    id: 41,
    step: '02',
    title: "初心者でもわかる！住宅ローンの選び方・審査のコツ",
    category: "予算を決める",
    date: "2026年3月28日",
    readTime: "約16分",
    image: "https://public.readdy.ai/ai/img_res/edited_acd4aceb2b1e380a82f361386e6e8e73_fb797b0a.jpg",
    excerpt: "「住宅ローンってどう選べばいいの？」「変動？固定？銀行も多すぎて分からない…」鳥取市・米子市・倉吉市で家づくりを始める20〜30代の方へ。金利タイプ×銀行比較×審査対策の3つを押さえれば失敗しません。",
    tags: ["住宅ローン", "選び方", "審査のコツ"],
    content: `
      <h2>はじめに</h2>
      <p>「住宅ローンってどう選べばいいの？」<br />「変動？固定？銀行も多すぎて分からない…」<br />「審査に落ちたらどうしよう…」</p>
      <p>鳥取市・米子市・倉吉市で家づくりを始める20〜30代の方から、こうした不安を本当に多く聞きます。私自身も家づくりの初期は、住宅ローンの仕組みが難しく感じて「とりあえず銀行に行けばいいのかな…？」と迷っていました。</p>
      <p>しかし、住宅ローンは <strong>選び方と審査のコツを知るだけで総返済額が数百万円変わる</strong> とても重要なポイントです。</p>
      <p>この記事では、初心者でも迷わず選べるように<strong>住宅ローンの選び方・審査のコツをわかりやすく解説</strong>します。</p>
      <p>読み終えるころには、「どのローンが自分に合うのか」「審査に通りやすくするには何をすべきか」が明確になり、安心して家づくりを進められるようになります。</p>

      <h2>結論</h2>
      <p>結論として、住宅ローンは<strong>金利タイプ × 銀行比較 × 審査対策</strong>の3つを押さえれば失敗しません。</p>
      <ul>
        <li>金利タイプは"家計の安定性"で選ぶ</li>
        <li>銀行は必ず3社以上比較する</li>
        <li>審査は「信用情報・返済比率・勤続年数」が重要</li>
      </ul>
      <p>特に鳥取県の若い世帯は、<strong>変動金利 or フラット35</strong> を選ぶケースが多いです。</p>

      <h2>住宅ローンの選び方｜初心者は3つだけ押さえればOK</h2>
      <p>住宅ローンは複雑に見えますが、初心者が見るべきポイントは次の3つだけです。</p>

      <h3>① 金利タイプを選ぶ（変動・固定・フラット35）</h3>
      <p>住宅ローンの金利タイプは大きく4種類。</p>
      <table>
        <thead>
          <tr><th>金利タイプ</th><th>特徴</th><th>向いている人</th></tr>
        </thead>
        <tbody>
          <tr><td>変動金利</td><td>最も低い・半年ごとに見直し</td><td>安さ重視・借入額が少ない</td></tr>
          <tr><td>固定金利</td><td>返済額がずっと一定</td><td>安心重視・収入が安定している</td></tr>
          <tr><td>2段階固定</td><td>最初の10年だけ固定</td><td>子育て初期を楽にしたい</td></tr>
          <tr><td>フラット35</td><td>35年間完全固定</td><td>長期安定・審査に不安がある人</td></tr>
        </tbody>
      </table>
      <p><strong>鳥取で人気なのは？</strong></p>
      <ul>
        <li>変動金利（若い世帯・借入額が少ないケース）</li>
        <li>フラット35（自営業・審査が不安な人）</li>
      </ul>

      <h3>② 銀行を比較する（3社以上が必須）</h3>
      <p>銀行によって<strong>金利・手数料・団信（保険）の内容が大きく違う</strong>ため、比較は必須です。</p>
      <p><strong>比較すべき銀行の例</strong></p>
      <ul>
        <li>鳥取銀行</li>
        <li>山陰合同銀行</li>
        <li>JAバンク</li>
        <li>ネット銀行（住信SBI・楽天・auじぶん）</li>
        <li>フラット35取扱金融機関</li>
      </ul>
      <p>👉 比較するだけで総返済額が100〜300万円変わることもあります。</p>

      <h3>③ 総返済額で比較する（本当に重要）</h3>
      <p>初心者がやりがちな失敗が「金利だけ」で選んでしまうこと。本当に見るべきは<strong>総返済額（35年間の合計）</strong>です。</p>
      <ul>
        <li>金利</li>
        <li>手数料</li>
        <li>団信</li>
        <li>保証料</li>
      </ul>
      <p>これらを含めて比較すると、「安いと思っていた銀行が実は高い」ということがよくあります。</p>

      <h2>住宅ローン審査のコツ｜初心者でも通りやすくする方法</h2>
      <p>審査は「運」ではなく、見るポイントが決まっています。</p>

      <h3>① 返済比率（年収に対する返済額）</h3>
      <p>返済比率とは<strong>年収に対して返済額がどれくらいか</strong>を示す数字。</p>
      <table>
        <thead>
          <tr><th>金融機関</th><th>返済比率の目安</th></tr>
        </thead>
        <tbody>
          <tr><td>銀行</td><td>25〜35%</td></tr>
          <tr><td>フラット35</td><td>30〜35%</td></tr>
        </tbody>
      </table>
      <p>返済比率が高いと審査に落ちやすくなります。</p>

      <h3>② 信用情報（クレジット履歴）</h3>
      <p>審査で最も重要なのが信用情報。以下があると審査に不利になります。</p>
      <ul>
        <li>クレジットカードの延滞</li>
        <li>スマホの分割払いの滞納</li>
        <li>奨学金の延滞</li>
        <li>リボ払いの残高が多い</li>
      </ul>
      <p>特にスマホの滞納は多く、「それだけで審査に落ちる」ケースもあります。</p>

      <h3>③ 勤続年数（1年以上が目安）</h3>
      <p>銀行は「安定した収入」を重視します。</p>
      <ul>
        <li>勤続1年以上 → 審査が通りやすい</li>
        <li>転職直後 → 審査が厳しくなることも</li>
      </ul>
      <p>ただし、フラット35は勤続年数の条件が緩めです。</p>

      <h3>④ 借入を減らす（車のローン・カードの枠）</h3>
      <p>審査前にやっておくと有利なこと。</p>
      <ul>
        <li>車のローンを完済する</li>
        <li>クレジットカードの枚数を減らす</li>
        <li>カードのキャッシング枠を0円にする</li>
      </ul>
      <p>これだけで審査が通りやすくなります。</p>

      <h2>初心者がやりがちな失敗と注意点</h2>

      <h3>① 金利だけで選んでしまう</h3>
      <p>→ 総返済額で比較しないと損する。</p>

      <h3>② 銀行を1社しか見ない</h3>
      <p>→ 比較しないと100万円以上損することも。</p>

      <h3>③ 審査前に大きな買い物をする</h3>
      <p>→ 車のローン・家電の分割払いはNG。</p>

      <h3>④ 住宅会社の言われるままに銀行を決める</h3>
      <p>→ 自分で比較したほうが安くなることが多い。</p>

      <h2>住宅ローン選びは"住宅会社選び"にも影響する</h2>
      <p>実は、住宅ローンの選び方は<strong>住宅会社選びにも直結</strong>します。</p>
      <ul>
        <li>高性能住宅 → フラット35が有利</li>
        <li>ローコスト住宅 → 変動金利が選ばれやすい</li>
        <li>建売 → 銀行が指定されることもある</li>
      </ul>
      <p>👉 住宅会社を比較することで、ローンの選択肢も広がります。</p>

      <h2>まとめ</h2>
      <p>住宅ローンは<strong>金利タイプ × 銀行比較 × 審査対策</strong>の3つを押さえれば失敗しません。</p>
      <ul>
        <li>金利タイプは家計の安定性で選ぶ</li>
        <li>銀行は3社以上比較する</li>
        <li>審査は「返済比率・信用情報・勤続年数」が重要</li>
        <li>総返済額で比較することが最も大切</li>
      </ul>
      <p>初心者でも、このポイントを押さえるだけで安心して家づくりを進められます。</p>

      <h2>次に読む記事</h2>
      <p>住宅ローンの選び方が分かったら、次に気になるのは「火災保険・地震保険はどう選べばいいの？」という点だと思います。住宅ローンを組む場合、火災保険は必須で、補償内容によっては総返済額にも影響します。</p>
      <p>次は👉 <a href="/articles/37">【完全版】火災保険・地震保険の選び方｜補償内容・相場・注意点を初心者向けに解説</a>を読むと、家を守るために必要な保険が明確になり、家づくりの不安が一気に解消します。</p>
    `
  },
  {
    id: 38,
    step: '02',
    title: "【2026年最新】家づくりの値引き交渉術｜知らないと損するポイントまとめ",
    category: "予算を決める",
    date: "2026年3月27日",
    readTime: "約13分",
    image: "https://public.readdy.ai/ai/img_res/edited_acd4aceb2b1e380a82f361386e6e8e73_a3242d85.jpg",
    excerpt: "「住宅会社って値引きできるの？」「いつ交渉すればいいのか分からない…」2026年最新事情を踏まえ、初心者でも失敗せずにできる値引き交渉術をまとめました。タイミング×比較×相談ベースの3つで数十万円変わります。",
    tags: ["値引き", "交渉術", "節約"],
    content: `
      <h2>はじめに</h2>
      <p>「住宅会社って値引きできるの？」<br />「どのタイミングで交渉すればいいのか分からない…」<br />「強く言うのは苦手だけど、少しでも安くしたい…」</p>
      <p>鳥取市・米子市・倉吉市で家づくりを始めた20〜30代の方から、こうした相談を本当によく聞きます。私自身も家づくりの初期は「値引きなんてできないだろう」と思っていましたが、実際は<strong>交渉の仕方とタイミング次第で数十万円〜100万円以上変わる</strong>ことを知り、驚いた経験があります。</p>
      <p>この記事では、2026年の最新事情を踏まえながら<strong>初心者でも失敗せずにできる値引き交渉術</strong>をわかりやすくまとめました。</p>

      <h2>結論</h2>
      <p>家づくりの値引きは<strong>"タイミング × 比較 × 交渉の仕方"の3つで決まります</strong>。</p>
      <ul>
        <li>タイミングは契約直前が最も強い</li>
        <li>比較（相見積もり）があると交渉が通りやすい</li>
        <li>本体価格よりオプション・付帯工事のほうが値引きされやすい</li>
      </ul>
      <p>そして、最も重要なのは<strong>「値引きしてください」ではなく"相談ベース"で伝えること</strong>です。</p>

      <h2>家づくりの値引きは"タイミング"で決まる</h2>
      <p>値引き交渉は、タイミングを間違えるとほぼ通りません。逆に、正しいタイミングなら初心者でも成功しやすいです。</p>

      <h3>① 最も値引きが通りやすいのは「契約直前」</h3>
      <p>住宅会社が最も契約を取りたいタイミングは<strong>「契約直前」</strong>です。理由はシンプルで、営業担当は「今月の契約数」を追っているから。契約直前に「総額が予算を少し超えてしまって…」と相談すると、値引きが入りやすくなります。</p>

      <h3>② 決算期（3月・9月）は狙い目</h3>
      <p>鳥取の工務店・ハウスメーカーも例外ではなく、<strong>決算期は契約数を伸ばしたい時期</strong>です。</p>
      <table>
        <thead>
          <tr><th>時期</th><th>値引きのしやすさ</th></tr>
        </thead>
        <tbody>
          <tr><td>3月（年度末）</td><td>★★★（最も通りやすい）</td></tr>
          <tr><td>9月（中間決算）</td><td>★★★（次に通りやすい）</td></tr>
          <tr><td>その他の月</td><td>★★（契約直前なら可能）</td></tr>
        </tbody>
      </table>

      <h3>③ 完成見学会の直後もチャンス</h3>
      <p>見学会で来場者が多いと、「今月中に契約を取りたい」という心理が働きます。見学会後の打ち合わせで「予算が少し厳しくて…」と伝えると、調整してくれることが多いです。</p>

      <h2>値引きしやすい項目・しにくい項目</h2>

      <h3>値引きしやすい項目</h3>
      <table>
        <thead>
          <tr><th>値引きしやすい項目</th><th>値引きしにくい項目</th></tr>
        </thead>
        <tbody>
          <tr><td>オプション（キッチン・設備）</td><td>本体価格（特に工務店）</td></tr>
          <tr><td>付帯工事（外部配管・電気工事）</td><td>標準仕様のグレードアップ</td></tr>
          <tr><td>外構工事</td><td>断熱・気密など性能部分</td></tr>
          <tr><td>家具・照明のサービス</td><td>　</td></tr>
          <tr><td>太陽光の値引き</td><td>　</td></tr>
        </tbody>
      </table>
      <p>👉 <strong>本体価格よりも"周辺費用"のほうが調整しやすい</strong>です。</p>
      <p>性能は値引きすると品質が落ちるため、基本的に不可です。</p>

      <h2>値引き交渉のコツ｜初心者でも成功しやすい方法</h2>

      <h3>① 「値引きしてください」ではなく"相談ベース"で伝える</h3>
      <p>営業担当は「強い値引き要求」が苦手です。逆に、相談ベースだと動きやすくなります。</p>
      <p><strong>効果的な伝え方の例：</strong><br />「総額が予算より少しオーバーしてしまって…どこか調整できる部分はありますか？」</p>
      <p>この言い方が最も効果的です。</p>

      <h3>② 相見積もり（比較）があると強い</h3>
      <p>鳥取市・米子市で家づくりをする人の多くが3社以上の比較をしています。比較があると、住宅会社は「他社に負けたくない」という心理が働き、値引きが入りやすくなります。</p>

      <h3>③ 予算の上限を最初に伝えない</h3>
      <p>最初に「予算は2,500万円です」と言うと、見積もりが2,500万円に寄ってきます。最初は<strong>「2,300〜2,500万円で考えています」</strong>と幅を持たせるのがポイントです。</p>

      <h3>④ 追加オプションは"まとめて交渉"する</h3>
      <p>オプションを1つずつ追加すると、その都度値引きが難しくなります。</p>
      <ul>
        <li>キッチン・収納・照明・外構などをまとめて</li>
        <li>「全体で少し調整できませんか？」と伝える</li>
      </ul>
      <p>これだけで成功率が上がります。</p>

      <h2>絶対にやってはいけないNG行動</h2>
      <table>
        <thead>
          <tr><th>NG行動</th><th>なぜダメか</th></tr>
        </thead>
        <tbody>
          <tr><td>他社の悪口を言う</td><td>営業担当の心象が悪くなるだけ</td></tr>
          <tr><td>無理な値引きを要求する</td><td>工務店の場合、品質が落ちるリスクがある</td></tr>
          <tr><td>契約後に値引きを求める</td><td>契約後はほぼ不可能</td></tr>
        </tbody>
      </table>

      <h2>まとめ</h2>
      <p>家づくりの値引き交渉は、<strong>タイミング × 比較 × 相談ベースの3つで決まります</strong>。</p>
      <ul>
        <li>契約直前が最も通りやすい</li>
        <li>決算期（3月・9月）は狙い目</li>
        <li>本体価格よりオプション・付帯工事が値引きされやすい</li>
        <li>「値引きしてください」ではなく"相談ベース"で伝える</li>
        <li>相見積もりがあると強い</li>
      </ul>
      <p>初心者でも、このポイントを押さえるだけで<strong>数十万円の差が出ることも珍しくありません</strong>。</p>

      <h2>次に読む記事</h2>
      <p>値引き交渉のコツが分かったら、次に気になるのは「そもそも建売と注文住宅、どっちが自分に合うのか？」という点だと思います。選ぶ種類によって、値引きのしやすさも総額も大きく変わります。</p>
      <p>👉 「鳥取で建売と注文住宅どっちがいい？費用・性能・資産価値で徹底比較」を読むと、あなたに最適な家づくりの方向性が明確になります。</p>
    `
  },
  {
    id: 34,
    step: '03',
    title: "鳥取で行くべき住宅見学会｜初心者におすすめのチェックポイント",
    category: "パートナーを選ぶ",
    date: "2026年3月27日",
    readTime: "約13分",
    image: "https://public.readdy.ai/ai/img_res/edited_21d65d4039af5697254c3f0bfba6d247_c8373d7a.jpg",
    excerpt: "「住宅見学会って何を見ればいいの？」鳥取で家づくりを始める初心者が「行ってよかった」と思える見学会の選び方と、玄関の温度差・窓の種類・動線など初心者でも失敗しないチェックポイントをまとめました。",
    tags: ["鳥取", "見学会", "チェックポイント"],
    content: `
      <h2>はじめに</h2>
      <p>「住宅見学会って何を見ればいいの？」<br />「モデルハウスと完成見学会、どっちに行けばいい？」<br />「営業されそうで不安…」</p>
      <p>家づくり初心者にとって、見学会は"最初の大きな一歩"。しかし、ただ行くだけでは<strong>本当に知りたい情報は手に入りません</strong>。</p>
      <p>特に鳥取は、冬の寒さ・湿気・日射の強さなど、気候の影響を受けやすい地域のため、<strong>見学会でチェックすべきポイントが他県よりも多い</strong>のが特徴です。</p>
      <p>この記事では、鳥取で家づくりを始める人が「行ってよかった」と思える見学会の選び方と、<strong>初心者でも失敗しないチェックポイント</strong>をまとめました。</p>

      <h2>結論：鳥取で見学会に行くなら3つを押さえればOK</h2>
      <ol>
        <li>完成見学会（実際の家）を優先する</li>
        <li>断熱・気密・窓の"体感"を重視する</li>
        <li>営業トークより"暮らしのリアル"を見る</li>
      </ol>
      <p>👉 <strong>この3つを意識するだけで、見学会の質が一気に変わります。</strong></p>

      <h2>① 鳥取で行くべき見学会は「完成見学会」が最優先</h2>
      <p>見学会には大きく分けて3種類あります。</p>

      <h3>見学会の種類比較</h3>
      <table>
        <thead>
          <tr><th>種類</th><th>内容</th><th>向いている人</th></tr>
        </thead>
        <tbody>
          <tr><td>完成見学会</td><td>実際に建てた家を公開。施主の了解を得て一般公開</td><td>リアルな住み心地・標準仕様を見たい人</td></tr>
          <tr><td>モデルハウス</td><td>住宅会社が建てた展示用の家。豪華仕様が多い</td><td>会社のブランドや雰囲気を知りたい人</td></tr>
          <tr><td>構造見学会</td><td>建築中の家を公開。断熱材・構造が見られる</td><td>施工品質・性能の実態を確認したい人</td></tr>
        </tbody>
      </table>

      <h3>なぜ完成見学会が最強なのか</h3>
      <ul>
        <li>実際の広さが分かる</li>
        <li>生活動線がリアル</li>
        <li>標準仕様が見られる</li>
        <li>施主のこだわりが反映されている</li>
        <li>"等身大の家"を体感できる</li>
      </ul>
      <p>特に鳥取は土地が広めで、<strong>モデルハウスの豪華仕様が参考にならないケースが多いため、完成見学会の価値が非常に高い</strong>です。</p>

      <h2>② 見学会で必ずチェックすべきポイント（初心者向け）</h2>
      <p>見学会で見るべきポイントは、デザインより<strong>"暮らしやすさ"と"性能"</strong>です。</p>

      <h3>チェックポイント1. 玄関に入った瞬間の「温度差」</h3>
      <p>鳥取の冬は寒い。玄関に入った瞬間に「暖かい」か「ヒヤッとする」か、この違いは断熱・気密の差です。</p>
      <table>
        <thead>
          <tr><th>体感</th><th>意味</th></tr>
        </thead>
        <tbody>
          <tr><td>玄関から暖かい</td><td>高断熱・高気密の証拠</td></tr>
          <tr><td>玄関だけ寒い</td><td>断熱・気密が弱い可能性</td></tr>
          <tr><td>家全体が均一に暖かい</td><td>全館空調や高性能住宅の特徴</td></tr>
        </tbody>
      </table>

      <h3>チェックポイント2. 窓の種類（ここは絶対に見る）</h3>
      <p>窓は断熱性能の"7割"を左右します。</p>
      <ul>
        <li>樹脂サッシか、アルミ樹脂複合か</li>
        <li>ペアガラスか、トリプルガラスか</li>
        <li>窓枠に結露の跡がないか（冬季）</li>
      </ul>
      <p>👉 <strong>樹脂サッシ＋トリプルガラスが鳥取の気候に最適</strong></p>

      <h3>チェックポイント3. 収納の量と配置</h3>
      <p>鳥取は冬物が多く、コート・スノーブーツ・ストーブなど収納が必要。収納の位置が悪い家は、住んでから散らかりやすいです。</p>
      <ul>
        <li>玄関収納は広いか</li>
        <li>各部屋に使いやすい収納があるか</li>
        <li>季節家電の置き場があるか</li>
      </ul>

      <h3>チェックポイント4. 回遊動線・家事動線</h3>
      <p>洗濯動線・料理動線・片付け動線がスムーズだと、毎日のストレスが激減します。</p>
      <ul>
        <li>洗濯→干す→しまうの動線を確認</li>
        <li>キッチンからダイニングへの距離</li>
        <li>車→玄関→キッチンの動線</li>
      </ul>

      <h3>チェックポイント5. 日当たり・窓の位置</h3>
      <p>鳥取は冬の日射取得が重要です。</p>
      <ul>
        <li>南向きの大きな窓があるか</li>
        <li>冬に日差しがリビングに差し込むか</li>
        <li>夏に日射を遮る工夫があるか（庇・ブラインド）</li>
      </ul>

      <h3>チェックポイント6. 生活音（意外と重要）</h3>
      <table>
        <thead>
          <tr><th>確認箇所</th><th>なぜ重要か</th></tr>
        </thead>
        <tbody>
          <tr><td>2階の足音</td><td>子どもが走ると響くか確認</td></tr>
          <tr><td>トイレの音</td><td>リビングまで聞こえないか</td></tr>
          <tr><td>階段の軋み音</td><td>夜間の移動に影響する</td></tr>
        </tbody>
      </table>
      <p>静かさは"住んでからの満足度"に直結します。</p>

      <h3>チェックポイント7. 標準仕様かどうかを確認</h3>
      <p>見学会の家が<strong>標準仕様なのか、オプションなのか</strong>、ここを必ず確認。モデルハウスはオプション仕様が多く、標準では実現できないことも多いです。</p>

      <h2>③ 営業トークより"暮らしのリアル"を見る</h2>
      <p>見学会でよくあるのが、<strong>営業さんの説明を聞きすぎて、家を見る時間がなくなること</strong>。初心者ほど「説明を聞かなきゃいけない」と思いがちですが、実は逆で、"自分のペースで家を見る"ことが最優先です。</p>

      <h3>営業トークより見るべきもの</h3>
      <ul>
        <li>家の温度感（暖かい・涼しい・湿気がない）</li>
        <li>動線のスムーズさ</li>
        <li>収納の使いやすさ</li>
        <li>窓の位置と光の入り方</li>
        <li>家具の配置イメージ</li>
      </ul>
      <p>営業さんの話は、最後にまとめて聞けば十分です。</p>

      <h2>④ 見学会で絶対に聞くべき質問リスト</h2>
      <p>初心者でもこれだけ聞けばOKです。</p>

      <h3>性能系の質問</h3>
      <table>
        <thead>
          <tr><th>質問</th><th>確認ポイント</th></tr>
        </thead>
        <tbody>
          <tr><td>UA値はいくつですか？</td><td>0.46以下が最低ライン</td></tr>
          <tr><td>C値は測っていますか？</td><td>実測していることが重要</td></tr>
          <tr><td>標準仕様でこの性能が出ますか？</td><td>オプションか標準かを確認</td></tr>
        </tbody>
      </table>

      <h3>お金系の質問</h3>
      <ul>
        <li>この家の総額はいくらですか？</li>
        <li>標準仕様とオプションの割合は？</li>
        <li>外構は含まれていますか？</li>
      </ul>

      <h3>暮らし系の質問</h3>
      <ul>
        <li>この間取りを選んだ理由は？</li>
        <li>家事動線はどう考えていますか？</li>
        <li>冬の暖かさはどう確保していますか？</li>
      </ul>

      <h2>⑤ 見学会でやってはいけないNG行動</h2>
      <table>
        <thead>
          <tr><th>NG行動</th><th>なぜダメか</th></tr>
        </thead>
        <tbody>
          <tr><td>本体価格だけを聞く</td><td>総額で比較しないと意味がない</td></tr>
          <tr><td>モデルハウスだけで判断する</td><td>豪華仕様で現実とズレる</td></tr>
          <tr><td>営業さんの話を聞きすぎる</td><td>家を見る時間がなくなる</td></tr>
          <tr><td>1社だけで決める</td><td>最低3社は見学したほうが良い</td></tr>
        </tbody>
      </table>

      <h2>まとめ</h2>
      <p>鳥取で見学会に行くなら、完成見学会を中心に<strong>"性能 × 暮らしやすさ"をチェックすることが最重要</strong>です。初心者は次のポイントを押さえればOK。</p>
      <ol>
        <li>玄関の温度差</li>
        <li>窓の種類</li>
        <li>収納の量と配置</li>
        <li>回遊動線・家事動線</li>
        <li>日当たり・窓の位置</li>
        <li>生活音</li>
        <li>標準仕様かどうか</li>
      </ol>
      <p>見学会は"営業される場所"ではなく、<strong>あなたの暮らしをイメージする場所</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>次は、見学会で気に入った会社を比較するために<br />👉 <a href="/articles/33">「鳥取で住宅会社の選び方｜後悔しないための7つのチェックポイント」</a>を読むと、判断がよりスムーズになります。</p>
    `
  },
  {
    id: 39,
    step: '04',
    title: "鳥取で建売と注文住宅どっちがいい？費用・性能・資産価値で徹底比較",
    category: "物件・プランを決める",
    date: "2026年3月27日",
    readTime: "約14分",
    image: "https://public.readdy.ai/ai/img_res/edited_5733b07119465046310f048f24264db0_fb367fad.jpg",
    excerpt: "「建売と注文住宅、どっちが自分たちに合っているんだろう？」費用・性能・資産価値の3つの軸で徹底比較。鳥取の気候では性能面で注文住宅が圧倒的に有利な理由も解説します。",
    tags: ["鳥取", "建売", "注文住宅"],
    content: `
      <h2>はじめに</h2>
      <p>「建売と注文住宅、どっちが自分たちに合っているんだろう？」<br />「建売は安いって聞くけど、性能は大丈夫？」<br />「注文住宅は高そうで不安…」</p>
      <p>鳥取市・米子市・倉吉市で家づくりを始める20〜30代の方から、こうした悩みを本当に多く聞きます。私自身も最初は「建売のほうが安いし早いから良いのでは？」と思っていましたが、実際に比較してみると<strong>費用・性能・資産価値の違いが大きい</strong>ことに気づきました。</p>
      <p>この記事では、初心者でも迷わず選べるように<strong>建売と注文住宅の違いを、費用・性能・資産価値の3つの軸で徹底比較</strong>します。</p>

      <h2>結論</h2>
      <p><strong>コスパ重視なら建売、満足度と性能重視なら注文住宅</strong>が向いています。</p>
      <table>
        <thead>
          <tr><th>タイプ</th><th>向いている人</th></tr>
        </thead>
        <tbody>
          <tr><td>建売が向いている人</td><td>価格を抑えたい、早く住みたい、こだわりが少ない</td></tr>
          <tr><td>注文住宅が向いている人</td><td>性能・間取り・デザインにこだわりたい、長く快適に住みたい</td></tr>
        </tbody>
      </table>
      <p>特に鳥取県は冬の寒さが厳しいため、<strong>性能面では注文住宅が圧倒的に有利</strong>です。</p>

      <h2>建売住宅とは？｜完成済みの家をそのまま購入するスタイル</h2>
      <p>建売住宅は、土地と建物がセットで販売されている家のことです。</p>

      <h3>建売のメリット</h3>
      <ul>
        <li>価格が安い（総額を抑えやすい）</li>
        <li>完成しているので実物を見て判断できる</li>
        <li>入居までが早い（最短1〜2ヶ月）</li>
        <li>住宅ローンが組みやすい</li>
      </ul>
      <p>鳥取市・米子市では、2,000〜2,500万円台の建売が多く、「予算を抑えたい若い夫婦」に人気です。</p>

      <h3>建売のデメリット</h3>
      <ul>
        <li>間取りや設備を選べない</li>
        <li>断熱・気密性能が低いことが多い</li>
        <li>収納が少ないケースが多い</li>
        <li>似たようなデザインになりがち</li>
      </ul>
      <p>特に性能面は注意が必要で、<strong>UA値・C値が公開されていない建売は多い</strong>です。</p>

      <h2>注文住宅とは？｜間取り・性能・デザインを自由に決められる家</h2>
      <p>注文住宅は、土地を購入し、間取りや仕様を自由に決めて建てる家です。</p>

      <h3>注文住宅のメリット</h3>
      <ul>
        <li>間取りを自由に決められる</li>
        <li>断熱・気密性能を高めやすい</li>
        <li>デザインの自由度が高い</li>
        <li>将来の暮らしに合わせて設計できる</li>
        <li>資産価値が落ちにくい</li>
      </ul>
      <p>鳥取県は冬の寒さが厳しいため、<strong>高断熱・高気密の注文住宅は光熱費が大幅に安くなる</strong>というメリットがあります。</p>

      <h3>注文住宅のデメリット</h3>
      <ul>
        <li>建売より費用が高くなりやすい</li>
        <li>完成までに時間がかかる（6〜12ヶ月）</li>
        <li>住宅会社選びが難しい</li>
        <li>打ち合わせの手間がかかる</li>
      </ul>

      <h2>費用で比較｜建売は安い、注文住宅は満足度が高い</h2>
      <table>
        <thead>
          <tr><th>種類</th><th>費用相場（鳥取県）</th><th>特徴</th></tr>
        </thead>
        <tbody>
          <tr><td>建売</td><td>1,900〜2,600万円</td><td>外構込みのケースが多い・追加費用が少ない</td></tr>
          <tr><td>注文住宅</td><td>2,300〜3,200万円</td><td>性能や設備で変動・外構は別途のことが多い</td></tr>
        </tbody>
      </table>

      <h3>費用で後悔しやすいポイント</h3>
      <ul>
        <li>建売は「安いけど寒い」ケースが多い</li>
        <li>注文住宅は「オプションで高くなる」ケースが多い</li>
      </ul>
      <p>👉 <strong>総額で比較することが重要。</strong></p>

      <h2>性能で比較｜鳥取の気候では注文住宅が圧勝</h2>
      <p>鳥取は冬の寒さ・湿気・雪が厳しい地域です。</p>
      <table>
        <thead>
          <tr><th>性能</th><th>建売住宅</th><th>注文住宅</th></tr>
        </thead>
        <tbody>
          <tr><td>UA値（断熱性能）</td><td>非公開が多い・性能が低い傾向</td><td>0.46以下を狙える・数値が明確</td></tr>
          <tr><td>C値（気密性能）</td><td>測定なしが多い</td><td>0.5以下も可能・実測する会社が多い</td></tr>
          <tr><td>サッシ</td><td>アルミ樹脂複合が多い</td><td>樹脂サッシ・トリプルガラスが選べる</td></tr>
        </tbody>
      </table>
      <p>👉 <strong>鳥取の気候では、性能の差が"快適さ"に直結します。</strong></p>

      <h2>資産価値で比較｜注文住宅のほうが落ちにくい</h2>
      <table>
        <thead>
          <tr><th>　</th><th>建売の資産価値</th><th>注文住宅の資産価値</th></tr>
        </thead>
        <tbody>
          <tr><td>差別化</td><td>同じような家が多く差別化しにくい</td><td>高性能住宅は中古でも人気</td></tr>
          <tr><td>売却時</td><td>性能が低いと売却時に不利</td><td>デザイン性が高いと評価されやすい</td></tr>
          <tr><td>価格推移</td><td>価格が下がりやすい</td><td>長期的に価値が残りやすい</td></tr>
        </tbody>
      </table>
      <p>👉 <strong>長期的に見ると注文住宅のほうが価値が残りやすい。</strong></p>

      <h2>どっちが向いている？タイプ別のおすすめ</h2>

      <h3>建売が向いている人</h3>
      <ul>
        <li>予算を抑えたい</li>
        <li>早く住みたい</li>
        <li>こだわりが少ない</li>
        <li>実物を見て決めたい</li>
      </ul>

      <h3>注文住宅が向いている人</h3>
      <ul>
        <li>性能にこだわりたい</li>
        <li>間取りを自由にしたい</li>
        <li>長く快適に住みたい</li>
        <li>家族構成の変化に対応したい</li>
      </ul>

      <h2>まとめ</h2>
      <p>建売と注文住宅は、どちらが良い悪いではなく<strong>「自分たちの優先順位」で選ぶことが大切</strong>です。</p>
      <table>
        <thead>
          <tr><th>優先したいこと</th><th>おすすめ</th></tr>
        </thead>
        <tbody>
          <tr><td>費用を抑えたい</td><td>建売</td></tr>
          <tr><td>性能・満足度を重視</td><td>注文住宅</td></tr>
          <tr><td>資産価値を重視</td><td>注文住宅</td></tr>
          <tr><td>入居を急ぎたい</td><td>建売</td></tr>
        </tbody>
      </table>
      <p>鳥取県の気候を考えると、<strong>性能面では注文住宅が圧倒的に有利</strong>です。</p>

      <h2>次に読む記事</h2>
      <p>建売と注文住宅の違いが分かったら、次に気になるのは「どんな間取りが住みやすいのか？」という点だと思います。</p>
      <p>👉 <a href="/articles/40">「鳥取で後悔しない間取り10選｜4LDK・平屋・回遊動線の成功例まとめ」</a>を読むと、あなたに合った間取りのイメージが一気に広がります。</p>
    `
  },
  {
    id: 40,
    step: '04',
    title: "鳥取で後悔しない間取り10選｜4LDK・平屋・回遊動線の成功例まとめ",
    category: "物件・プランを決める",
    date: "2026年3月27日",
    readTime: "約16分",
    image: "https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_d297a1b9.jpg",
    excerpt: "「どんな間取りが住みやすいの？」鳥取の気候・生活スタイルに合わせた間取り10選を、平面図とイメージ画像付きで解説。回遊動線・FCL・パントリー・平屋など実例の成功例を紹介します。",
    tags: ["鳥取", "間取り", "10選"],
    content: `
      <h2>はじめに</h2>
      <p>「どんな間取りが住みやすいの？」<br />「4LDKと平屋、どちらが鳥取の暮らしに向いてる？」<br />「間取りで失敗したくない…」</p>
      <p>間取りは毎日の暮らしに直結するため、後から後悔してもリフォームコストが高くなります。この記事では、鳥取で家づくりをした実例から<strong>後悔しない間取り10選</strong>を、平面図とイメージ画像付きでわかりやすく紹介します。</p>

      <h2>結論：鳥取で後悔しない間取りのポイント</h2>
      <p>鳥取の暮らしに合う間取りは、<strong>動線 × 収納 × 日当たり</strong>の3つが整っていることが重要です。</p>

      <h2>① 回遊できるLDK動線</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:20px 0;background:#f8f9fa;padding:16px;border-radius:12px;">
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">📐 平面図イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_b98ea88b.jpg" alt="回遊動線 平面図" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">🏠 完成イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_597e106e.jpg" alt="回遊動線 実例" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
      </div>
      <p>洗濯→干す→しまう→料理→片付けがすべて1階で完結する動線。</p>
      <p><strong>メリット</strong></p>
      <ul>
        <li>家事動線が短くなる</li>
        <li>ストレスが激減する</li>
        <li>子育て世帯に大人気</li>
        <li>リビングからキッチンへスムーズに移動できる</li>
      </ul>
      <p><strong>ポイント</strong></p>
      <ul>
        <li>キッチン→洗面室→洗濯機→FCLが直線またはコの字動線</li>
        <li>行き止まりなく回れる設計が理想</li>
      </ul>

      <h2>② 1階にファミリークローゼット（FCL）</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:20px 0;background:#f8f9fa;padding:16px;border-radius:12px;">
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">📐 平面図イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_5c05b357.jpg" alt="FCL 平面図" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">🏠 完成イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_353d6ccc.jpg" alt="FCL 実例" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
      </div>
      <p>鳥取の家づくりで最も人気の収納がFCL（ファミリークローゼット）。</p>
      <p><strong>メリット</strong></p>
      <ul>
        <li>洗う→干す→しまうが1階で完結</li>
        <li>2階に上がる手間が減る</li>
        <li>子どもの支度がスムーズ</li>
      </ul>
      <p><strong>ポイント</strong></p>
      <ul>
        <li>2〜3帖で十分</li>
        <li>玄関近く or 洗面横が最強配置</li>
      </ul>

      <h2>③ キッチン横のパントリー</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:20px 0;background:#f8f9fa;padding:16px;border-radius:12px;">
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">📐 平面図イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_ce90170e.jpg" alt="パントリー 平面図" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">🏠 完成イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_8d154b16.jpg" alt="パントリー 実例" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
      </div>
      <p>鳥取は車移動が多く、まとめ買いが多い地域。パントリーは必須級です。</p>
      <p><strong>メリット</strong></p>
      <ul>
        <li>食材のストックがしやすい</li>
        <li>キッチンが散らからない</li>
        <li>災害時の備蓄にも使える</li>
      </ul>
      <p><strong>ポイント</strong></p>
      <ul>
        <li>1〜2帖のウォークイン型が使いやすい</li>
        <li>可動棚にすると収納効率アップ</li>
      </ul>

      <h2>④ 南向きリビング＋冬の日射取得</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:20px 0;background:#f8f9fa;padding:16px;border-radius:12px;">
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">📐 平面図イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_e40ec359.jpg" alt="南向きリビング 平面図" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">🏠 完成イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_62dfa573.jpg" alt="南向きリビング 実例" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
      </div>
      <p>鳥取の冬は日射が貴重。南向きリビングは暖かさが段違いです。</p>
      <p><strong>メリット</strong></p>
      <ul>
        <li>冬の暖房費が下がる</li>
        <li>明るくて気持ちいい</li>
        <li>洗濯物が乾きやすい</li>
      </ul>
      <p><strong>注意点</strong></p>
      <ul>
        <li>夏は日射遮蔽（軒・庇）が必要</li>
        <li>隣家の影にならないか土地選び段階で確認</li>
      </ul>

      <h2>⑤ 4LDKの「1階に1部屋」</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:20px 0;background:#f8f9fa;padding:16px;border-radius:12px;">
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">📐 平面図イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_f1066f2d.jpg" alt="1階に1部屋 平面図" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">🏠 完成イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_fa505b3c.jpg" alt="1階に1部屋 実例" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
      </div>
      <p>鳥取の家づくりで人気の間取り。1階に和室や洋室を1室設けるパターン。</p>
      <p><strong>メリット</strong></p>
      <ul>
        <li>将来の寝室として使える</li>
        <li>親が泊まりに来ても安心</li>
        <li>子どもの遊び部屋にもなる</li>
      </ul>
      <p><strong>失敗例</strong></p>
      <ul>
        <li>1階の部屋が狭すぎて使いにくい（最低4.5帖は確保）</li>
        <li>日当たりが悪い場所に設置してしまった</li>
      </ul>

      <h2>⑥ 平屋の一直線動線</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:20px 0;background:#f8f9fa;padding:16px;border-radius:12px;">
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">📐 平面図イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_3d2c09cd.jpg" alt="平屋 平面図" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">🏠 完成イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_d4230025.jpg" alt="平屋 実例" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
      </div>
      <p>鳥取県は土地が広めなので、平屋が人気。</p>
      <p><strong>メリット</strong></p>
      <ul>
        <li>階段がなく安全（老後も安心）</li>
        <li>家事動線が短い</li>
        <li>メンテナンスが楽</li>
      </ul>
      <p><strong>注意点</strong></p>
      <ul>
        <li>建築費は2階建てより坪単価が高くなりやすい</li>
        <li>同じ面積なら土地が広く必要</li>
      </ul>

      <h2>⑦ 2階ホールに室内干しスペース</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:20px 0;background:#f8f9fa;padding:16px;border-radius:12px;">
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">📐 平面図イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_47c52085.jpg" alt="室内干し 平面図" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">🏠 完成イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_9f234d79.jpg" alt="室内干し 実例" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
      </div>
      <p>鳥取は冬の湿気・雪で外干しが難しい日が多い。</p>
      <p><strong>メリット</strong></p>
      <ul>
        <li>室内干しが早く乾く</li>
        <li>洗濯物がリビングに出ない</li>
        <li>花粉・PM2.5対策にもなる</li>
      </ul>
      <p><strong>ポイント</strong></p>
      <ul>
        <li>2階ホールを広めに取り、物干しバーを設置</li>
        <li>換気ルートを確保して湿気対策も必要</li>
      </ul>

      <h2>⑧ 玄関土間収納（雪・雨対策）</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:20px 0;background:#f8f9fa;padding:16px;border-radius:12px;">
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">📐 平面図イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_facabd66.jpg" alt="土間収納 平面図" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">🏠 完成イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_05e63903.jpg" alt="土間収納 実例" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
      </div>
      <p>鳥取の冬は雪・雨が多いため、土間収納が大活躍。</p>
      <p><strong>メリット</strong></p>
      <ul>
        <li>ベビーカー・アウトドア用品が置ける</li>
        <li>コート掛けを作ると便利</li>
        <li>玄関が散らからない</li>
        <li>雪・泥の汚れを室内に持ち込まない</li>
      </ul>
      <p><strong>ポイント</strong></p>
      <ul>
        <li>最低1.5帖は確保したい</li>
        <li>タイル貼りにすると水洗いも可能</li>
      </ul>

      <h2>⑨ 回遊できるアイランドキッチン</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:20px 0;background:#f8f9fa;padding:16px;border-radius:12px;">
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">📐 平面図イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_aa4157e8.jpg" alt="アイランドキッチン 平面図" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">🏠 完成イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_e022e2e1.jpg" alt="アイランドキッチン 実例" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
      </div>
      <p>子育て世帯に人気のキッチン。</p>
      <p><strong>メリット</strong></p>
      <ul>
        <li>家族が動きやすい</li>
        <li>料理中のストレスが減る</li>
        <li>見た目がスッキリ</li>
        <li>子どもの様子を見ながら料理できる</li>
      </ul>
      <p><strong>ポイント</strong></p>
      <ul>
        <li>通路幅は最低90cm確保（2人作業なら120cm）</li>
        <li>油はねを考慮した配置が必要</li>
      </ul>

      <h2>⑩ 子ども部屋は最初は広く→将来仕切る</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin:20px 0;background:#f8f9fa;padding:16px;border-radius:12px;">
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">📐 平面図イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_5435b292.jpg" alt="子ども部屋 平面図" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
        <div>
          <p style="font-size:13px;font-weight:600;color:#4A6B60;margin-bottom:8px;">🏠 完成イメージ</p>
          <img src="https://public.readdy.ai/ai/img_res/edited_3a47163185c249566a58e9028b6338af_6dcb89d7.jpg" alt="子ども部屋 実例" style="width:100%;height:180px;object-fit:cover;border-radius:8px;" />
        </div>
      </div>
      <p>鳥取の家づくりで増えている考え方。</p>
      <p><strong>メリット</strong></p>
      <ul>
        <li>小さいうちは広く使える</li>
        <li>将来2部屋に分けられる</li>
        <li>コストを抑えられる</li>
        <li>家族構成の変化に対応できる</li>
      </ul>
      <p><strong>ポイント</strong></p>
      <ul>
        <li>最初から2つの窓と2つのドアを設置しておく</li>
        <li>コンセントも仕切り後を想定して複数設置</li>
      </ul>

      <h2>見学会で間取りをチェックするポイント</h2>
      <table>
        <thead>
          <tr><th>チェックポイント</th><th>確認内容</th></tr>
        </thead>
        <tbody>
          <tr><td>玄関からリビングまでの動線</td><td>スムーズに移動できるか</td></tr>
          <tr><td>洗濯動線（洗う→干す→しまう）</td><td>1階で完結するか</td></tr>
          <tr><td>収納の位置と量</td><td>使う場所の近くにあるか</td></tr>
          <tr><td>日当たり（冬の日射取得）</td><td>南向きに大きな窓があるか</td></tr>
          <tr><td>キッチンの使いやすさ</td><td>回遊できるか・通路幅は十分か</td></tr>
          <tr><td>生活音（階段・トイレ）</td><td>リビングに響かないか</td></tr>
        </tbody>
      </table>
      <p>👉 <strong>間取りは"実物を見る"のが一番早いです。</strong></p>

      <h2>まとめ</h2>
      <p>鳥取で後悔しない間取りは、<strong>動線 × 収納 × 日当たり</strong>の3つが整っている間取りです。特におすすめは以下の10タイプ。</p>
      <ol>
        <li>回遊動線（LDK全体が繋がる）</li>
        <li>1階FCL（洗濯動線の短縮）</li>
        <li>パントリー（まとめ買い対応）</li>
        <li>南向きリビング（日射取得）</li>
        <li>1階に1部屋（多目的利用）</li>
        <li>平屋の一直線動線（バリアフリー）</li>
        <li>室内干しスペース（雪・雨対策）</li>
        <li>土間収納（雪・泥の持ち込み防止）</li>
        <li>アイランドキッチン（子育て対応）</li>
        <li>将来仕切る子ども部屋（可変設計）</li>
      </ol>
      <p>これらを意識するだけで、<strong>暮らしやすさが大きく変わります</strong>。</p>

      <h2>次に読む記事</h2>
      <p>間取りの成功例を見ると、「この間取りって、どんな土地だから実現できたんだろう？」「うちの土地でも同じようにできるのかな？」と、次に気になるのは <strong>"土地の条件"</strong> です。</p>
      <p>実は、間取りの良し悪しの半分以上は<strong>土地の形・日当たり・地盤・ハザード</strong>で決まります。そこで、間取りの成功例を見た今こそ読んでほしいのがこちらです。</p>
      <p>👉 <a href="/articles/48">【鳥取版】失敗しない土地選びのコツ10選｜日当たり・地盤・ハザードのチェックポイント</a></p>
      <p>土地の選び方が分かると、「どんな間取りがその土地に合うのか」「どんな土地なら理想の間取りが実現しやすいのか」が一気にクリアになります。</p>
    `
  }
];

export const stepInfo: Record<string, { label: string; icon: string; color: string; bgColor: string }> = {
  '01': { label: '知識をつける', icon: 'ri-book-open-line', color: '#5A7A52', bgColor: '#E8F0E6' },
  '02': { label: '予算を決める', icon: 'ri-money-yen-circle-line', color: '#7A6845', bgColor: '#F5F0E8' },
  '03': { label: 'パートナーを選ぶ', icon: 'ri-building-4-line', color: '#4A6B60', bgColor: '#EEF2F0' },
  '04': { label: '物件・プランを決める', icon: 'ri-map-pin-2-line', color: '#6B5B45', bgColor: '#F2EDE8' },
  '05': { label: '契約・引き渡し', icon: 'ri-file-list-3-line', color: '#4A5B6B', bgColor: '#E8EDF2' },
};

export const categories = [
  {
    id: 1,
    name: "資金計画",
    description: "頭金・住宅ローン・諸費用など、お金のことを丁寧に解説します",
    icon: "ri-money-yen-circle-line",
    color: "#E8F0E6",
    textColor: "#5A7A52",
    articleCount: 12
  },
  {
    id: 2,
    name: "物件探し",
    description: "エリア選び・物件比較・内見のコツをわかりやすく紹介",
    icon: "ri-map-pin-2-line",
    color: "#F5F0E8",
    textColor: "#7A6845",
    articleCount: 9
  },
  {
    id: 3,
    name: "住宅ローン",
    description: "金利・審査・返済計画のすべてをやさしく解説",
    icon: "ri-bank-line",
    color: "#EEF2F0",
    textColor: "#4A6B60",
    articleCount: 15
  },
  {
    id: 4,
    name: "契約・手続き",
    description: "売買契約・登記・引き渡しの流れを順を追って説明",
    icon: "ri-file-list-3-line",
    color: "#F2EEF5",
    textColor: "#5D4E7A",
    articleCount: 8
  }
];

// STEP05の記事をarticles配列に追加
articlesStep05.forEach((a) => articles.push(a));
articlesStep05Ext.forEach((a) => articles.push(a));
articlesStep01Ext.forEach((a) => articles.push(a));
